import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import * as THREE from 'three';
import { jsPDF } from 'jspdf';
import TShirt3D from './components/TShirt3D';
import LayerPanel from './components/LayerPanel';
import DesignGrid, { DesignCard } from './components/DesignGrid';
import ConfigNav from './components/ConfigNav';
import BottomBar from './components/BottomBar';
import ExportModal from './components/ExportModal';
import TutorialOverlay, { hasDoneTutorial, resetTutorial } from './components/TutorialOverlay';
import WelcomeModal, { hasSeenWelcome } from './components/WelcomeModal';
import { useLayers } from './hooks/useLayers';
import { AppState, Layer, MaterialDescriptor, TShirtZone } from './types';
import { createTextCanvas, loadImageFromFile, loadImageFromUrl, renderLayersToCanvas } from './utils/textureUtils';
import { buildContactZip } from './utils/buildContactZip';
import Btn from './components/Btn';
import TextEditor from './components/TextEditor';
import PriceSimulator from './components/PriceSimulator';
import SettingsMenu from './components/SettingsMenu';
import { getModelGender } from './utils/pricing';
import './App.css';

type TabKey =
  | 'design'
  | 'motif'
  | 'colors'
  | 'logos'
  | 'numbers'
  | 'name'
  | 'text'
  | 'gradient'
  | 'text-edit'
  | 'settings';

type DesignItem = {
  name: string;
  url: string;
  sourcePath?: string;
  isNone?: boolean;
};

type ModelItem = {
  name: string;
  url: string;
  fileName: string;
  category: string;
  sourcePath: string;
};

type BestDesignItem = {
  key: string;
  name: string;
  url: string;
};

type PreviewCollection = 'design' | 'best_design';

type SerializedLayerSource = {
  kind: 'url' | 'dataUrl' | 'assetRef';
  value: string;
  collection?: 'design' | 'motif';
};

type SerializedLayer = {
  type: Layer['type'];
  zone: TShirtZone;
  locked: boolean;
  uvCenter: [number, number];
  uvSize: [number, number];
  uvRotation: number;
  order: number;
  transformMode: Layer['transformMode'];
  data?: Record<string, any>;
  source?: SerializedLayerSource;
};

type CreationFileV1 = {
  format: 'indyanna.creation';
  version: 1;
  createdAt: string;
  model?: {
    url?: string;
    fileName?: string;
    sourcePath?: string;
  };
  state: {
    materialColors: Record<string, string>;
    patternMotifColors: { motif1: string; motif2: string };
    motifMaterialSelections: Record<string, Record<string, boolean>>;
    layers: SerializedLayer[];
  };
};

type ShareLinks = {
  viewerUrl: string;
  editorUrl: string;
};

type ExportModalState = {
  visible: boolean;
  pdfUrl: string | null;
  captures: string[];
  initialMessage: string;
};

type ShareFormState = {
  visible: boolean;
  email: string;
  consentAssist: boolean;
  error: string | null;
};

type CreationFeedbackState =
  | { visible: false; mode: null; message: ''; links?: undefined }
  | { visible: true; mode: 'saving' | 'loaded'; message: string; links?: undefined }
  | { visible: true; mode: 'saved'; message: string; links: ShareLinks };

type EditTextForm = {
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  strokeColor: string;
  strokeWidth: number;
  rotation: number;
};

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').trim().replace(/\/$/, '');
const toApiUrl = (path: string) => (API_BASE_URL ? `${API_BASE_URL}${path}` : path);
const PREVIEW_ROUTE_BASE = '/api_indyannastudio/previews';

const buildPreviewUrl = (collection: PreviewCollection, key: string) =>
  `${toApiUrl(`${PREVIEW_ROUTE_BASE}/${collection}`)}?key=${encodeURIComponent(key)}`;

const hasStoredPreview = async (collection: PreviewCollection, key: string) => {
  try {
    const response = await fetch(buildPreviewUrl(collection, key), {
      method: 'HEAD',
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    return false;
  }
};

const saveStoredPreview = async (collection: PreviewCollection, key: string, dataUrl: string) => {
  try {
    const response = await fetch(toApiUrl(`${PREVIEW_ROUTE_BASE}/${collection}?key=${encodeURIComponent(key)}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, dataUrl }),
    });
    return response.ok;
  } catch {
    return false;
  }
};

const clampByte = (value: number) => Math.min(255, Math.max(0, Math.round(value)));

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map(channel => clampByte(channel).toString(16).padStart(2, '0')).join('')}`;

const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return { r: 0, g: 0, b: 0 };
  }
  const int = parseInt(normalized, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
};

const getRelativeLuminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const srgb = [r, g, b].map(channel => channel / 255);
  const linear = srgb.map(v => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
};

const isLightSwatch = (value?: string) => {
  if (!value) return false;
  if (isGradientValue(value)) {
    const parsed = parseGradientValue(value);
    const avg = (getRelativeLuminance(parsed.start) + getRelativeLuminance(parsed.end)) / 2;
    return avg > 0.72;
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(value.trim())) return false;
  return getRelativeLuminance(value.trim()) > 0.72;
};

const blendHex = (start: string, end: string, t: number) => {
  const from = hexToRgb(start);
  const to = hexToRgb(end);
  return rgbToHex(
    from.r + (to.r - from.r) * t,
    from.g + (to.g - from.g) * t,
    from.b + (to.b - from.b) * t
  );
};

const gradientRegex = /^linear-gradient\((\d+)deg,\s*(#[0-9a-fA-F]{6}),\s*(#[0-9a-fA-F]{6})(?:,\s*(\d{1,3})%)?\)$/;

const isGradientValue = (value?: string) =>
  Boolean(value && value.startsWith('linear-gradient('));

const parseGradientValue = (value?: string) => {
  const match = value ? value.match(gradientRegex) : null;
  if (!match) {
    return { angle: 90, start: '#ffffff', end: '#ffffff', stop: 50 };
  }
  const stop = Math.min(100, Math.max(0, Number(match[4] ?? 50)));
  return {
    angle: Number(match[1]),
    start: match[2],
    end: match[3],
    stop,
  };
};

const buildGradientValue = (angle: number, start: string, end: string, stop = 50) =>
  `linear-gradient(${angle}deg, ${start}, ${end}, ${Math.min(100, Math.max(0, Math.round(stop)))}%)`;

const toCssGradient = (value?: string) => {
  if (!value || !isGradientValue(value)) return value;
  const parsed = parseGradientValue(value);
  const stop = Math.min(100, Math.max(0, parsed.stop));
  const midColor = blendHex(parsed.start, parsed.end, 0.5);
  return `linear-gradient(${parsed.angle}deg, ${parsed.start} 0%, ${midColor} ${stop}%, ${parsed.end} 100%)`;
};

const normalizeRotation = (rotationRad: number) => {
  const deg = (rotationRad * 180) / Math.PI;
  const wrapped = ((((deg + 180) % 360) + 360) % 360) - 180;
  return (wrapped * Math.PI) / 180;
};

const toCanvasFontFamily = (fontFamily: string) => {
  const escaped = (fontFamily || 'Arial').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `"${escaped}", Arial, sans-serif`;
};

const getSwatchBackground = (value: string) => toCssGradient(value) || value;

const normalizeMaterialName = (name?: string) => (name || '').trim();

const splitMaterialName = (name: string) => {
  const trimmed = normalizeMaterialName(name);
  if (!trimmed) return { group: 'Groupe', element: 'Element', raw: '' };
  const parts = trimmed.split('/');
  const group = parts[0]?.trim() || trimmed;
  const element = parts.slice(1).join('/').trim() || group;
  return { group, element, raw: trimmed };
};

const isNoPaintMaterialName = (name: string) => {
  const { group, element, raw } = splitMaterialName(name);
  return raw.toLowerCase() === 'no_paint' || group.toLowerCase() === 'no_paint' || element.toLowerCase() === 'no_paint';
};

const isPaintableMaterial = (material: THREE.Material) => !isNoPaintMaterialName(material.name || '');

const getMaterialKey = (material: THREE.Material, meshName: string, materialIndex?: number) => {
  const materialName = normalizeMaterialName(material.name);
  if (materialName) return materialName;
  const meshLabel = normalizeMaterialName(meshName);
  if (meshLabel) return materialIndex !== undefined ? `${meshLabel}::${materialIndex}` : meshLabel;
  return material.uuid;
};

type MotifPickerTarget =
  | { type: 'group'; id: string }
  | { type: 'material'; id: string }
  | { type: 'text' }
  | { type: 'text-stroke' }
  | { type: 'motif-design'; motifId: string; designKey: 'design1' | 'design2' }
  | { type: 'pattern-motif'; motifKey: 'motif1' | 'motif2' }
  | { type: 'motif-group'; motifId: string; groupName: string }
  | { type: 'motif-material'; motifId: string; materialId: string }
  | { type: 'form-text-color' }
  | { type: 'form-text-stroke' }
  | { type: 'form-number-color' }
  | { type: 'form-number-stroke' }
  | { type: 'form-name-color' }
  | { type: 'form-name-stroke' }
  | { type: 'form-edit-color' }
  | { type: 'form-edit-stroke' };

type CopyableColorEntry = {
  key: string;
  label: string;
  color: string;
  onPick: () => void;
  onChange: (next: string) => void;
};

type LocalFontAsset = {
  family: string;
  url: string;
  format: 'truetype' | 'opentype' | 'woff' | 'woff2';
};

// Vite returns /public/... as the URL for public-dir globs — strip the prefix so
// browsers receive the correct served path (/assets/... instead of /public/assets/...).
const normalizeAssetUrl = (url: string) =>
  url.startsWith('/public/') ? url.slice('/public'.length) : url;

const getLocalFontFamily = (sourcePath: string) => {
  const fileName = decodeURIComponent(sourcePath.split('/').pop() || '');
  return fileName
    .replace(/\.[^/.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getLocalFontFormat = (sourcePath: string): LocalFontAsset['format'] => {
  const extension = (sourcePath.split('.').pop() || '').toLowerCase();
  if (extension === 'woff2') return 'woff2';
  if (extension === 'woff') return 'woff';
  if (extension === 'otf') return 'opentype';
  return 'truetype';
};

const localFontModules = {
  ...import.meta.glob('/public/assets/font/*.{ttf,otf,woff,woff2}', { eager: true, as: 'url' }),
  ...import.meta.glob('/src/assets/font/*.{ttf,otf,woff,woff2}', { eager: true, as: 'url' }),
} as Record<string, string>;

const LOCAL_FONT_OPTIONS: LocalFontAsset[] = Object.entries(localFontModules)
  .map(([sourcePath, url]) => ({
    family: getLocalFontFamily(sourcePath),
    url: normalizeAssetUrl(url),
    format: getLocalFontFormat(sourcePath),
  }))
  .filter(font => font.family.length > 0)
  .sort((a, b) => a.family.localeCompare(b.family, 'fr', { sensitivity: 'base' }));

const BASE_FONT_OPTIONS = [
  'Bebas Neue',
  'Anton',
  'Oswald',
  'Teko',
  'Montserrat Alternates',
  'Poppins',
  'Raleway',
  'Playfair Display',
  'Abril Fatface',
  'Cinzel',
  'Righteous',
  'Bungee',
  'Black Ops One',
  'Alfa Slab One',
  'Rubik Mono One',
  'Staatliches',
  'Bangers',
  'Lilita One',
  'Orbitron',
  'Press Start 2P',
  'Pacifico',
  'Satisfy',
  'Impact',
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Verdana',
];

const FONT_OPTIONS = Array.from(
  new Set([...LOCAL_FONT_OPTIONS.map(font => font.family), ...BASE_FONT_OPTIONS])
);

const designImageModules = {
  ...import.meta.glob('/public/assets/design/*.{png,jpg,jpeg,webp,svg}', { eager: true, as: 'url' }),
  ...import.meta.glob('/src/assets/design/*.{png,jpg,jpeg,webp,svg}', { eager: true, as: 'url' }),
} as Record<string, string>;

const DESIGN_ITEMS: DesignItem[] = [
  { name: 'Aucun motif', url: '', isNone: true },
  ...Object.entries(designImageModules)
    .map(([sourcePath, url]) => {
      const fileName = sourcePath.split('/').pop() || 'Design';
      const name = fileName.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim();
      return { name: name || fileName, url: normalizeAssetUrl(url), sourcePath };
    })
    .sort((a, b) => a.name.localeCompare(b.name)),
];

const motifImageModules = {
  ...import.meta.glob('/public/assets/motif/*.{png,jpg,jpeg,webp,svg}', { eager: true, as: 'url' }),
  ...import.meta.glob('/src/assets/motif/*.{png,jpg,jpeg,webp,svg}', { eager: true, as: 'url' }),
} as Record<string, string>;

const MOTIF_ITEMS: DesignItem[] = [
  { name: 'Aucun motif', url: '', isNone: true },
  ...Object.entries(motifImageModules)
    .map(([sourcePath, url]) => {
      const fileName = sourcePath.split('/').pop() || 'Motif';
      const name = fileName.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim();
      return { name: name || fileName, url: normalizeAssetUrl(url), sourcePath };
    })
    .sort((a, b) => a.name.localeCompare(b.name)),
];

const modelModules = {
  ...import.meta.glob('/public/model/**/*.glb', { eager: true, as: 'url' }),
  ...import.meta.glob('/src/assets/model/**/*.glb', { eager: true, as: 'url' }),
} as Record<string, string>;

const getModelCategory = (path: string) => {
  const parts = path.split('/');
  const modelIndex = parts.findIndex(part => part === 'model');
  if (modelIndex !== -1 && parts[modelIndex + 1]) {
    return parts[modelIndex + 1];
  }
  return 'Autres';
};

const MODEL_ITEMS: ModelItem[] = Object.entries(modelModules)
  .map(([path, url]) => {
    const fileName = path.split('/').pop() || 'Modele';
    const name = fileName.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim();
    return {
      name: name || fileName,
      url: normalizeAssetUrl(url),
      fileName,
      category: getModelCategory(path),
      sourcePath: path,
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

const DEFAULT_MODEL =
  MODEL_ITEMS.find(item => item.sourcePath.includes('/model/Homme/Tshirt Homme Manches Courtes.glb')) ||
  MODEL_ITEMS.find(item => item.fileName === 'Tshirt Homme Manches Courtes.glb') ||
  MODEL_ITEMS[0] ||
  null;

const bestDesignJsonModules = {
  ...import.meta.glob('/public/assets/best_design/*.json', { eager: true, as: 'url' }),
  ...import.meta.glob('/src/assets/best_design/*.json', { eager: true, as: 'url' }),
} as Record<string, string>;

const BEST_DESIGN_ITEMS: BestDesignItem[] = Object.entries(bestDesignJsonModules)
  .map(([path, url]) => {
    const fileName = path.split('/').pop() || 'Creation';
    const name = fileName.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim();
    return {
      key: path,
      name: name || fileName,
      url: normalizeAssetUrl(url),
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

const SHOW_BEST_DESIGN_TAB = false;

  const TAB_META: Record<TabKey, { title: string; subtitle: string }> = {
  design: {
    title: 'Sélectionner le design',
    subtitle: 'Choisissez un design de base.',
  },
  motif: {
    title: 'Sélectionner le motif',
    subtitle: 'Choisissez une zone puis un motif.',
  },
  colors: {
    title: 'Couleurs',
    subtitle: 'Appliquez les couleurs par zone.',
  },
  gradient: {
    title: 'Dégradé',
    subtitle: 'Appliquez un dégradé par zone.',
  },
  logos: {
    title: 'Logo',
    subtitle: 'Importez votre logo ou emblème.',
  },
  numbers: {
    title: 'Numéro',
    subtitle: 'Ajoutez un numéro de joueur.',
  },
  name: {
    title: 'Nom',
    subtitle: 'Ajoutez un nom au dos ou au devant.',
  },
  text: {
    title: 'Texte',
    subtitle: 'Ajoutez du texte libre.',
  },
  'text-edit': {
    title: 'Modification texte',
    subtitle: 'Ajustez la taille et la rotation.',
  },
  settings: {
    title: 'Menu',
    subtitle: 'Création et informations.',
  },
};

const NAV_ITEMS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'design',   label: 'Design',   icon: '/assets/icon/icons8-design-50.png' },
  { key: 'colors',   label: 'Couleurs', icon: '/assets/icon/icons8-paint-palette-50.png' },
  { key: 'logos',    label: 'Logo',     icon: '/assets/icon/icons8-picture-50.png' },
  { key: 'text',     label: 'Texte',    icon: '/assets/icon/icons8-texte-50.png' },
  { key: 'settings', label: 'Menu',     icon: '/assets/icon/icons8-menu-50.png' },
];

const COLOR_SWATCH_GRID = {
  left: [
    ['#000000', '#FFFFFF'],
    ['#FFFA84', '#FEE837', '#FCD60F', '#FAC80B', '#E4B010'],
    ['#FFCD55', '#FFB637', '#FF8326', '#F95C1B', '#CF4216'],
    ['#FFBA9C', '#FF7A62', '#F03B2B', '#C12519', '#801E1E'],
    ['#FFAFA5', '#F95552', '#D52231', '#B92B32', '#6D2A31'],
    ['#FFA6A9', '#FF5E75', '#E91C56', '#A5123D', '#7D2041'],
    ['#FF9FC1', '#FA72A9', '#DA3B6F', '#BC3266', '#862B5A'],
    ['#EFC3E1', '#CE89BB', '#AF5589', '#833F6E', '#6D3359'],
    ['#C0AFD0', '#A081BA', '#695190', '#593979', '#452757'],
    ['#BFCDD0', '#9BB3BD', '#5B7F96', '#3B5769', '#282E3B'],
    ['#A0BCD4', '#628EC0', '#345188', '#2D3F6D', '#323950'],
    ['#96C8DA', '#4AAAD6', '#017ABB', '#1F5A91', '#2A415F'],
    ['#95D5E6', '#66BDD0', '#1287AB', '#116C8A', '#0B4861'],
    ['#8FDAD5', '#60C8C2', '#299C9D', '#12797C', '#0A5860'],
    ['#C5DEDC', '#9CC2C4', '#6FA2AA', '#4D7C87', '#195B6D'],
    ['#BAEED1', '#7FC9B1', '#29A67F', '#06826D', '#0C5B56'],
  ],
  right: [
    ['#F8FAFC', '#E2E8F0', '#CBD5E1', '#94A3B8', '#475569'],
    ['#CBE8BC', '#87D18C', '#37A469', '#138B63', '#166D56'],
    ['#C1E1C4', '#A1C99D', '#70A479', '#518365', '#2B4F43'],
    ['#9BD68D', '#5FAE40', '#2E8C3E', '#01733A', '#214A35'],
    ['#CAE76E', '#9BC94A', '#6C9F3F', '#577B3D', '#365535'],
    ['#CFD958', '#C0BB16', '#949029', '#5B5D20', '#414523'],
    ['#DED45B', '#D7BC32', '#C6A02E', '#A18528', '#5F521B'],
    ['#E2D5B3', '#CDBA91', '#B09A6D', '#9E7F4B', '#725B35'],
    ['#F7BB75', '#B48844', '#8E5D29', '#644A25', '#4D3F27'],
    ['#F3BB79', '#C68448', '#AB5B2D', '#753A1B', '#543826'],
    ['#F2D7B8', '#EDBE91', '#C87F58', '#A85541', '#603A36'],
    ['#F2D2B9', '#ECB59F', '#C57D75', '#AE4D53', '#85343B'],
    ['#CED2CD', '#AEB3B0', '#939794', '#676D69', '#3A403E'],
    ['#DBD2C3', '#BAB1A0', '#9D9180', '#837965', '#585047'],
    ['#E0EAE9', '#E5F7E2', '#FFD9CF', '#FAEBF4', '#FFFFC1'],
    ['#1BB0DD', '#B3E754', '#FF366D', '#FD3832', '#F9F944'],
  ],
};

const normalizeKey = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const getDefaultColorForMaterial = (material: MaterialDescriptor): string | undefined => {
  const key = normalizeKey(`${material.group} ${material.element} ${material.name}`);
  if (key.includes('avant') || key.includes('front')) return '#000000';
  if (key.includes('arriere') || key.includes('back')) return '#000000';
  if (key.includes('manche droite') || key.includes('right sleeve')) return '#000000';
  if (key.includes('manche gauche') || key.includes('left sleeve')) return '#000000';
  if (key.includes('col') || key.includes('collar') || key.includes('neck')) return '#ef7218';
  return undefined;
};

const buildMaterialColors = (
  nextMaterials: MaterialDescriptor[],
  previousColors: Record<string, string> = {}
) => {
  const next: Record<string, string> = {};
  const hasUserColors = Object.keys(previousColors).length > 0;
  nextMaterials.forEach(material => {
    if (hasUserColors && previousColors[material.id]) {
      next[material.id] = previousColors[material.id];
      return;
    }
    const mapped = getDefaultColorForMaterial(material);
    if (mapped) {
      next[material.id] = mapped;
      return;
    }
    next[material.id] = material.defaultColor || '#ffffff';
  });
  return next;
};

const getContrastGradientColor = (hex: string) => {
  const normalized = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return '#ffffff';
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

const analyzeImageForBlackWhite = (image: HTMLImageElement) => {
  const canvas = document.createElement('canvas');
  const size = 64;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { hasWhite: false, hasBlack: false };
  ctx.drawImage(image, 0, 0, size, size);
  const data = ctx.getImageData(0, 0, size, size).data;
  let whiteCount = 0;
  let blackCount = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 20) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (r > 210 && g > 210 && b > 210) whiteCount += 1;
    if (r < 15 && g < 15 && b < 15) blackCount += 1;
    if (whiteCount > 12 && blackCount > 12) break;
  }
  return { hasWhite: whiteCount > 12, hasBlack: blackCount > 12 };
};

const captureViewImage = (
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  angleRad: number,
  size = 900,
  quality = 0.8,
  forcedDesignTexture?: THREE.Texture
) => {
  const prevTarget = renderer.getRenderTarget();
  const prevClear = renderer.getClearColor(new THREE.Color());
  const prevClearAlpha = renderer.getClearAlpha();

  // Compute bbox from mesh geometry only — lights at large world positions would otherwise
  // inflate the box and push the camera far away, making the model appear tiny.
  const bbox = new THREE.Box3();
  const tempBox = new THREE.Box3();
  scene.traverse(obj => {
    if (!(obj instanceof THREE.Mesh)) return;
    if (obj.name.endsWith('_overlay')) return;
    const geo = (obj as THREE.Mesh).geometry;
    if (!geo) return;
    if (!geo.boundingBox) geo.computeBoundingBox();
    if (!geo.boundingBox) return;
    tempBox.copy(geo.boundingBox).applyMatrix4(obj.matrixWorld);
    bbox.union(tempBox);
  });
  if (bbox.isEmpty()) bbox.setFromObject(scene);
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  const radius = bbox.getBoundingSphere(new THREE.Sphere()).radius || 1;
  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
  const fov = (camera.fov * Math.PI) / 180;
  const distance = radius / Math.sin(fov / 2);
  camera.position.set(Math.sin(angleRad) * distance, distance * 0.05, Math.cos(angleRad) * distance);
  camera.lookAt(center);
  camera.near = Math.max(0.01, distance / 50);
  camera.far = distance * 5;
  camera.updateProjectionMatrix();

  const renderTarget = new THREE.WebGLRenderTarget(size, size);
  if ('LinearSRGBColorSpace' in THREE && 'colorSpace' in renderTarget.texture) {
    renderTarget.texture.colorSpace = (THREE as any).LinearSRGBColorSpace;
  }
  renderer.setRenderTarget(renderTarget);
  renderer.setClearColor('#ffffff', 1);
  renderer.clear();
  // Warmup render: on first offscreen pass, shaders may recompile and replace
  // designMix uniforms. We rebind after this render when forced texture is provided.
  renderer.render(scene, camera);
  if (forcedDesignTexture) {
    scene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return;
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      materials.forEach(material => {
        const uniforms = (material.userData as any)?.designMixUniforms as
          | { designMap?: { value: THREE.Texture }; printStrength?: { value: number } }
          | undefined;
        if (!uniforms?.designMap) return;
        uniforms.designMap.value = forcedDesignTexture;
        if (uniforms.printStrength) {
          uniforms.printStrength.value = 1;
        }
      });
    });
  }
  renderer.clear();
  renderer.render(scene, camera);

  const buffer = new Uint8Array(size * size * 4);
  renderer.readRenderTargetPixels(renderTarget, 0, 0, size, size, buffer);
  renderer.setRenderTarget(prevTarget);
  renderer.setClearColor(prevClear, prevClearAlpha);
  renderTarget.dispose();

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const imageData = ctx.createImageData(size, size);
  const shouldConvertToSrgb = renderTarget.texture.colorSpace !== THREE.SRGBColorSpace;
  const toSrgb = (value: number) => {
    const v = value / 255;
    const srgb = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
    return Math.min(255, Math.max(0, Math.round(srgb * 255)));
  };
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const srcIndex = (x + (size - y - 1) * size) * 4;
      const dstIndex = (x + y * size) * 4;
      const r = buffer[srcIndex];
      const g = buffer[srcIndex + 1];
      const b = buffer[srcIndex + 2];
      imageData.data[dstIndex] = shouldConvertToSrgb ? toSrgb(r) : r;
      imageData.data[dstIndex + 1] = shouldConvertToSrgb ? toSrgb(g) : g;
      imageData.data[dstIndex + 2] = shouldConvertToSrgb ? toSrgb(b) : b;
      imageData.data[dstIndex + 3] = buffer[srcIndex + 3];
    }
  }
  ctx.putImageData(imageData, 0, 0);

  return canvas.toDataURL('image/jpeg', quality);
};

function App() {
  const orbitControlsRef = useRef<any>(null);
  const panelScrollRef = useRef<HTMLDivElement | null>(null);
  const motifOrderRef = useRef(0);
  const previewRendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const previewSceneRef = useRef<THREE.Scene | null>(null);
  const previewCameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const importDesignInputRef = useRef<HTMLInputElement | null>(null);
  const importCreationInputRef = useRef<HTMLInputElement | null>(null);
  const linkAutoLoadRef = useRef(false);

  const getBaseMotifLayersById = (motifId: string) =>
    layersManager.layers.filter(layer => {
      if (!layer.data?.isMotif) return false;
      if ((layer.data as any)?.isPatternMotif) return false;
      const layerMotifId = (layer.data as any)?.motifId || layer.id;
      return layerMotifId === motifId;
    });

  const getPrimaryMotifLayer = (motifId: string) => {
    const pinned = motifLayerIdsRef.current[motifId];
    if (pinned) {
      const found = layersManager.layers.find(layer => layer.id === pinned);
      if (found) return found;
    }
    return getBaseMotifLayersById(motifId)[0] || null;
  };

  const bringMotifToFront = (motifId: string) => {
    const targetLayers = getBaseMotifLayersById(motifId);
    if (targetLayers.length === 0) return;
    const motifOrders = layersManager.layers
      .filter(layer => layer.data?.isMotif)
      .map(layer => layer.order);
    const maxMotifOrder = motifOrders.length ? Math.max(...motifOrders) : -1;
    const nonMotifOrders = layersManager.layers
      .filter(layer => !layer.data?.isMotif)
      .map(layer => layer.order);
    const minNonMotifOrder = nonMotifOrders.length ? Math.min(...nonMotifOrders) : undefined;
    const desiredOrder =
      minNonMotifOrder !== undefined
        ? Math.min(maxMotifOrder + 1, minNonMotifOrder - 0.01)
        : maxMotifOrder + 1;
    targetLayers.forEach(layer => {
      layersManager.updateLayer(layer.id, { order: desiredOrder });
    });
  };
  const motifLayerIdsRef = useRef<Record<string, string>>({});
  const patternLayerIdsRef = useRef<Record<string, string[]>>({});
  const motifImageCacheRef = useRef<Record<string, HTMLImageElement>>({});
  const defaultDesignAppliedRef = useRef(false);
  const systemLayerIdRef = useRef<string | null>(null);
  const isSyncingMaterialColorsRef = useRef(false);
  // Set true before programmatic setMaterialColors that must NOT enter history
  const skipNextColorHistoryRef = useRef(false);
  const [showIntro, setShowIntro] = useState(true);
  const [activeModel, setActiveModel] = useState<ModelItem | null>(DEFAULT_MODEL);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [creationImportIssues, setCreationImportIssues] = useState<string[] | null>(null);
  const [sessionAccess, setSessionAccess] = useState<'editor' | 'viewer'>('editor');
  const [bestDesignFiles, setBestDesignFiles] = useState<Record<string, CreationFileV1>>({});
  const [bestDesignPreviewUrls, setBestDesignPreviewUrls] = useState<Record<string, string>>({});
  const [isBestPreviewRendering, setIsBestPreviewRendering] = useState(false);
  const [shareForm, setShareForm] = useState<ShareFormState>({
    visible: false,
    email: '',
    consentAssist: true,
    error: null,
  });
  const [exportModal, setExportModal] = useState<ExportModalState>({
    visible: false,
    pdfUrl: null,
    captures: [],
    initialMessage: '',
  });
  const [showWelcome, setShowWelcome] = useState(!hasSeenWelcome());
  const [showTutorial, setShowTutorial] = useState(!hasDoneTutorial());
  const [showPriceSimulator, setShowPriceSimulator] = useState(false);
  const [pausePreviewGeneration, setPausePreviewGeneration] = useState(false);
  const exportPdfUrlRef = useRef<string | null>(null);
  const [creationFeedback, setCreationFeedback] = useState<CreationFeedbackState>({
    visible: false,
    mode: null,
    message: '',
  });
  const creationFeedbackTimerRef = useRef<number | null>(null);
  const [designPreviewUrls, setDesignPreviewUrls] = useState<Record<string, string>>({});
  const [previewModelKey, setPreviewModelKey] = useState<string>('');
  const previewBusyRef = useRef(false);
  const bestPreviewBusyRef = useRef(false);
  const previewRenderJobsRef = useRef(0);
  const [modelPreviewRefreshKey, setModelPreviewRefreshKey] = useState(0);
  const designStateCacheRef = useRef<
    Record<
      string,
      {
        motifMaterialColors: Record<string, string>;
        motifDesignColors: { design1: string; design2: string };
        selectedMaterialIds: string[];
        materialSelections: Record<string, boolean>;
      }
    >
  >({});
  const materialsSignatureRef = useRef('');
  const [appState, setAppState] = useState<AppState>({
    layers: [],
    selectedLayerId: null,
    globalTransformMode: 'translate',
    isModelLoading: true,
    modelError: null,
    isExporting: false,
  });

  const [activeTab, setActiveTab] = useState<TabKey>('design');
  // Map internal tabs (motif, gradient) to their parent nav entry for highlighting
  const displayTab: TabKey = activeTab === 'motif' ? 'design' : activeTab === 'gradient' ? 'colors' : activeTab;
  const [isDragging, setIsDragging] = useState(false);
  const [modelIntroReady, setModelIntroReady] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [designSubTab, setDesignSubTab] = useState<'designs' | 'best'>('designs');
  const [colorSubTab, setColorSubTab] = useState<'groups' | 'custom'>('groups');
  const [fontSearch, setFontSearch] = useState('');
  const [secondaryColor] = useState('#ffffff');
  const [materials, setMaterials] = useState<MaterialDescriptor[]>([]);
  const [materialColors, setMaterialColors] = useState<Record<string, string>>({});
  const materialColorsRef = useRef<Record<string, string>>({});
  const [materialUvBounds, setMaterialUvBounds] = useState<
    Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]>
  >({});
  const [zoneUvBounds, setZoneUvBounds] = useState<
    Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }>
  >({});
  const [materialUvTriangles, setMaterialUvTriangles] = useState<Record<string, number[][]>>({});
  const [motifColorFlags, setMotifColorFlags] = useState<Record<string, { hasWhite: boolean; hasBlack: boolean }>>({});
  const [activeColorPicker, setActiveColorPicker] = useState<MotifPickerTarget | null>(null);
  const [colorPickerOffset, setColorPickerOffset] = useState<{ top: number; height: number } | null>(null);
  const [expandedColorGroups, setExpandedColorGroups] = useState<Record<string, boolean>>({});
  const [expandedMotifGroups, setExpandedMotifGroups] = useState<Record<string, boolean>>({});
  const [colorPickerMode, setColorPickerMode] = useState<'solid' | 'gradient'>('solid');
  const [gradientStop, setGradientStop] = useState<'start' | 'end'>('start');
  const [gradientAngle, setGradientAngle] = useState(90);
  const [gradientColors, setGradientColors] = useState({ start: '#ffffff', end: '#ffffff' });
  const [gradientBalance, setGradientBalance] = useState(50);
  const [gradientMobileTab, setGradientMobileTab] = useState<'primary' | 'secondary' | 'balance'>('primary');
  const gradientApplyTimerRef = useRef<number | null>(null);
  const [isColorCopyMode, setIsColorCopyMode] = useState(false);
  const [colorCopySource, setColorCopySource] = useState<{
    key: string;
    label: string;
    color: string;
  } | null>(null);
  const [motifMaterialSelections, setMotifMaterialSelections] = useState<Record<string, Record<string, boolean>>>({});
  const [activeMotifModal, setActiveMotifModal] = useState<string | null>(null);
  const [selectedMotifTarget, setSelectedMotifTarget] = useState<string | null>(null);
  const [motifPanelMode, setMotifPanelMode] = useState<'list' | 'grid'>('list');
  const [patternMotifColors, setPatternMotifColors] = useState({ motif1: '#000000', motif2: '#ffffff' });
  const [textForm, setTextForm] = useState({
    text: 'Texte',
    fontFamily: 'Arial',
    fontSize: 200,
    color: '#000000',
    strokeColor: '#000000',
    strokeWidth: 0,
  });
  const [numberForm, setNumberForm] = useState({
    text: '00',
    fontFamily: 'Anton',
    fontSize: 260,
    color: '#ffffff',
    strokeColor: '#000000',
    strokeWidth: 14,
  });
  const [nameForm, setNameForm] = useState({
    text: 'Nom',
    fontFamily: 'Bebas Neue',
    fontSize: 180,
    color: '#111827',
    strokeColor: '#ffffff',
    strokeWidth: 0,
  });
  const [editTextForm, setEditTextForm] = useState<EditTextForm>({
    text: '',
    fontFamily: 'Arial',
    fontSize: 200,
    color: '#000000',
    strokeColor: '#000000',
    strokeWidth: 0,
    rotation: 0,
  });

  materialColorsRef.current = materialColors;

  const layersManager = useLayers({
    materialColorsRef,
    onRestoreMaterialColors: setMaterialColors,
  });

  useEffect(() => {
    if (LOCAL_FONT_OPTIONS.length === 0) return;

    const styleId = 'indy-local-font-face-style';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = LOCAL_FONT_OPTIONS.map(font => {
      const escapedFamily = font.family.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return [
        '@font-face {',
        `  font-family: "${escapedFamily}";`,
        `  src: url("${font.url}") format("${font.format}");`,
        '  font-style: normal;',
        '  font-weight: 400;',
        '  font-display: swap;',
        '}',
      ].join('\n');
    }).join('\n');

    if ('fonts' in document) {
      void Promise.allSettled(
        LOCAL_FONT_OPTIONS.map(font => document.fonts.load(`16px "${font.family}"`))
      );
    }
  }, []);

  const getZonePlacement = (zone: string) => {
    const bounds = zoneUvBounds[zone];
    if (!bounds) return null;
    const width = Math.max(0.01, bounds.uMax - bounds.uMin);
    const height = Math.max(0.01, bounds.vMax - bounds.vMin);
    return {
      uvCenter: [(bounds.uMin + bounds.uMax) / 2, (bounds.vMin + bounds.vMax) / 2] as [number, number],
      uvSize: [width, height] as [number, number],
    };
  };

  const getPlacementFromBounds = (
    boundsList?: { uMin: number; uMax: number; vMin: number; vMax: number }[]
  ) => {
    if (!boundsList || boundsList.length === 0) return null;
    let uMin = 1;
    let vMin = 1;
    let uMax = 0;
    let vMax = 0;
    boundsList.forEach(bounds => {
      uMin = Math.min(uMin, bounds.uMin);
      vMin = Math.min(vMin, bounds.vMin);
      uMax = Math.max(uMax, bounds.uMax);
      vMax = Math.max(vMax, bounds.vMax);
    });
    const width = Math.max(0.01, uMax - uMin);
    const height = Math.max(0.01, vMax - vMin);
    return {
      uvCenter: [(uMin + uMax) / 2, (vMin + vMax) / 2] as [number, number],
      uvSize: [width, height] as [number, number],
    };
  };

  const buildAutoMotifPlacements = (): { zone: TShirtZone; materialId?: string; uvCenter: [number, number]; uvSize: [number, number] }[] => {
    const zoneMaterialMap: Partial<Record<TShirtZone, string>> = {};

    materials.forEach(material => {
      const key = normalizeKey(`${material.group} ${material.element} ${material.name}`);

      if (!zoneMaterialMap.front && (key.includes('avant') || key.includes('front'))) {
        zoneMaterialMap.front = material.id;
        return;
      }
      if (!zoneMaterialMap.back && (key.includes('arriere') || key.includes('back'))) {
        zoneMaterialMap.back = material.id;
        return;
      }
      if (
        !zoneMaterialMap.leftSleeve &&
        (key.includes('manche gauche') || key.includes('leftsleeve') || key.includes('left sleeve'))
      ) {
        zoneMaterialMap.leftSleeve = material.id;
        return;
      }
      if (
        !zoneMaterialMap.rightSleeve &&
        (key.includes('manche droite') || key.includes('rightsleeve') || key.includes('right sleeve'))
      ) {
        zoneMaterialMap.rightSleeve = material.id;
      }
    });

    const zoneOrder: TShirtZone[] = ['front', 'back', 'leftSleeve', 'rightSleeve'];
    const placements: { zone: TShirtZone; materialId?: string; uvCenter: [number, number]; uvSize: [number, number] }[] = [];

    zoneOrder.forEach(zone => {
      const materialId = zoneMaterialMap[zone];
      const fromMaterial = materialId ? getPlacementFromBounds(materialUvBounds[materialId]) : null;
      const placement = fromMaterial || getZonePlacement(zone);
      if (!placement) return;
      placements.push({
        zone,
        materialId,
        uvCenter: placement.uvCenter,
        uvSize: placement.uvSize,
      });
    });

    return placements;
  };

  const getTabForLayer = useCallback((layer: Layer | null): TabKey => {
    if (!layer) return 'colors';
    if (layer.type === 'image') return 'logos';
    if (layer.type === 'text') {
      const kind = (layer.data as any)?.kind;
      if (kind === 'number') return 'numbers';
      if (kind === 'name') return 'name';
      return 'text';
    }
    return 'colors';
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setShowIntro(false);
      return;
    }

    const introTimer = window.setTimeout(() => {
      setShowIntro(false);
    }, 2000);

    return () => window.clearTimeout(introTimer);
  }, []);

  useEffect(() => {
    if (showIntro) {
      setModelIntroReady(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setModelIntroReady(true);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [showIntro]);

  useEffect(() => {
    return () => {
      if (creationFeedbackTimerRef.current !== null) {
        window.clearTimeout(creationFeedbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!activeModel) return;
    setAppState(prev => ({ ...prev, isModelLoading: true, modelError: null }));
    setDesignPreviewUrls({});
    setBestDesignPreviewUrls({});
    setPreviewModelKey(activeModel?.url || '');
    skipNextColorHistoryRef.current = true;
    setMaterialColors({});
    defaultDesignAppliedRef.current = false;
    systemLayerIdRef.current = null;
  }, [activeModel?.url]);

  useEffect(() => {
    setAppState(prev => ({ ...prev, selectedLayerId: layersManager.selectedLayer?.id || null }));
  }, [layersManager.selectedLayer]);

  useEffect(() => {
    if (materials.length === 0) return;
    const motifLayers = layersManager.layers.filter(
      layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif
    );
    if (motifLayers.length === 0) return;

    let selectionsChanged = false;
    const nextSelections = { ...motifMaterialSelections };

    motifLayers.forEach(layer => {
      const motifId = (layer.data as any)?.motifId || layer.id;
      if (!motifId) return;
      const current = { ...(nextSelections[motifId] || {}) };
      let changed = false;
      materials.forEach(material => {
        if (current[material.id] === undefined) {
          current[material.id] = true;
          changed = true;
        }
      });
      if (changed) {
        nextSelections[motifId] = current;
        const selectedIds = Object.entries(current)
          .filter(([, value]) => value)
          .map(([id]) => id);
        layersManager.updateLayer(layer.id, {
          data: { ...(layer.data as any), selectedMaterialIds: selectedIds },
        });
        selectionsChanged = true;
      }
    });

    if (selectionsChanged) {
      setMotifMaterialSelections(nextSelections);
    }
  }, [materials, layersManager.layers, motifMaterialSelections, layersManager]);

  useEffect(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = !isDragging;
    }
  }, [isDragging]);

  useEffect(() => {
    if (!activeColorPicker) {
      setColorPickerOffset(null);
      return;
    }
    if (!panelScrollRef.current) return;
    setColorPickerOffset({
      top: panelScrollRef.current.scrollTop,
      height: panelScrollRef.current.clientHeight,
    });
  }, [activeColorPicker]);

  useEffect(() => {
    setActiveColorPicker(null);
  }, [activeTab]);

  useEffect(() => {
    if (activeMotifModal) {
      setExpandedMotifGroups({});
    }
  }, [activeMotifModal]);

  useEffect(() => {
    if (activeTab === 'gradient') {
      setGradientMobileTab('primary');
      setGradientStop('start');
    }
  }, [activeTab]);

  useEffect(() => {
    const validIds = new Set(layersManager.layers.map(layer => layer.id));
    Object.entries(patternLayerIdsRef.current).forEach(([targetId, layerIds]) => {
      const keptIds = (Array.isArray(layerIds) ? layerIds : [layerIds]).filter(layerId => validIds.has(layerId));
      if (keptIds.length === 0) {
        delete patternLayerIdsRef.current[targetId];
      } else {
        patternLayerIdsRef.current[targetId] = keptIds;
      }
    });
  }, [layersManager.layers]);

  const handleResetModel = useCallback(() => {
    if (DEFAULT_MODEL) {
      setActiveModel(DEFAULT_MODEL);
    }
    layersManager.clearAllLayers();
    skipNextColorHistoryRef.current = true;
    setMaterialColors(buildMaterialColors(materials));
    layersManager.clearHistory();
    setActiveTab('design');
    setActiveColorPicker(null);
    setActiveMotifModal(null);
  }, [layersManager, materials]);

  useEffect(() => {
    if (activeColorPicker) {
      setColorPickerMode('solid');
    }
  }, [activeColorPicker]);

  useEffect(() => {
    if (!appState.isModelLoading && orbitControlsRef.current) {
      orbitControlsRef.current.target.set(0, 0, 0);
      orbitControlsRef.current.update();
    }
  }, [appState.isModelLoading]);

  useEffect(() => {
    if (appState.isModelLoading) return;
    if (defaultDesignAppliedRef.current) return;
    defaultDesignAppliedRef.current = true;
    const pointDiagonal = DESIGN_ITEMS.find(item => item.name === 'Point diagonal');
    if (pointDiagonal) {
      void handleAddDesign(pointDiagonal);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.isModelLoading, activeModel?.url]);

  useEffect(() => {
    const selectedLayer = layersManager.selectedLayer;
    if (selectedLayer?.type === 'text' && selectedLayer.data) {
      const data = selectedLayer.data as any;
      // Use the fontSize stored in the layer — never auto-resize during edits
      setEditTextForm({
        text: data.text || '',
        fontFamily: data.fontFamily || 'Arial',
        fontSize: data.fontSize > 0 ? data.fontSize : 200,
        color: data.color || '#000000',
        strokeColor: data.strokeColor || '#000000',
        strokeWidth: data.strokeWidth || 0,
        rotation: normalizeRotation(selectedLayer.uvRotation || 0),
      });
      setActiveTab(getTabForLayer(selectedLayer));
      return;
    }
    if (selectedLayer?.type === 'image') {
      setActiveTab(getTabForLayer(selectedLayer));
    }
  }, [layersManager.selectedLayer, getTabForLayer]);

  const handleLayerSelect = (layerId: string | null) => {
    if (!layerId) {
      layersManager.selectLayer(null);
      return;
    }
    const layer = layersManager.layers.find(item => item.id === layerId);
    if (layer?.data?.isMotif) {
      layersManager.selectLayer(null);
      return;
    }
    layersManager.selectLayer(layerId);
  };

  const handleImageAdd = async (file: File) => {
    try {
      await layersManager.addImageLayer(file);
    } catch (error) {
      console.error('Image add error:', error);
      alert('Image add error. Check file validity.');
    }
  };

  const handleTextAdd = (textData: any) => {
    try {
      layersManager.addTextLayer(textData);
    } catch (error) {
      console.error('Text add error:', error);
      alert('Text add error.');
    }
  };

  const handleTextFormChange = (field: string, value: string | number) => {
    setTextForm(prev => ({ ...prev, [field]: value }));
  };

  const applyTextFormToLayer = (layer: Layer, nextForm: EditTextForm, silent = false) => {
    if (layer.type !== 'text') return;
    const textData = {
      text: nextForm.text,
      fontFamily: nextForm.fontFamily,
      fontSize: nextForm.fontSize,
      color: nextForm.color,
      strokeColor: nextForm.strokeColor,
      strokeWidth: nextForm.strokeWidth,
      kind: (layer.data as any)?.kind,
      canvasResolution: (layer.data as any)?.canvasResolution || 1024,
    };
    const canvas = createTextCanvas(textData);
    layersManager.updateLayer(layer.id, {
      source: canvas,
      data: textData,
      uvRotation: nextForm.rotation,
    }, { silent });
  };

  const handleEditTextFormChange = (field: string, value: string | number, silent = false) => {
    const nextForm: EditTextForm = { ...editTextForm, [field]: value } as EditTextForm;
    if (field === 'rotation') nextForm.rotation = normalizeRotation(nextForm.rotation);
    setEditTextForm(nextForm);
    const layer = layersManager.selectedLayer;
    if (!layer || layer.type !== 'text') return;
    applyTextFormToLayer(layer, nextForm, silent);
  };

  const closeTextEdit = () => {
    const tab = getTabForLayer(layersManager.selectedLayer);
    setActiveTab(tab);
    layersManager.selectLayer(null);
    setActiveColorPicker(null);
    setShowFontPicker(false);
    setFontSearch('');
  };

const handleImageLayerRotate = (layerId: string, deltaDeg: number) => {
    const layer = layersManager.layers.find(item => item.id === layerId);
    if (!layer) return;
    const deltaRad = (deltaDeg * Math.PI) / 180;
    handleLayerTransform(layerId, { uvRotation: layer.uvRotation + deltaRad });
  };

  const handleImageLayerScale = (layerId: string, factor: number) => {
    const layer = layersManager.layers.find(item => item.id === layerId);
    if (!layer) return;
    const [u, v] = layer.uvSize;
    const clamp = (value: number) => Math.max(0.03, Math.min(1, value));
    handleLayerTransform(layerId, { uvSize: [clamp(u * factor), clamp(v * factor)] });
  };


  const isMaterialOccupied = (materialId: string, excludeMotifId?: string) => {
    return layersManager.layers.some(layer => {
      if (!layer.data?.isMotif) return false;
      if ((layer.data as any)?.isPatternMotif) return false;
      const layerMotifId = (layer.data as any)?.motifId || layer.id;
      if (excludeMotifId && layerMotifId === excludeMotifId) return false;
      const selectedIds = (layer.data as any)?.selectedMaterialIds as string[] | undefined;
      if (!selectedIds) return true;
      return selectedIds.includes(materialId);
    });
  };

  const handleAddDesign = async (design: DesignItem) => {
    try {
      cacheCurrentDesignState();
      const activeBaseLayer = layersManager.layers.find(
        layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif
      );
      const activeSelectedIds =
        ((activeBaseLayer?.data as any)?.selectedMaterialIds as string[] | undefined) || undefined;
      const activeMaterialColors =
        ((activeBaseLayer?.data as any)?.motifMaterialColors as Record<string, string> | undefined) || undefined;
      const activeDesignColors =
        ((activeBaseLayer?.data as any)?.motifDesignColors as { design1?: string; design2?: string } | undefined) ||
        undefined;
      if (design.isNone) {
        layersManager.layers
          .filter(layer => layer.data?.isMotif)
          .forEach(layer => layersManager.removeLayer(layer.id));
        motifLayerIdsRef.current = {};
        setMotifMaterialSelections({});
        motifOrderRef.current = 0;
        return;
      }

      layersManager.layers
        .filter(layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif)
        .forEach(layer => layersManager.removeLayer(layer.id));
      motifLayerIdsRef.current = {};
      setMotifMaterialSelections({});
      motifOrderRef.current = 0;

      const motifSourceKey = design.sourcePath || design.name;
      const cachedState = designStateCacheRef.current[motifSourceKey];
      const motifId = `motif_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const allSelections: Record<string, boolean> = {};
      const allColors: Record<string, string> = {};
      const activeSelectionMap = buildSelectionsFromIds(
        activeSelectedIds && activeSelectedIds.length > 0 ? activeSelectedIds : undefined
      );
      const cachedSelectionMap = cachedState?.materialSelections;
      const cachedHasAnySelection = cachedSelectionMap ? Object.values(cachedSelectionMap).some(Boolean) : false;
      const selectionSource = cachedHasAnySelection ? cachedSelectionMap : activeSelectionMap;
      materials.forEach(material => {
        allSelections[material.id] =
          selectionSource?.[material.id] !== undefined
            ? selectionSource[material.id]
            : true;
        allColors[material.id] =
          cachedState?.motifMaterialColors?.[material.id] ||
          activeMaterialColors?.[material.id] ||
          '#f97316';
      });
      const hasAnySelection = Object.values(allSelections).some(Boolean);
      if (!hasAnySelection) {
        materials.forEach(material => {
          allSelections[material.id] = true;
        });
      }

      setMotifMaterialSelections(prev => ({ ...prev, [motifId]: allSelections }));

      const selectionMap = allSelections;
      const colorMap = { ...allColors };
      const motifDesignColors =
        cachedState?.motifDesignColors ||
        (activeDesignColors
          ? { design1: activeDesignColors.design1 || '#ef7218', design2: activeDesignColors.design2 || '#ffffff' }
          : { design1: '#ef7218', design2: '#ffffff' });

      let image = motifImageCacheRef.current[motifSourceKey];
      if (!image) {
        image = await loadImageFromUrl(design.url);
        motifImageCacheRef.current[motifSourceKey] = image;
      }
      const flags = analyzeImageForBlackWhite(image);
      setMotifColorFlags(prev => ({ ...prev, [motifSourceKey]: flags }));

      const selectedMaterialIds = Object.keys(selectionMap).filter(id => selectionMap[id]);
      const baseLayer: Omit<Layer, 'id' | 'order'> = {
        type: 'image',
        zone: 'front',
        locked: true,
        source: image,
        uvCenter: [0.5, 0.5],
        uvSize: [1, 1],
        uvRotation: 0,
        transformMode: 'translate',
        data: {
          fileName: design.name,
          originalSize: { width: image.width, height: image.height },
          needsInitialPlacement: false,
          isMotif: true,
          isGlobalUv: true,
          motifId,
          motifSourceKey,
          motifLabel: design.name,
          selectedMaterialIds,
          motifMaterialColors: { ...colorMap },
          motifDesignColors: { ...motifDesignColors },
          motifPlacementMaterialId: undefined,
        }
      };
      const layer = layersManager.addLayer(baseLayer);
      motifOrderRef.current -= 1;
      layersManager.updateLayer(layer.id, { order: motifOrderRef.current });
      motifLayerIdsRef.current[motifId] = layer.id;
      bringMotifToFront(motifId);
    } catch (error) {
      console.error('Design add error:', error);
      alert('Design add error.');
    }
  };

  const handleImportDesign = async (file: File) => {
    try {
      cacheCurrentDesignState();
      setActiveTab('design');
      setActiveColorPicker(null);
      layersManager.layers
        .filter(layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif)
        .forEach(layer => layersManager.removeLayer(layer.id));
      motifLayerIdsRef.current = {};
      setMotifMaterialSelections({});
      motifOrderRef.current = 0;
      const image = await loadImageFromFile(file);
      const motifSourceKey = `import_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const motifId = `motif_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const allSelections: Record<string, boolean> = {};
      const allColors: Record<string, string> = {};
      materials.forEach(material => {
        allSelections[material.id] = true;
        allColors[material.id] = '#f97316';
      });

      setMotifMaterialSelections(prev => ({ ...prev, [motifId]: allSelections }));

      const selectionMap = allSelections;
      const colorMap = { ...allColors };
      motifImageCacheRef.current[motifSourceKey] = image;
      const flags = analyzeImageForBlackWhite(image);
      setMotifColorFlags(prev => ({ ...prev, [motifSourceKey]: flags }));

      const selectedMaterialIds = Object.keys(selectionMap).filter(id => selectionMap[id]);
      const baseLayer: Omit<Layer, 'id' | 'order'> = {
        type: 'image',
        zone: 'front',
        locked: true,
        source: image,
        uvCenter: [0.5, 0.5],
        uvSize: [1, 1],
        uvRotation: 0,
        transformMode: 'translate',
        data: {
          fileName: file.name,
          originalSize: { width: image.width, height: image.height },
          needsInitialPlacement: false,
          isMotif: true,
          isGlobalUv: true,
          motifId,
          motifSourceKey,
          motifLabel: file.name.replace(/\.[^/.]+$/, ''),
          selectedMaterialIds,
          motifMaterialColors: { ...colorMap },
          motifDesignColors: { design1: '#ef7218', design2: '#ffffff' },
          motifPlacementMaterialId: undefined,
        }
      };
      const layer = layersManager.addLayer(baseLayer);
      motifOrderRef.current -= 1;
      layersManager.updateLayer(layer.id, { order: motifOrderRef.current });
      motifLayerIdsRef.current[motifId] = layer.id;
      bringMotifToFront(motifId);
    } catch (error) {
      console.error('Design import error:', error);
      alert('Import design error.');
    }
  };

  const wait = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));

  const closeCreationFeedback = () => {
    setCreationFeedback({ visible: false, mode: null, message: '' });
  };

  const playLoadedFeedback = async () => {
    setCreationFeedback({ visible: true, mode: 'loaded', message: 'Chargement de votre creation en cours...' });
    if (creationFeedbackTimerRef.current !== null) {
      window.clearTimeout(creationFeedbackTimerRef.current);
    }
    await new Promise<void>(resolve => {
      creationFeedbackTimerRef.current = window.setTimeout(() => {
        creationFeedbackTimerRef.current = null;
        setCreationFeedback({ visible: false, mode: null, message: '' });
        resolve();
      }, 2000);
    });
  };

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      console.warn('Clipboard copy failed:', error);
    }
  };

  const sourceToDataUrl = async (source: CanvasImageSource): Promise<string | null> => {
    if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) {
      try {
        return source.toDataURL('image/png');
      } catch {
        return null;
      }
    }
    if (typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement) {
      try {
        if (source.src && source.src.startsWith('data:')) return source.src;
        const width = source.naturalWidth || source.width;
        const height = source.naturalHeight || source.height;
        if (width > 0 && height > 0) {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(source, 0, 0, width, height);
            return canvas.toDataURL('image/png');
          }
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  const serializeLayer = async (layer: Layer): Promise<SerializedLayer | null> => {
    const data = layer.data ? { ...(layer.data as any) } : undefined;
    if ((data as any)?.isSystem) return null;

    const serialized: SerializedLayer = {
      type: layer.type,
      zone: layer.zone,
      locked: layer.locked,
      uvCenter: [...layer.uvCenter] as [number, number],
      uvSize: [...layer.uvSize] as [number, number],
      uvRotation: layer.uvRotation,
      order: layer.order,
      transformMode: layer.transformMode,
      data,
    };

    if (layer.type === 'image') {
      if ((data as any)?.isMotif) {
        const motifSourceKey = (data as any)?.motifSourceKey || (data as any)?.fileName;
        if (motifSourceKey) {
          serialized.source = {
            kind: 'assetRef',
            value: motifSourceKey,
            collection: (data as any)?.isPatternMotif ? 'motif' : 'design',
          };
          return serialized;
        }
      }
      const dataUrl = await sourceToDataUrl(layer.source);
      if (dataUrl) {
        serialized.source = { kind: 'dataUrl', value: dataUrl };
      } else if (typeof HTMLImageElement !== 'undefined' && layer.source instanceof HTMLImageElement) {
        const src = layer.source.currentSrc || layer.source.src;
        if (src) {
          serialized.source = { kind: 'url', value: src };
        }
      }
    }

    return serialized;
  };

  const rebuildRuntimeLayerRefs = (layers: Layer[]) => {
    const nextMotifLayerIds: Record<string, string> = {};
    const nextPatternLayerIds: Record<string, string[]> = {};
    const nextMotifSelections: Record<string, Record<string, boolean>> = {};

    layers.forEach(layer => {
      if (!layer.data?.isMotif) return;
      const data = layer.data as any;
      const motifId = data?.motifId || layer.id;
      const selectedMaterialIds = (data?.selectedMaterialIds as string[] | undefined) || [];

      if (!data?.isPatternMotif) {
        if (!nextMotifLayerIds[motifId]) {
          nextMotifLayerIds[motifId] = layer.id;
        }
        const defaults: Record<string, boolean> = {};
        materials.forEach(material => {
          defaults[material.id] = selectedMaterialIds.length > 0 ? selectedMaterialIds.includes(material.id) : true;
        });
        nextMotifSelections[motifId] = defaults;
        return;
      }

      const targetKey = (data?.patternDesignKey as string | undefined) || (data?.patternMaterialId as string | undefined);
      if (!targetKey) return;
      if (!nextPatternLayerIds[targetKey]) {
        nextPatternLayerIds[targetKey] = [];
      }
      nextPatternLayerIds[targetKey].push(layer.id);
    });

    motifLayerIdsRef.current = nextMotifLayerIds;
    patternLayerIdsRef.current = nextPatternLayerIds;
    setMotifMaterialSelections(nextMotifSelections);
    motifOrderRef.current = Math.min(0, ...layers.map(layer => layer.order || 0));
  };

  const parseCreationFile = (raw: any): CreationFileV1 | null => {
    if (!raw || typeof raw !== 'object') return null;
    if (raw.creation && typeof raw.creation === 'object') {
      return parseCreationFile(raw.creation);
    }
    if (raw.format === 'indyanna.creation' && raw.version === 1 && raw.state) {
      return raw as CreationFileV1;
    }
    if (Array.isArray(raw.layers)) {
      return {
        format: 'indyanna.creation',
        version: 1,
        createdAt: new Date().toISOString(),
        state: {
          layers: raw.layers as SerializedLayer[],
          materialColors: (raw.materialColors || {}) as Record<string, string>,
          patternMotifColors:
            (raw.patternMotifColors as { motif1?: string; motif2?: string } | undefined)?.motif1
              ? {
                  motif1: (raw.patternMotifColors as any).motif1,
                  motif2: (raw.patternMotifColors as any).motif2 || '#ffffff',
                }
              : { motif1: '#000000', motif2: '#ffffff' },
          motifMaterialSelections: (raw.motifMaterialSelections || {}) as Record<string, Record<string, boolean>>,
        },
      };
    }
    return null;
  };

  const findCatalogItemBySourceValueInternal = (
    sourceValue: string,
    collection?: 'design' | 'motif',
    allowFuzzy = true
  ) => {
    const normalizedValue = sourceValue.toLowerCase().replace(/\\/g, '/');
    const normalizedNoPublic = normalizedValue.replace('/public/', '/');
    const stripBuildHash = (value: string) =>
      value
        .replace(/\.[^/.]+$/, '')
        .replace(/-[a-z0-9]{6,}(?:-[a-z0-9]{1,})?$/i, '')
        .trim();
    const valueTail = normalizedNoPublic.split('/').pop() || normalizedNoPublic;
    const valueTailBare = valueTail.replace(/\.[^/.]+$/, '');
    const valueTailNoHash = stripBuildHash(valueTail);
    const valueTailBareNoHash = stripBuildHash(valueTailBare);

    const getMatchScore = (item: DesignItem) => {
      if (item.isNone) return 0;
      const url = (item.url || '').toLowerCase().replace(/\\/g, '/');
      const sourcePath = (item.sourcePath || '').toLowerCase().replace(/\\/g, '/');
      const sourcePathNoPublic = sourcePath.replace('/public/', '/');
      const name = (item.name || '').toLowerCase();
      const fileName = url.split('/').pop()?.toLowerCase() || '';
      const sourceFileName = sourcePath.split('/').pop()?.toLowerCase() || '';
      const bareName = fileName.replace(/\.[^/.]+$/, '');
      const sourceBareName = sourceFileName.replace(/\.[^/.]+$/, '');
      const matchesDirect =
        url === normalizedValue ||
        url === normalizedNoPublic ||
        sourcePath === normalizedValue ||
        sourcePath === normalizedNoPublic ||
        sourcePathNoPublic === normalizedValue ||
        sourcePathNoPublic === normalizedNoPublic ||
        name === normalizedValue ||
        name === normalizedNoPublic ||
        fileName === normalizedValue ||
        fileName === normalizedNoPublic ||
        sourceFileName === normalizedValue ||
        sourceFileName === normalizedNoPublic ||
        bareName === normalizedValue ||
        bareName === normalizedNoPublic ||
        sourceBareName === normalizedValue ||
        sourceBareName === normalizedNoPublic;
      if (matchesDirect) return 300;

      if (fileName && (normalizedValue.endsWith(`/${fileName}`) || normalizedNoPublic.endsWith(`/${fileName}`))) {
        return 200;
      }
      if (
        sourceFileName &&
        (normalizedValue.endsWith(`/${sourceFileName}`) || normalizedNoPublic.endsWith(`/${sourceFileName}`))
      ) {
        return 200;
      }
      if (!allowFuzzy) return 0;

      const normalizedItemNoHash = stripBuildHash(fileName);
      const normalizedSourceItemNoHash = stripBuildHash(sourceFileName);
      const normalizedNameNoHash = stripBuildHash(name);
      if (
        (valueTailNoHash && normalizedItemNoHash && valueTailNoHash === normalizedItemNoHash) ||
        (valueTailNoHash && normalizedSourceItemNoHash && valueTailNoHash === normalizedSourceItemNoHash) ||
        (valueTailBareNoHash && normalizedItemNoHash && valueTailBareNoHash === normalizedItemNoHash) ||
        (valueTailBareNoHash && normalizedSourceItemNoHash && valueTailBareNoHash === normalizedSourceItemNoHash) ||
        (valueTailNoHash && normalizedNameNoHash && valueTailNoHash === normalizedNameNoHash) ||
        (valueTailBareNoHash && normalizedNameNoHash && valueTailBareNoHash === normalizedNameNoHash)
      ) {
        return 100;
      }
      return 0;
    };

    const pickBest = (items: DesignItem[]) => {
      let bestItem: DesignItem | undefined;
      let bestScore = 0;
      for (const item of items) {
        const score = getMatchScore(item);
        if (score <= bestScore) continue;
        bestScore = score;
        bestItem = item;
        if (score >= 300) break;
      }
      return { item: bestItem, score: bestScore };
    };

    if (collection === 'motif') {
      return pickBest(MOTIF_ITEMS).item;
    }
    if (collection === 'design') {
      return pickBest(DESIGN_ITEMS).item;
    }
    const designMatch = pickBest(DESIGN_ITEMS);
    const motifMatch = pickBest(MOTIF_ITEMS);
    return designMatch.score >= motifMatch.score ? designMatch.item : motifMatch.item;
  };

  const findCatalogItemBySourceValue = (sourceValue: string, collection?: 'design' | 'motif') =>
    findCatalogItemBySourceValueInternal(sourceValue, collection, true);

  const findCatalogItemBySourceValueStrict = (sourceValue: string, collection?: 'design' | 'motif') =>
    findCatalogItemBySourceValueInternal(sourceValue, collection, false);

  const deserializeLayerForBestPreview = async (serialized: SerializedLayer, index: number): Promise<Layer | null> => {
    if (!serialized || (serialized.type !== 'image' && serialized.type !== 'text')) return null;
    const rawData = serialized.data ? { ...(serialized.data as any) } : {};
    if ((rawData as any)?.isSystem) return null;

    if (serialized.type === 'text') {
      const textData = {
        text: rawData.text || 'Texte',
        fontFamily: rawData.fontFamily || 'Arial',
        fontSize: Number(rawData.fontSize) || 200,
        color: rawData.color || '#000000',
        strokeColor: rawData.strokeColor || '#000000',
        strokeWidth: Number(rawData.strokeWidth) || 0,
        kind: rawData.kind,
        canvasResolution: Number(rawData.canvasResolution) || 1024,
      };
      return {
        id: `best_prev_text_${index}_${Math.random().toString(36).slice(2, 6)}`,
        type: 'text',
        zone: serialized.zone || 'front',
        locked: true,
        source: createTextCanvas(textData),
        uvCenter: Array.isArray(serialized.uvCenter) ? [Number(serialized.uvCenter[0]), Number(serialized.uvCenter[1])] : [0.5, 0.5],
        uvSize: Array.isArray(serialized.uvSize) ? [Number(serialized.uvSize[0]), Number(serialized.uvSize[1])] : [1, 1],
        uvRotation: Number(serialized.uvRotation) || 0,
        order: Number.isFinite(Number(serialized.order)) ? Number(serialized.order) : index + 1,
        transformMode: 'translate',
        data: textData as any,
      };
    }

    const sourceInfo = serialized.source;
    if (!sourceInfo?.value) return null;
    const resolvedUrl =
      sourceInfo.kind === 'assetRef'
        ? findCatalogItemBySourceValue(sourceInfo.value, sourceInfo.collection)?.url || ''
        : sourceInfo.value;
    if (!resolvedUrl) return null;
    const image = await loadImageFromUrl(resolvedUrl);
    return {
      id: `best_prev_img_${index}_${Math.random().toString(36).slice(2, 6)}`,
      type: 'image',
      zone: serialized.zone || 'front',
      locked: true,
      source: image,
      uvCenter: Array.isArray(serialized.uvCenter) ? [Number(serialized.uvCenter[0]), Number(serialized.uvCenter[1])] : [0.5, 0.5],
      uvSize: Array.isArray(serialized.uvSize) ? [Number(serialized.uvSize[0]), Number(serialized.uvSize[1])] : [1, 1],
      uvRotation: Number(serialized.uvRotation) || 0,
      order: Number.isFinite(Number(serialized.order)) ? Number(serialized.order) : index + 1,
      transformMode: 'translate',
      data: rawData,
    };
  };

  const waitForPreviewSceneReady = async (shouldCancel: () => boolean) => {
    let tries = 0;
    const maxTries = 20;
    while (!shouldCancel() && tries < maxTries) {
      const renderer = previewRendererRef.current;
      const scene = previewSceneRef.current;
      if (renderer && scene) {
        let ready = false;
        scene.traverse(obj => {
          if (ready || !(obj instanceof THREE.Mesh)) return;
          const material = obj.material;
          const materials = Array.isArray(material) ? material : [material];
          if (materials.some(item => (item as THREE.Material).userData?.designMixUniforms?.designMap)) {
            ready = true;
          }
        });
        if (ready) return true;
      }
      tries += 1;
      await new Promise<void>(resolve => window.setTimeout(resolve, 120));
    }
    return false;
  };

  const buildPreviewStorageKey = (sourceKey: string) => {
    const modelKey = activeModel?.sourcePath || activeModel?.url || 'default-model';
    return `${modelKey}|${sourceKey}`;
  };

  const buildBestDesignModelPreview = async (creation: CreationFileV1) => {
    const buildGradientTextureForPreview = (
      value: string,
      boundsList?: { uMin: number; uMax: number; vMin: number; vMax: number }[]
    ) => {
      const gradient = parseGradientValue(value);
      if (!gradient) return null;
      const combined = boundsList && boundsList.length
        ? boundsList.reduce(
            (acc, bounds) => ({
              uMin: Math.min(acc.uMin, bounds.uMin),
              vMin: Math.min(acc.vMin, bounds.vMin),
              uMax: Math.max(acc.uMax, bounds.uMax),
              vMax: Math.max(acc.vMax, bounds.vMax),
            }),
            { uMin: 1, vMin: 1, uMax: 0, vMax: 0 }
          )
        : { uMin: 0, vMin: 0, uMax: 1, vMax: 1 };
      const size = 256;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const radians = ((gradient.angle - 90) * Math.PI) / 180;
      const dx = Math.cos(radians);
      const dy = Math.sin(radians);
      const x0 = size / 2 - dx * (size / 2);
      const y0 = size / 2 - dy * (size / 2);
      const x1 = size / 2 + dx * (size / 2);
      const y1 = size / 2 + dy * (size / 2);
      const paint = ctx.createLinearGradient(x0, y0, x1, y1);
      const offset = Math.min(1, Math.max(-1, (gradient.stop - 50) / 50));
      const stopA = Math.min(1, Math.max(0, 0 + offset));
      const stopB = Math.min(1, Math.max(0, 1 + offset));
      paint.addColorStop(stopA, gradient.start);
      paint.addColorStop(stopB, gradient.end);
      ctx.fillStyle = paint;
      ctx.fillRect(0, 0, size, size);

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = 4;
      const width = Math.max(0.0001, combined.uMax - combined.uMin);
      const height = Math.max(0.0001, combined.vMax - combined.vMin);
      texture.repeat.set(1 / width, 1 / height);
      texture.offset.set(-combined.uMin / width, -combined.vMin / height);
      texture.needsUpdate = true;
      return texture;
    };

    const renderer = previewRendererRef.current;
    const scene = previewSceneRef.current;
    if (!renderer || !scene) return null;

    const designTargets: { material: THREE.Material; uniforms: { designMap: { value: THREE.Texture } } }[] = [];
    const materialColorBackup: Array<{ material: THREE.Material; color: THREE.Color; map: THREE.Texture | null }> = [];
    const tempGradientTextures: THREE.Texture[] = [];
    scene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return;
      const material = obj.material;
      const materialsList = Array.isArray(material) ? material : [material];
      materialsList.forEach((item, materialIndex) => {
        const mat = item as THREE.Material;
        const userData = mat.userData as any;
        if (userData?.designMixUniforms?.designMap) {
          designTargets.push({ material: mat, uniforms: userData.designMixUniforms });
        }
        const key = getMaterialKey(mat, obj.name || '', materialIndex);
        const nextColor = creation.state.materialColors?.[key];
        if (!nextColor) return;
        const standard = mat as THREE.MeshStandardMaterial;
        if (!standard.color) return;
        materialColorBackup.push({ material: mat, color: standard.color.clone(), map: standard.map || null });
        if (isGradientValue(nextColor)) {
          const gradientTexture = buildGradientTextureForPreview(nextColor, materialUvBounds[key]);
          if (gradientTexture) {
            tempGradientTextures.push(gradientTexture);
            standard.color.set('#ffffff');
            standard.map = gradientTexture;
            standard.needsUpdate = true;
          }
          return;
        }
        if (/^#[0-9a-fA-F]{6}$/.test(nextColor)) {
          standard.color.set(nextColor);
          const originalMap = ((mat.userData as any)?.originalMap as THREE.Texture | null | undefined);
          standard.map = originalMap === undefined ? standard.map : originalMap;
          standard.needsUpdate = true;
        }
      });
    });
    if (designTargets.length === 0) return null;

    const previewLayers = (
      await Promise.all(
        (Array.isArray(creation.state.layers) ? creation.state.layers : []).map((layer, index) =>
          deserializeLayerForBestPreview(layer, index)
        )
      )
    ).filter(Boolean) as Layer[];
    previewLayers.sort((a, b) => a.order - b.order);

    const textureSize = 512;
    const motifCanvas = document.createElement('canvas');
    motifCanvas.width = textureSize;
    motifCanvas.height = textureSize;
    const motifCtx = motifCanvas.getContext('2d');
    if (!motifCtx) return null;
    renderLayersToCanvas(motifCtx, previewLayers, textureSize, textureSize);

    const previewTexture = new THREE.CanvasTexture(motifCanvas);
    previewTexture.flipY = false;
    previewTexture.colorSpace = THREE.SRGBColorSpace;
    previewTexture.needsUpdate = true;

    const originalDesignMaps = designTargets.map(target => target.uniforms.designMap.value);
    const prevTarget = renderer.getRenderTarget();
    const prevClear = renderer.getClearColor(new THREE.Color());
    const prevClearAlpha = renderer.getClearAlpha();
    const prevToneExposure = renderer.toneMappingExposure;
    const size = 220;
    let renderTarget: THREE.WebGLRenderTarget | null = null;
    let output: string | null = null;

    try {
      renderTarget = new THREE.WebGLRenderTarget(size, size);
      renderer.setRenderTarget(renderTarget);
      renderer.setClearColor('#f0f0f0', 1);
      renderer.toneMappingExposure = 1.18;

      const bbox = new THREE.Box3();
      const tempBoxB = new THREE.Box3();
      scene.traverse(obj => {
        if (!(obj instanceof THREE.Mesh)) return;
        if (obj.name.endsWith('_overlay')) return;
        const geo = (obj as THREE.Mesh).geometry;
        if (!geo) return;
        if (!geo.boundingBox) geo.computeBoundingBox();
        if (!geo.boundingBox) return;
        tempBoxB.copy(geo.boundingBox).applyMatrix4(obj.matrixWorld);
        bbox.union(tempBoxB);
      });
      if (bbox.isEmpty()) bbox.setFromObject(scene);
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      const radius = bbox.getBoundingSphere(new THREE.Sphere()).radius || 1;
      const previewCamera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
      const fov = (previewCamera.fov * Math.PI) / 180;
      const distance = radius / Math.sin(fov / 2);
      const zoomDistance = distance * 0.9;
      const angle = (-20 * Math.PI) / 180;
      previewCamera.position.set(Math.sin(angle) * zoomDistance, distance * 0.05, Math.cos(angle) * zoomDistance);
      previewCamera.lookAt(0, 0, 0);
      previewCamera.near = Math.max(0.01, distance / 50);
      previewCamera.far = distance * 5;
      previewCamera.updateProjectionMatrix();

      // Warmup render: triggers onBeforeCompile for the preview renderer (first-time shader
      // compilation). This must happen BEFORE overriding designMap so that onBeforeCompile
      // does not reset our previewTexture back to the original canvas texture.
      renderer.render(scene, previewCamera);

      // onBeforeCompile replaces material.userData.designMixUniforms with a new object.
      // Refresh references so we write to the current shader's uniforms, not stale ones.
      designTargets.forEach(target => {
        const fresh = (target.material.userData as any).designMixUniforms;
        if (fresh) target.uniforms = fresh;
      });

      designTargets.forEach(target => {
        target.uniforms.designMap.value = previewTexture;
      });

      renderer.render(scene, previewCamera);
      const buffer = new Uint8Array(size * size * 4);
      renderer.readRenderTargetPixels(renderTarget, 0, 0, size, size, buffer);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const imageData = ctx.createImageData(size, size);
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          const srcIndex = (x + (size - y - 1) * size) * 4;
          const dstIndex = (x + y * size) * 4;
          imageData.data[dstIndex] = buffer[srcIndex];
          imageData.data[dstIndex + 1] = buffer[srcIndex + 1];
          imageData.data[dstIndex + 2] = buffer[srcIndex + 2];
          imageData.data[dstIndex + 3] = buffer[srcIndex + 3];
        }
      }
      ctx.putImageData(imageData, 0, 0);
      output = canvas.toDataURL('image/png');
    } catch (error) {
      console.warn('Best design preview generation failed:', error);
      output = null;
    } finally {
      designTargets.forEach((target, index) => {
        target.uniforms.designMap.value = originalDesignMaps[index];
        // needsUpdate=true ensures the GPU re-uploads the restored texture and
        // triggers onBeforeCompile on the next render. onBeforeCompile will reset
        // designMap to textureRef.current which is the same canvas texture we just
        // restored here, so there is no conflict.
        target.material.needsUpdate = true;
      });
      materialColorBackup.forEach(entry => {
        const standard = entry.material as THREE.MeshStandardMaterial;
        if (standard.color) {
          standard.color.copy(entry.color);
          standard.map = entry.map;
          entry.material.needsUpdate = true;
        }
      });
      tempGradientTextures.forEach(texture => texture.dispose());
      if (renderTarget) renderTarget.dispose();
      previewTexture.dispose();
      renderer.setRenderTarget(prevTarget);
      renderer.setClearColor(prevClear, prevClearAlpha);
      renderer.toneMappingExposure = prevToneExposure;
    }

    return output;
  };

  useEffect(() => {
    if (!SHOW_BEST_DESIGN_TAB && designSubTab !== 'designs') {
      setDesignSubTab('designs');
    }
  }, [designSubTab]);

  useEffect(() => {
    if (BEST_DESIGN_ITEMS.length === 0) {
      setBestDesignFiles({});
      return;
    }
    if (!SHOW_BEST_DESIGN_TAB) {
      setBestDesignFiles({});
      return;
    }
    let cancelled = false;

    const loadBestDesignFiles = async () => {
      const nextFiles: Record<string, CreationFileV1> = {};
      for (const item of BEST_DESIGN_ITEMS) {
        try {
          const response = await fetch(item.url);
          if (!response.ok) continue;
          const raw = await response.json();
          const parsed = parseCreationFile(raw);
          if (!parsed) continue;
          nextFiles[item.key] = parsed;
        } catch (error) {
          console.warn('Best design load failed:', item.url, error);
        }
      }
      if (cancelled) return;
      setBestDesignFiles(nextFiles);
    };

    void loadBestDesignFiles();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!SHOW_BEST_DESIGN_TAB) return;
    if (appState.isModelLoading) return;
    if (pausePreviewGeneration) return;
    if (Object.keys(bestDesignFiles).length === 0) return;
    let cancelled = false;

    const loadBestDesignPreviews = async () => {
      setIsBestPreviewRendering(true);
      bestPreviewBusyRef.current = true;
      try {
        const ready = await waitForPreviewSceneReady(() => cancelled);
        if (!ready || cancelled) {
          return;
        }
        await new Promise<void>(resolve =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
        );
        if (cancelled) {
          return;
        }

        for (const item of BEST_DESIGN_ITEMS) {
          if (cancelled) return;
          if (bestDesignPreviewUrls[item.key]) continue;

          const storageKey = buildPreviewStorageKey(item.key);
          const hasStored = await hasStoredPreview('best_design', storageKey);
          if (cancelled) return;
          if (hasStored) {
            setBestDesignPreviewUrls(prev => ({
              ...prev,
              [item.key]: buildPreviewUrl('best_design', storageKey),
            }));
            continue;
          }

          const parsed = bestDesignFiles[item.key];
          if (!parsed) continue;
          previewRenderJobsRef.current += 1;
          let preview: string | null = null;
          try {
            preview = await buildBestDesignModelPreview(parsed);
          } finally {
            previewRenderJobsRef.current = Math.max(0, previewRenderJobsRef.current - 1);
          }
          if (cancelled || !preview) continue;
          setBestDesignPreviewUrls(prev => ({ ...prev, [item.key]: preview }));
          const saved = await saveStoredPreview('best_design', storageKey, preview);
          if (cancelled) return;
          if (saved) {
            setBestDesignPreviewUrls(prev => ({
              ...prev,
              [item.key]: buildPreviewUrl('best_design', storageKey),
            }));
          }
        }
      } finally {
        bestPreviewBusyRef.current = false;
        setIsBestPreviewRendering(false);
      }
    };

    void loadBestDesignPreviews();
    return () => {
      cancelled = true;
      bestPreviewBusyRef.current = false;
      setIsBestPreviewRendering(false);
    };
  }, [appState.isModelLoading, bestDesignFiles, bestDesignPreviewUrls, activeModel?.url, pausePreviewGeneration]);

  const buildCreationFile = async (): Promise<CreationFileV1> => {
    const serializedLayers = await Promise.all(
      [...layersManager.layers]
        .sort((a, b) => a.order - b.order)
        .map(layer => serializeLayer(layer))
    );
    const layers = serializedLayers.filter(Boolean) as SerializedLayer[];
    return {
      format: 'indyanna.creation',
      version: 1,
      createdAt: new Date().toISOString(),
      model: activeModel
        ? {
            url: activeModel.url,
            fileName: activeModel.fileName,
            sourcePath: activeModel.sourcePath,
          }
        : undefined,
      state: {
        materialColors: { ...materialColors },
        patternMotifColors: { ...patternMotifColors },
        motifMaterialSelections: { ...motifMaterialSelections },
        layers,
      },
    };
  };

  const applyParsedCreation = async (parsed: CreationFileV1, options?: { skipModelSwitch?: boolean }) => {
    const issues: string[] = [];

    if (!options?.skipModelSwitch && parsed.model) {
      const modelUrl = parsed.model.url;
      const modelSourcePath = parsed.model.sourcePath;
      const modelFileName = parsed.model.fileName;
      const isCurrentModel =
        (modelUrl && modelUrl === activeModel?.url) ||
        (modelSourcePath && modelSourcePath === activeModel?.sourcePath) ||
        (modelFileName && modelFileName === activeModel?.fileName);
      if (isCurrentModel) {
        // Nothing to do.
      } else {
        const targetModel =
          (modelSourcePath && MODEL_ITEMS.find(item => item.sourcePath === modelSourcePath)) ||
          (modelFileName && MODEL_ITEMS.find(item => item.fileName === modelFileName)) ||
          (modelUrl && MODEL_ITEMS.find(item => item.url === modelUrl));
        if (targetModel) {
          setActiveModel(targetModel);
        } else {
          const modelRef = modelSourcePath || modelFileName || modelUrl || 'inconnu';
          issues.push(`Modele introuvable dans ce projet: ${modelRef}`);
        }
      }
    }

    layersManager.clearAllLayers();
    layersManager.selectLayer(null);
    setActiveColorPicker(null);
    setActiveMotifModal(null);
    motifLayerIdsRef.current = {};
    patternLayerIdsRef.current = {};
    motifOrderRef.current = 0;
    setSelectedMotifTarget(null);

    if (parsed.state.materialColors && typeof parsed.state.materialColors === 'object') {
      skipNextColorHistoryRef.current = true;
      setMaterialColors({ ...parsed.state.materialColors });
    }
    if (parsed.state.patternMotifColors) {
      setPatternMotifColors({
        motif1: parsed.state.patternMotifColors.motif1 || '#000000',
        motif2: parsed.state.patternMotifColors.motif2 || '#ffffff',
      });
    }

    const sourceLayers = Array.isArray(parsed.state.layers) ? parsed.state.layers : [];
    const createdLayers: Layer[] = [];

    for (let i = 0; i < sourceLayers.length; i += 1) {
      const rawLayer = sourceLayers[i] as SerializedLayer;
      if (!rawLayer || (rawLayer.type !== 'image' && rawLayer.type !== 'text')) {
        issues.push(`Layer ${i + 1}: type invalide, ignore.`);
        continue;
      }
      const rawData = rawLayer.data ? { ...rawLayer.data } : {};
      if ((rawData as any).isSystem) {
        continue;
      }

      try {
        let source: CanvasImageSource | null = null;
        if (rawLayer.type === 'text') {
          const textData = {
            text: rawData.text || 'Texte',
            fontFamily: rawData.fontFamily || 'Arial',
            fontSize: Number(rawData.fontSize) || 200,
            color: rawData.color || '#000000',
            strokeColor: rawData.strokeColor || '#000000',
            strokeWidth: Number(rawData.strokeWidth) || 0,
            kind: rawData.kind,
            canvasResolution: Number(rawData.canvasResolution) || 1024,
          };
          source = createTextCanvas(textData);
          Object.assign(rawData, textData);
        } else {
          const sourceKind = rawLayer.source?.kind;
          const sourceValue = rawLayer.source?.value;
          if (!sourceValue) {
            issues.push(`Layer image ${i + 1}: source manquante, ignore.`);
            continue;
          }
          if (sourceKind === 'assetRef') {
            const collection = rawLayer.source?.collection;
            const found = findCatalogItemBySourceValue(sourceValue, collection);
            if (!found?.url) {
              issues.push(`Layer image ${i + 1}: asset introuvable (${sourceValue}), ignore.`);
              continue;
            }
            source = await loadImageFromUrl(found.url);
            rawData.motifSourceKey = rawData.motifSourceKey || (found.sourcePath || found.name || found.url);
          } else {
            source = await loadImageFromUrl(sourceValue);
          }
        }

        const added = layersManager.addLayer({
          type: rawLayer.type,
          zone: rawLayer.zone || 'front',
          locked: Boolean(rawLayer.locked),
          source,
          uvCenter: Array.isArray(rawLayer.uvCenter) ? [Number(rawLayer.uvCenter[0]), Number(rawLayer.uvCenter[1])] : [0.5, 0.5],
          uvSize: Array.isArray(rawLayer.uvSize) ? [Number(rawLayer.uvSize[0]), Number(rawLayer.uvSize[1])] : [1, 1],
          uvRotation: Number(rawLayer.uvRotation) || 0,
          transformMode: rawLayer.transformMode || 'translate',
          data: rawData,
        });
        const nextOrder = Number(rawLayer.order);
        layersManager.updateLayer(added.id, { order: Number.isFinite(nextOrder) ? nextOrder : i + 1 });
        const created = {
          ...added,
          order: Number.isFinite(nextOrder) ? nextOrder : i + 1,
          data: rawData,
        };
        createdLayers.push(created);
      } catch (layerError) {
        issues.push(`Layer ${i + 1}: ${layerError instanceof Error ? layerError.message : 'erreur de chargement'}`);
      }
    }

    if (Object.keys(parsed.state.motifMaterialSelections || {}).length > 0) {
      setMotifMaterialSelections({ ...parsed.state.motifMaterialSelections });
    } else {
      const reconstructed: Record<string, Record<string, boolean>> = {};
      createdLayers.forEach(layer => {
        if (!layer.data?.isMotif) return;
        if ((layer.data as any)?.isPatternMotif) return;
        const motifId = ((layer.data as any)?.motifId || layer.id) as string;
        const selectedIds = ((layer.data as any)?.selectedMaterialIds as string[] | undefined) || [];
        const selectionByMaterial: Record<string, boolean> = {};
        materials.forEach(material => {
          selectionByMaterial[material.id] = selectedIds.length ? selectedIds.includes(material.id) : true;
        });
        reconstructed[motifId] = selectionByMaterial;
      });
      setMotifMaterialSelections(reconstructed);
    }

    rebuildRuntimeLayerRefs(createdLayers);
    requestPreviewRefresh();
    return issues;
  };

  const handleShareCreation = () => {
    setShareForm({
      visible: true,
      email: shareForm.email,
      consentAssist: shareForm.consentAssist,
      error: null,
    });
  };

  const handleShareFormSubmit = async () => {
    const email = shareForm.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setShareForm(prev => ({ ...prev, error: 'Adresse email invalide.' }));
      return;
    }
    if (!shareForm.consentAssist) {
      setShareForm(prev => ({
        ...prev,
        error: "Merci d'accepter l'autorisation de contact pour continuer.",
      }));
      return;
    }

    setShareForm(prev => ({ ...prev, visible: false, error: null }));
    try {
      setCreationFeedback({ visible: true, mode: 'saving', message: 'Sauvegarde en cours...' });
      const file = await buildCreationFile();
      const MAX_CREATION_BYTES = 10 * 1024 * 1024;
      if (new Blob([JSON.stringify(file)]).size > MAX_CREATION_BYTES) {
        setCreationFeedback({
          visible: true,
          mode: 'saving',
          message: 'Le projet dépasse la limite de 10 Mo. Réduisez le nombre d\'images ou leur résolution avant de sauvegarder.',
        });
        window.setTimeout(() => setCreationFeedback({ visible: false, mode: null, message: '' }), 5000);
        return;
      }
      const fetchPromise = fetch(toApiUrl('/api_indyannastudio/creations'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creation: file,
          recipientEmail: email,
          consentAssist: shareForm.consentAssist,
          baseUrl: window.location.origin,
        }),
      });
      const [response] = await Promise.all([fetchPromise, wait(2500)]);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Sauvegarde serveur impossible');
      }
      const payload = await response.json();
      const viewerUrl = String(payload.viewerUrl || '');
      const editorUrl = String(payload.editorUrl || '');
      const recipientMailSent = Boolean(payload.recipientMailSent);

      setCreationFeedback({
        visible: true,
        mode: 'saved',
        message: recipientMailSent
          ? `Vos liens ont ete envoyes a ${email}.`
          : `Creation sauvegardee. Envoi email a ${email} non confirme.`,
      });
    } catch (error) {
      console.error('Creation export error:', error);
      setCreationFeedback({
        visible: true,
        mode: 'saved',
        message: `Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      });
    }
  };

  const handleImportCreation = async (file: File) => {
    const issues: string[] = [];
    try {
      const text = await file.text();
      let parsedRaw: any;
      try {
        parsedRaw = JSON.parse(text);
      } catch {
        throw new Error('Le fichier .ist est invalide (JSON).');
      }

      const parsed = parseCreationFile(parsedRaw);
      if (!parsed) {
        throw new Error('Format .ist non reconnu.');
      }
      setSessionAccess('editor');
      issues.push(...(await applyParsedCreation(parsed)));
    } catch (error) {
      console.error('Creation import error:', error);
      issues.push(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      await playLoadedFeedback();
      setCreationImportIssues(issues.length > 0 ? issues : null);
    }
  };

  const handleOpenCreationFromLink = async (creationId: string, token: string) => {
    const issues: string[] = [];
    try {
      const response = await fetch(toApiUrl(`/api_indyannastudio/creations/${encodeURIComponent(creationId)}?token=${encodeURIComponent(token)}`));
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Lien de creation invalide');
      }
      const payload = await response.json();
      const access = (payload?.access as 'viewer' | 'editor') || 'viewer';
      const parsed = parseCreationFile(payload?.creation);
      if (!parsed) {
        throw new Error('Creation distante invalide');
      }
      setSessionAccess(access);
      issues.push(...(await applyParsedCreation(parsed)));
    } catch (error) {
      console.error('Remote creation open error:', error);
      issues.push(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      await playLoadedFeedback();
      setCreationImportIssues(issues.length > 0 ? issues : null);
    }
  };

  const handleApplyBestDesign = async (itemKey: string) => {
    const parsed = bestDesignFiles[itemKey];
    if (!parsed) return;
    const issues: string[] = [];
    try {
      setSessionAccess('editor');
      issues.push(...(await applyParsedCreation(parsed)));
    } catch (error) {
      issues.push(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setCreationImportIssues(issues.length > 0 ? issues : null);
    }
  };

  useEffect(() => {
    if (linkAutoLoadRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const creationId = params.get('creation');
    const token = params.get('token');
    if (!creationId || !token) return;
    linkAutoLoadRef.current = true;
    void handleOpenCreationFromLink(creationId, token);
  }, []);

  const handleExport = async (qty?: number) => {
    if (layersManager.layers.length === 0) {
      alert('Ajoutez au moins un élément avant de terminer.');
      return;
    }

    setPausePreviewGeneration(true);

    try {
      setAppState(prev => ({ ...prev, isExporting: true }));
      const waitStart = Date.now();
      while (
        (previewBusyRef.current || bestPreviewBusyRef.current || previewRenderJobsRef.current > 0) &&
        Date.now() - waitStart < 3500
      ) {
        await new Promise<void>(resolve => window.setTimeout(resolve, 50));
      }
      await new Promise<void>(resolve =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );

      const renderer = previewRendererRef.current;
      const scene = previewSceneRef.current;
      if (!renderer || !scene) {
        throw new Error('Renderer non disponible');
      }

      const waitFrames = async (count: number) => {
        for (let i = 0; i < count; i += 1) {
          await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
        }
      };

      // Force a rebind of design/motif texture BEFORE capture so export previews are accurate.
      setModelPreviewRefreshKey(prev => prev + 1);
      await waitFrames(3);

      const prevSelected = layersManager.selectedLayer?.id || null;
      if (prevSelected) {
        layersManager.selectLayer(null);
        // Selecting/deselecting can trigger a canvas redraw; wait for it to land before capture.
        await waitFrames(2);
      }
      // Extra frame to ensure texture is up-to-date before capture
      await waitFrames(2);

      const views = [
        { label: 'front', angle: 0 },
        { label: 'right', angle: Math.PI / 2 },
        { label: 'back', angle: Math.PI },
        { label: 'left', angle: -Math.PI / 2 },
      ];

      type CaptureUniforms = {
        designMap: { value: THREE.Texture };
        printStrength?: { value: number };
      };

      const designTargets: Array<{ material: THREE.Material; uniforms: CaptureUniforms }> = [];
      scene.traverse(obj => {
        if (!(obj instanceof THREE.Mesh)) return;
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        materials.forEach(material => {
          const uniforms = (material.userData as any)?.designMixUniforms as CaptureUniforms | undefined;
          if (uniforms?.designMap) {
            designTargets.push({ material, uniforms });
          }
        });
      });

      const originalDesignMaps = designTargets.map(target => target.uniforms.designMap.value);
      let exportCaptureTexture: THREE.CanvasTexture | null = null;
      let images: string[] = [];
      try {
        if (designTargets.length > 0) {
          const captureCanvas = document.createElement('canvas');
          captureCanvas.width = 2048;
          captureCanvas.height = 2048;
          const captureCtx = captureCanvas.getContext('2d');
          if (captureCtx) {
            renderLayersToCanvas(
              captureCtx,
              layersManager.layers,
              2048,
              2048,
              undefined,
              zoneUvBounds as Record<TShirtZone, { uMin: number; uMax: number; vMin: number; vMax: number }>,
              secondaryColor,
              materialUvBounds
            );
            exportCaptureTexture = new THREE.CanvasTexture(captureCanvas);
            exportCaptureTexture.flipY = false;
            exportCaptureTexture.colorSpace = THREE.SRGBColorSpace;
            exportCaptureTexture.wrapS = THREE.ClampToEdgeWrapping;
            exportCaptureTexture.wrapT = THREE.ClampToEdgeWrapping;
            exportCaptureTexture.minFilter = THREE.LinearFilter;
            exportCaptureTexture.magFilter = THREE.LinearFilter;
            exportCaptureTexture.generateMipmaps = false;
            exportCaptureTexture.anisotropy = 4;
            exportCaptureTexture.premultiplyAlpha = false;
            exportCaptureTexture.needsUpdate = true;

            designTargets.forEach(target => {
              const fresh = (target.material.userData as any)?.designMixUniforms as CaptureUniforms | undefined;
              if (fresh?.designMap) {
                target.uniforms = fresh;
              }
            });
            designTargets.forEach(target => {
              target.uniforms.designMap.value = exportCaptureTexture as THREE.Texture;
              if (target.uniforms.printStrength) {
                target.uniforms.printStrength.value = 1;
              }
            });
            await waitFrames(1);
          }
        }

        images = views
          .map(view => captureViewImage(renderer, scene, view.angle, 900, 0.85, exportCaptureTexture || undefined))
          .filter(Boolean) as string[];
      } finally {
        designTargets.forEach((target, index) => {
          const fresh = (target.material.userData as any)?.designMixUniforms as CaptureUniforms | undefined;
          const activeUniforms = fresh?.designMap ? fresh : target.uniforms;
          const fallback = originalDesignMaps[index];
          if (fallback) {
            activeUniforms.designMap.value = fallback;
          }
          if (activeUniforms.printStrength) {
            activeUniforms.printStrength.value = 1;
          }
        });
        if (exportCaptureTexture) {
          exportCaptureTexture.dispose();
        }
      }

      if (images.length !== views.length) {
        throw new Error('Capture des vues incomplète');
      }

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const size = Math.min(pageWidth - margin * 2, pageHeight - margin * 2 - 16);

      const logo = new Image();
      logo.src = '/assets/IndyannaStudio_Logo.png';
      if (!logo.complete || logo.naturalWidth === 0) {
        await new Promise<void>((resolve) => {
          logo.onload = () => resolve();
          logo.onerror = () => resolve();
        });
      }

      const timestamp = new Date();
      const timestampText = `Généré le ${timestamp.toLocaleDateString('fr-FR')} à ${timestamp.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })}`;

      images.forEach((dataUrl, index) => {
        if (index > 0) pdf.addPage();
        if (logo.complete && logo.naturalWidth > 0) {
          const logoWidth = 44;
          const logoHeight = (logo.naturalHeight / logo.naturalWidth) * logoWidth;
          pdf.addImage(logo, 'PNG', margin, 6, logoWidth, logoHeight);
        }
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(80);
        pdf.text(timestampText, margin + 50, 12);
        const x = (pageWidth - size) / 2;
        const y = margin + 16;
        pdf.addImage(dataUrl, 'JPEG', x, y, size, size);
      });

      const pdfBlob = pdf.output('blob');
      // Revoke previous object URL if any
      if (exportPdfUrlRef.current) {
        URL.revokeObjectURL(exportPdfUrlRef.current);
      }
      const pdfUrl = URL.createObjectURL(pdfBlob);
      exportPdfUrlRef.current = pdfUrl;

      if (prevSelected) {
        layersManager.selectLayer(prevSelected);
      }

      const initialMessage = qty != null && qty > 0 ? `Quantité souhaitée : ${qty}` : '';
      setExportModal({ visible: true, pdfUrl, captures: images, initialMessage });
    } catch (error) {
      console.error('Export error:', error);
      alert(`Erreur lors de la génération : ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setAppState(prev => ({ ...prev, isExporting: false }));
      setModelPreviewRefreshKey(prev => prev + 1);
      setPausePreviewGeneration(false);
    }
  };

  const handleContactSubmit = async (form: { name: string; email: string; phone: string; message: string }) => {
    const creation = await buildCreationFile();
    const modelName = activeModel?.fileName || activeModel?.sourcePath || 'Produit';
    const runtimeAssetRefDataUrls: Record<string, string> = {};

    await Promise.all(
      layersManager.layers.map(async layer => {
        if (layer.type !== 'image' || !layer.data?.isMotif) return;
        const data = layer.data as any;
        const sourceKey = String(data?.motifSourceKey || data?.fileName || '').trim();
        if (!sourceKey) return;
        const collection = (data?.isPatternMotif ? 'motif' : 'design') as 'design' | 'motif';
        const dataUrl = await sourceToDataUrl(layer.source);
        if (!dataUrl) return;
        runtimeAssetRefDataUrls[`${collection}::${sourceKey}`] = dataUrl;
        runtimeAssetRefDataUrls[`any::${sourceKey}`] = dataUrl;
      })
    );

    const { zipBase64, recapText } = await buildContactZip({
      creation,
      captures: exportModal.captures,
      materials,
      modelName,
      resolveAssetRef: (sourceValue, collection) => {
        const key = `${collection || 'any'}::${sourceValue}`;
        const fallbackKey = `any::${sourceValue}`;
        const isDataUrl = /^data:/i.test(sourceValue);
        if (isDataUrl) {
          return { name: sourceValue, url: sourceValue };
        }
        const localDataUrl = runtimeAssetRefDataUrls[key] || runtimeAssetRefDataUrls[fallbackKey];
        const strictCatalogItem =
          findCatalogItemBySourceValueStrict(sourceValue, collection) ||
          findCatalogItemBySourceValueStrict(sourceValue);
        if (strictCatalogItem?.url) {
          return { name: strictCatalogItem.name || sourceValue, url: strictCatalogItem.url };
        }
        if (localDataUrl) {
          return { name: sourceValue, dataUrl: localDataUrl };
        }
        const catalogItem =
          findCatalogItemBySourceValue(sourceValue, collection) ||
          findCatalogItemBySourceValue(sourceValue);
        if (catalogItem?.url) {
          return { name: catalogItem.name || sourceValue, url: catalogItem.url };
        }
        if (/^(https?:\/\/|\/|data:)/i.test(sourceValue)) {
          return { name: sourceValue, url: sourceValue };
        }
        return null;
      },
    });

    const response = await fetch(toApiUrl('/api_indyannastudio/contact-request'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        creation,
        recap: recapText,
        zipBase64,
        baseUrl: window.location.origin,
      }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error || 'Envoi impossible');
    }
  };

  const handleExportModalClose = () => {
    setExportModal({ visible: false, pdfUrl: null, captures: [], initialMessage: '' });
  };

  const handleLayerTransform = (layerId: string, transform: any) => {
    const next = { ...transform };
    if (typeof next.uvRotation === 'number') {
      next.uvRotation = normalizeRotation(next.uvRotation);
    }
    const layer = layersManager.layers.find(item => item.id === layerId);
    if (layer?.type === 'text' && next.uvSize) {
      // Zone resize: only update uvSize — source canvas and data are unchanged
      layersManager.updateLayer(layerId, next);
      return;
    }
    layersManager.updateLayer(layerId, next);
  };

  const handleMaterialsReady = useCallback((nextMaterials: MaterialDescriptor[]) => {
    const signature = nextMaterials
      .map(material => `${material.id}:${material.group}:${material.element}:${material.name}:${material.defaultColor}`)
      .sort()
      .join('|');
    if (signature && signature === materialsSignatureRef.current) {
      return;
    }
    materialsSignatureRef.current = signature;
    setMaterials(nextMaterials);
    skipNextColorHistoryRef.current = true;
    setMaterialColors(prev => buildMaterialColors(nextMaterials, prev));
  }, []);

  const handleUvBoundsReady = (
    materialBounds: Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]>,
    zoneBounds: Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }>,
    materialTriangles?: Record<string, number[][]>
  ) => {
    setMaterialUvBounds(materialBounds);
    setZoneUvBounds(zoneBounds);
    setMaterialUvTriangles(materialTriangles || {});
  };

  const groupedMaterials = materials.reduce<Record<string, MaterialDescriptor[]>>((acc, material) => {
    if (!acc[material.group]) {
      acc[material.group] = [];
    }
    acc[material.group].push(material);
    return acc;
  }, {});

  const getMaterialColor = (material: MaterialDescriptor) =>
    materialColors[material.id] || material.defaultColor || '#ffffff';

  const getMaterialColorById = (materialId: string) => {
    const material = materials.find(item => item.id === materialId);
    return materialColors[materialId] || material?.defaultColor || '#ffffff';
  };

  const requestPreviewRefresh = () => {
    // Design previews are persisted on the API and should not be regenerated on style edits.
  };

  const buildSelectionsFromIds = (selectedIds?: string[]) => {
    const map: Record<string, boolean> = {};
    const selectedSet = selectedIds ? new Set(selectedIds) : null;
    materials.forEach(material => {
      map[material.id] = selectedSet ? selectedSet.has(material.id) : true;
    });
    return map;
  };

  const cacheDesignStateFromLayer = (layer: Layer) => {
    if (!layer.data?.isMotif) return;
    if ((layer.data as any)?.isPatternMotif) return;
    const sourceKey = (layer.data as any)?.motifSourceKey || (layer.data as any)?.fileName;
    if (!sourceKey) return;
    const motifId = (layer.data as any)?.motifId || layer.id;
    const selectedMaterialIds =
      ((layer.data as any)?.selectedMaterialIds as string[] | undefined) || undefined;
    const selections = motifMaterialSelections[motifId] || buildSelectionsFromIds(selectedMaterialIds);
    const motifMaterialColors =
      ((layer.data as any)?.motifMaterialColors as Record<string, string> | undefined) || {};
    const motifDesignColors =
      ((layer.data as any)?.motifDesignColors as { design1?: string; design2?: string } | undefined) || {};

    designStateCacheRef.current[sourceKey] = {
      motifMaterialColors: { ...motifMaterialColors },
      motifDesignColors: {
        design1: motifDesignColors.design1 || '#ef7218',
        design2: motifDesignColors.design2 || '#ffffff',
      },
      selectedMaterialIds: Object.entries(selections)
        .filter(([, value]) => value)
        .map(([id]) => id),
      materialSelections: { ...selections },
    };
  };

  const cacheDesignStateForMotif = (motifId: string) => {
    const targetLayer = getPrimaryMotifLayer(motifId);
    if (!targetLayer) return;
    cacheDesignStateFromLayer(targetLayer);
  };

  const cacheCurrentDesignState = () => {
    const baseLayer = layersManager.layers.find(
      layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif
    );
    if (baseLayer) {
      cacheDesignStateFromLayer(baseLayer);
    }
  };


  const getGroupColorInfo = (groupMaterials: MaterialDescriptor[]) => {
    if (!groupMaterials.length) {
      return { color: '#ffffff', isMixed: false };
    }
    const colors = groupMaterials.map(material => getMaterialColor(material));
    const uniqueColors = new Set(colors.map(color => color.toLowerCase()));
    return { color: colors[0], isMixed: uniqueColors.size > 1 };
  };

  const toggleColorGroup = (groupName: string) => {
    setExpandedColorGroups(prev =>
      prev[groupName] ? {} : { [groupName]: true }
    );
  };

  const getMotifGroupColorInfo = (motifId: string, groupMaterials: MaterialDescriptor[]) => {
    if (!groupMaterials.length) {
      return { color: '#ffffff', isMixed: false };
    }
    const colors = groupMaterials.map(material => getMotifMaterialColor(motifId, material.id));
    const uniqueColors = new Set(colors.map(color => color.toLowerCase()));
    return { color: colors[0], isMixed: uniqueColors.size > 1 };
  };

  const toggleMotifGroup = (groupName: string) => {
    setExpandedMotifGroups(prev =>
      prev[groupName] ? {} : { [groupName]: true }
    );
  };

  useEffect(() => {
    const placement = getZonePlacement('front');
    if (!placement) return;
    const motifLayers = layersManager.layers.filter(
      layer => layer.data?.isMotif && (layer.data as any)?.needsInitialPlacement
    );
    if (motifLayers.length === 0) return;
    motifLayers.forEach(layer => {
      layersManager.updateLayer(layer.id, {
        uvCenter: placement.uvCenter,
        uvSize: placement.uvSize,
        data: { ...(layer.data as any), needsInitialPlacement: false },
      });
    });
  }, [zoneUvBounds, layersManager.layers]);

  useEffect(() => {
    if (activeTab !== 'motif') return;
    if (selectedMotifTarget) return;
    if (materials.length === 0) return;
    setSelectedMotifTarget(materials[0].id);
  }, [activeTab, materials, selectedMotifTarget]);

  useEffect(() => {
    if (activeTab !== 'motif') return;
    setMotifPanelMode('list');
  }, [activeTab]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (sessionAccess === 'viewer') return;
      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || (target as HTMLElement).isContentEditable) {
          return;
        }
      }
      const isMod = event.ctrlKey || event.metaKey;
      if (!isMod) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        layersManager.undo();
      } else if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault();
        layersManager.redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [layersManager, sessionAccess]);

  useEffect(() => {
    if (!selectedMotifTarget) return;
    if (selectedMotifTarget === 'design1' || selectedMotifTarget === 'design2') return;
    if (materials.some(material => material.id === selectedMotifTarget)) return;
    setSelectedMotifTarget(materials[0]?.id || null);
  }, [materials, selectedMotifTarget]);

  useEffect(() => {
    if (activeTab === 'colors' || activeTab === 'gradient') return;
    if (!isColorCopyMode && !colorCopySource) return;
    setIsColorCopyMode(false);
    setColorCopySource(null);
  }, [activeTab, isColorCopyMode, colorCopySource]);

  useEffect(() => {
    if (!activeColorPicker) return;
    if (!isColorCopyMode && !colorCopySource) return;
    setIsColorCopyMode(false);
    setColorCopySource(null);
  }, [activeColorPicker, isColorCopyMode, colorCopySource]);

  const getMotifAllColorInfo = (motifId: string) => {
    if (!materials.length) {
      return { color: '#ffffff', isMixed: false };
    }
    const colors = materials.map(material => getMotifMaterialColor(motifId, material.id));
    const uniqueColors = new Set(colors.map(color => color.toLowerCase()));
    return { color: colors[0], isMixed: uniqueColors.size > 1 };
  };

  const handleMaterialColorChange = (materialId: string, color: string) => {
    setMaterialColors(prev => {
      const next = { ...prev, [materialId]: color };
      layersManager.pushColorHistory(next);
      return next;
    });
  };

  const startColorCopyMode = () => {
    setActiveColorPicker(null);
    setIsColorCopyMode(true);
    setColorCopySource(null);
  };

  const cancelColorCopyMode = () => {
    setIsColorCopyMode(false);
    setColorCopySource(null);
  };

  const handleCopyableColorRowClick = (entry: CopyableColorEntry) => {
    if (!isColorCopyMode) {
      entry.onPick();
      return;
    }
    if (!colorCopySource) {
      setColorCopySource({ key: entry.key, label: entry.label, color: entry.color });
      return;
    }
    if (colorCopySource.key === entry.key) {
      setColorCopySource(null);
      return;
    }
    entry.onChange(colorCopySource.color);
  };

  const handleGroupColorChange = (groupName: string, color: string) => {
    setMaterialColors(prev => {
      const next = { ...prev };
      materials.forEach(material => {
        if (material.group === groupName) {
          next[material.id] = color;
        }
      });
      layersManager.pushColorHistory(next);
      return next;
    });
  };

  useEffect(() => {
    if (materials.length === 0) return;
    const existingSystemLayer = layersManager.layers.find(layer => (layer.data as any)?.isSystem);
    if (existingSystemLayer) {
      systemLayerIdRef.current = existingSystemLayer.id;
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const systemLayer = layersManager.addLayer({
      type: 'image',
      zone: 'front',
      locked: true,
      source: canvas,
      uvCenter: [0.5, 0.5],
      uvSize: [0.01, 0.01],
      uvRotation: 0,
      transformMode: 'translate',
      data: {
        fileName: '__system__',
        originalSize: { width: 1, height: 1 },
        isSystem: true,
        materialColorsSnapshot: { ...materialColors },
      }
    });
    systemLayerIdRef.current = systemLayer.id;
  }, [materials.length]);

  useEffect(() => {
    // During undo/redo, materialColors is already restored by onRestoreMaterialColors —
    // don't let the system-layer snapshot overwrite it.
    if (layersManager.historyRestorationRef.current) {
      layersManager.historyRestorationRef.current = false;
      return;
    }
    if (isSyncingMaterialColorsRef.current) return;
    if (!systemLayerIdRef.current) return;
    const systemLayer = layersManager.layers.find(layer => layer.id === systemLayerIdRef.current);
    if (!systemLayer) return;
    const snapshot = (systemLayer.data as any)?.materialColorsSnapshot as Record<string, string> | undefined;
    if (!snapshot) return;
    const keys = Object.keys(snapshot);
    const currentKeys = Object.keys(materialColors);
    if (keys.length === currentKeys.length && keys.every(key => materialColors[key] === snapshot[key])) {
      return;
    }
    isSyncingMaterialColorsRef.current = true;
    setMaterialColors({ ...snapshot });
    isSyncingMaterialColorsRef.current = false;
  }, [layersManager.layers]);

  useEffect(() => {
    if (isSyncingMaterialColorsRef.current) return;
    // Skip history for programmatic/initialization color changes
    if (skipNextColorHistoryRef.current) {
      skipNextColorHistoryRef.current = false;
      return;
    }
    if (!systemLayerIdRef.current) return;
    const systemLayerId = systemLayerIdRef.current;
    const systemLayer = layersManager.layers.find(layer => layer.id === systemLayerId);
    if (!systemLayer) return;
    const current = (systemLayer.data as any)?.materialColorsSnapshot as Record<string, string> | undefined;
    const nextSnapshot = { ...materialColors };
    const currentKeys = current ? Object.keys(current) : [];
    const nextKeys = Object.keys(nextSnapshot);
    const same =
      current &&
      currentKeys.length === nextKeys.length &&
      nextKeys.every(key => current[key] === nextSnapshot[key]);
    if (same) return;
    layersManager.updateLayer(systemLayerId, {
      data: { ...(systemLayer.data as any), materialColorsSnapshot: nextSnapshot },
    });
  }, [materialColors]);

  useEffect(() => {
    requestPreviewRefresh();
  }, [materialColors, patternMotifColors]);

  useEffect(() => {
    const patternLayers = layersManager.layers.filter(
      layer => layer.data?.isMotif && (layer.data as any)?.isPatternMotif
    );
    if (patternLayers.length === 0) return;
    patternLayers.forEach(layer => {
      const materialId = (layer.data as any)?.patternMaterialId as string | undefined;
      const currentPatternColors =
        ((layer.data as any)?.motifPatternColors as { motif1?: string; motif2?: string } | undefined) || {};
      const needsPatternUpdate =
        currentPatternColors.motif1 !== patternMotifColors.motif1 ||
        currentPatternColors.motif2 !== patternMotifColors.motif2;
      if (needsPatternUpdate) {
        layersManager.updateLayer(layer.id, {
          data: {
            ...(layer.data as any),
            motifPatternColors: { ...patternMotifColors },
          },
        });
      }

      if (!materialId) return;
      const nextColor = getMaterialColorById(materialId);
      const currentColors = ((layer.data as any)?.motifMaterialColors as Record<string, string> | undefined) || {};
      if (currentColors[materialId] === nextColor) return;
      layersManager.updateLayer(layer.id, {
        data: {
          ...(layer.data as any),
          motifMaterialColors: { ...currentColors, [materialId]: nextColor },
        },
      });
    });
  }, [materialColors, patternMotifColors, layersManager.layers, layersManager]);

  const handleMotifMaterialToggle = (motifId: string, materialId: string, enabled: boolean) => {
    if (enabled) {
      layersManager.layers.forEach(layer => {
        if (!layer.data?.isMotif) return;
        if ((layer.data as any)?.isPatternMotif) return;
        const layerMotifId = (layer.data as any)?.motifId || layer.id;
        if (layerMotifId === motifId) return;
        const selectedIds = (layer.data as any)?.selectedMaterialIds as string[] | undefined;
        if (!selectedIds || !selectedIds.includes(materialId)) return;
        const nextSelected = selectedIds.filter(id => id !== materialId);
        layersManager.updateLayer(layer.id, {
          data: { ...(layer.data as any), selectedMaterialIds: nextSelected },
        });
        setMotifMaterialSelections(prev => ({
          ...prev,
          [layerMotifId]: { ...(prev[layerMotifId] || {}), [materialId]: false },
        }));
      });
    }
    const defaultSelections: Record<string, boolean> = {};
    materials.forEach(material => {
      defaultSelections[material.id] = true;
    });
    const currentSelections = motifMaterialSelections[motifId] || defaultSelections;
    const nextSelections = { ...currentSelections, [materialId]: enabled };

    setMotifMaterialSelections(prev => ({ ...prev, [motifId]: nextSelections }));

    const nextSelected = Object.entries(nextSelections)
      .filter(([, value]) => value)
      .map(([id]) => id);

    const targetLayers = getBaseMotifLayersById(motifId);
    targetLayers.forEach(layer => {
      layersManager.updateLayer(layer.id, {
        data: { ...(layer.data as any), selectedMaterialIds: nextSelected },
      });
    });
    cacheDesignStateForMotif(motifId);
    requestPreviewRefresh();
  };

  const handleMotifGroupVisibilityToggle = (
    motifId: string,
    groupMaterials: MaterialDescriptor[],
    enabled: boolean
  ) => {
    if (enabled) {
      const idsToEnable = groupMaterials.map(material => material.id);
      layersManager.layers.forEach(layer => {
        if (!layer.data?.isMotif) return;
        if ((layer.data as any)?.isPatternMotif) return;
        const layerMotifId = (layer.data as any)?.motifId || layer.id;
        if (layerMotifId === motifId) return;
        const selectedIds = (layer.data as any)?.selectedMaterialIds as string[] | undefined;
        if (!selectedIds || selectedIds.length === 0) return;
        const nextSelected = selectedIds.filter(id => !idsToEnable.includes(id));
        if (nextSelected.length === selectedIds.length) return;
        layersManager.updateLayer(layer.id, {
          data: { ...(layer.data as any), selectedMaterialIds: nextSelected },
        });
        setMotifMaterialSelections(prev => {
          const current = { ...(prev[layerMotifId] || {}) };
          idsToEnable.forEach(id => {
            if (current[id] !== undefined) {
              current[id] = false;
            }
          });
          return { ...prev, [layerMotifId]: current };
        });
      });
    }

    const defaultSelections: Record<string, boolean> = {};
    materials.forEach(material => {
      defaultSelections[material.id] = true;
    });
    const currentSelections = motifMaterialSelections[motifId] || defaultSelections;
    const nextSelections = { ...currentSelections };
    groupMaterials.forEach(material => {
      nextSelections[material.id] = enabled;
    });

    setMotifMaterialSelections(prev => ({ ...prev, [motifId]: nextSelections }));

    const nextSelected = Object.entries(nextSelections)
      .filter(([, value]) => value)
      .map(([id]) => id);

    const targetLayers = getBaseMotifLayersById(motifId);
    targetLayers.forEach(layer => {
      layersManager.updateLayer(layer.id, {
        data: { ...(layer.data as any), selectedMaterialIds: nextSelected },
      });
    });
    cacheDesignStateForMotif(motifId);
    requestPreviewRefresh();
  };

  const updateMotifLayerColors = (motifId: string, nextColors: Record<string, string>) => {
    const targetLayers = getBaseMotifLayersById(motifId);
    if (targetLayers.length === 0) return;
    const safeColors = { ...nextColors };
    targetLayers.forEach(layer => {
      layersManager.updateLayer(layer.id, {
        data: { ...(layer.data as any), motifMaterialColors: safeColors },
      });
    });
    cacheDesignStateForMotif(motifId);
    requestPreviewRefresh();
  };

  const updateMotifDesignColors = (motifId: string, nextColors: { design1: string; design2: string }) => {
    const targetLayers = getBaseMotifLayersById(motifId);
    if (targetLayers.length === 0) return;
    targetLayers.forEach(layer => {
      layersManager.updateLayer(layer.id, {
        data: { ...(layer.data as any), motifDesignColors: { ...nextColors } },
      });
    });
    cacheDesignStateForMotif(motifId);
    requestPreviewRefresh();
  };

  const handleMotifDesignColorChange = (
    motifId: string,
    designKey: 'design1' | 'design2',
    color: string
  ) => {
    const current = getMotifDesignColors(motifId);
    const next = { ...current, [designKey]: color };
    updateMotifDesignColors(motifId, next);
  };

  const handleMotifMaterialColorChange = (motifId: string, materialId: string, color: string) => {
    const layerColors = (getMotifLayer(motifId)?.data as any)?.motifMaterialColors as
      | Record<string, string>
      | undefined;
    const current = { ...(layerColors || {}) };
    current[materialId] = color;
    updateMotifLayerColors(motifId, current);
  };

  const handleMotifGroupColorChange = (motifId: string, groupName: string, color: string) => {
    const groupMaterials = groupedMaterials[groupName] || [];
    const layerColors = (getMotifLayer(motifId)?.data as any)?.motifMaterialColors as
      | Record<string, string>
      | undefined;
    const current = { ...(layerColors || {}) };
    groupMaterials.forEach(material => {
      current[material.id] = color;
    });
    updateMotifLayerColors(motifId, current);
  };

  const handleMotifAllColorChange = (motifId: string, color: string) => {
    const layerColors = (getMotifLayer(motifId)?.data as any)?.motifMaterialColors as
      | Record<string, string>
      | undefined;
    const current = { ...(layerColors || {}) };
    materials.forEach(material => {
      current[material.id] = color;
    });
    updateMotifLayerColors(motifId, current);
  };

  const motifInstances = layersManager.layers
    .filter(layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif)
    .reduce<Record<string, { layer: Layer; sourceKey: string; label: string }>>((acc, layer) => {
      const motifId = (layer.data as any)?.motifId || layer.id;
      const sourceKey = (layer.data as any)?.motifSourceKey || layer.data?.fileName || motifId;
      const label = (layer.data as any)?.motifLabel || layer.data?.fileName || 'Motif';
      acc[motifId] = { layer, sourceKey, label };
      return acc;
    }, {});

  const motifInstancesBySource = Object.values(motifInstances).reduce<Record<string, string[]>>((acc, item) => {
    if (!acc[item.sourceKey]) acc[item.sourceKey] = [];
    acc[item.sourceKey].push((item.layer.data as any)?.motifId || item.layer.id);
    return acc;
  }, {});

  const getMotifLabel = (motifId: string) =>
    motifInstances[motifId]?.label || 'Motif';

  const getMotifLayer = (motifId: string) =>
    motifInstances[motifId]?.layer;

  const getMotifMaterialColor = (motifId: string, materialId: string) => {
    const layerColors = (getMotifLayer(motifId)?.data as any)?.motifMaterialColors as
      | Record<string, string>
      | undefined;
    return layerColors?.[materialId] || '#ffffff';
  };

  const getMotifDesignColors = (motifId: string) => {
    const motifLayer = getMotifLayer(motifId);
    const colors = (motifLayer?.data as any)?.motifDesignColors as
      | { design1?: string; design2?: string }
      | undefined;
    return {
      design1: colors?.design1 || '#ffffff',
      design2: colors?.design2 || '#ffffff',
    };
  };

  const getActiveMotifId = () => {
    if (activeMotifModal) return activeMotifModal;
    const motifLayer = layersManager.layers.find(
      layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif
    );
    if (!motifLayer) return null;
    return (motifLayer.data as any)?.motifId || motifLayer.id;
  };

  const activeMotifId = getActiveMotifId();

  useEffect(() => {
    if (activeMotifId) return;
    if (selectedMotifTarget !== 'design1' && selectedMotifTarget !== 'design2') return;
    setSelectedMotifTarget(materials[0]?.id || null);
  }, [activeMotifId, materials, selectedMotifTarget]);

  const designColorEntries = activeMotifId
    ? (() => {
        const motifLayer = getMotifLayer(activeMotifId);
        const sourceKey = (motifLayer?.data as any)?.motifSourceKey || (motifLayer?.data as any)?.fileName;
        const flags = sourceKey ? motifColorFlags[sourceKey] : undefined;
        const colors = getMotifDesignColors(activeMotifId);
        const entries: { id: string; label: string; color: string; designKey: 'design1' | 'design2' }[] = [];
        if (flags?.hasBlack) {
          entries.push({ id: 'design-1', label: 'Design 1', color: colors.design1, designKey: 'design1' });
        }
        if (flags?.hasWhite) {
          entries.push({ id: 'design-2', label: 'Design 2', color: colors.design2, designKey: 'design2' });
        }
        return entries;
      })()
    : [];

  const motifDesignRows = (() => {
    const baseColors = activeMotifId ? getMotifDesignColors(activeMotifId) : { design1: '#ef7218', design2: '#ffffff' };
    const motifLayer = activeMotifId ? getMotifLayer(activeMotifId) : null;
    const sourceKey = (motifLayer?.data as any)?.motifSourceKey || (motifLayer?.data as any)?.fileName;
    const flags = sourceKey ? motifColorFlags[sourceKey] : undefined;
    const canShow = Boolean(activeMotifId);
    return [
      {
        id: 'design-1',
        label: 'Design 1',
        color: baseColors.design1,
        designKey: 'design1' as const,
        isDisabled: !canShow || flags?.hasBlack === false,
      },
      {
        id: 'design-2',
        label: 'Design 2',
        color: baseColors.design2,
        designKey: 'design2' as const,
        isDisabled: !canShow || flags?.hasWhite === false,
      },
    ];
  })();

  const patternMotifFlags = (() => {
    const patternLayers = layersManager.layers.filter(
      layer => layer.data?.isMotif && (layer.data as any)?.isPatternMotif
    );
    if (patternLayers.length === 0) return { hasWhite: false, hasBlack: false };
    return patternLayers.reduce(
      (acc, layer) => {
        const sourceKey = (layer.data as any)?.motifSourceKey || (layer.data as any)?.fileName;
        const flags = sourceKey ? motifColorFlags[sourceKey] : undefined;
        if (flags?.hasWhite) acc.hasWhite = true;
        if (flags?.hasBlack) acc.hasBlack = true;
        return acc;
      },
      { hasWhite: false, hasBlack: false }
    );
  })();

  const motifPatternEntries: Array<{ id: string; label: string; color: string; motifKey: 'motif1' | 'motif2' }> = [];
  if (patternMotifFlags.hasBlack) {
    motifPatternEntries.push({ id: 'motif-1', label: 'Motif 1', color: patternMotifColors.motif1, motifKey: 'motif1' });
  }
  if (patternMotifFlags.hasWhite) {
    motifPatternEntries.push({ id: 'motif-2', label: 'Motif 2', color: patternMotifColors.motif2, motifKey: 'motif2' });
  }

  const designCards: DesignCard[] = DESIGN_ITEMS.map(item => {
    const sourceKey = item.url || item.name;
    const motifIdsForSource = motifInstancesBySource[sourceKey] || [];
    const hasInstance = motifIdsForSource.length > 0;
    const visibleMotifIds = motifIdsForSource.filter((motifId) => {
      const layer = motifInstances[motifId]?.layer;
      if (!layer) return false;
      const selectedIds = (layer.data as any)?.selectedMaterialIds as string[] | undefined;
      return !selectedIds || selectedIds.length > 0;
    });
    const isVisible = visibleMotifIds.length > 0;
    const previewUrl =
      !appState.isModelLoading && previewModelKey === (activeModel?.url || '')
        ? designPreviewUrls[sourceKey]
        : undefined;
    return {
      key: sourceKey,
      name: item.name,
      isNone: Boolean(item.isNone),
      previewUrl,
      isActive: isVisible,
      onSelect: () => handleAddDesign(item),
    };
  });

  const bestDesignCards: DesignCard[] = BEST_DESIGN_ITEMS.map(item => {
    const parsed = bestDesignFiles[item.key];
    const previewUrl = bestDesignPreviewUrls[item.key];
    return {
      key: `best-${item.key}`,
      name: item.name,
      isNone: false,
      previewUrl,
      isActive: false,
      onSelect: () => {
        if (!parsed) return;
        void handleApplyBestDesign(item.key);
      },
    };
  });

  const patternMotifsByTarget = layersManager.layers
    .filter(layer => layer.data?.isMotif && (layer.data as any)?.isPatternMotif)
    .reduce<Record<string, { sourceKey: string; layerId: string }>>((acc, layer) => {
      const materialId = (layer.data as any)?.patternMaterialId as string | undefined;
      const designKey = (layer.data as any)?.patternDesignKey as string | undefined;
      const targetKey = designKey || materialId;
      if (!targetKey) return acc;
      const sourceKey = (layer.data as any)?.motifSourceKey || (layer.data as any)?.fileName || layer.id;
      acc[targetKey] = { sourceKey, layerId: layer.id };
      return acc;
    }, {});

  const motifCards: DesignCard[] = selectedMotifTarget
    ? MOTIF_ITEMS.map(item => {
        const sourceKey = item.url || item.name;
        const mappedTarget =
          selectedMotifTarget === 'design1'
            ? 'design2'
            : selectedMotifTarget === 'design2'
              ? 'design1'
              : selectedMotifTarget;
        const current = patternMotifsByTarget[mappedTarget];
        const isActive = item.isNone ? !current : current?.sourceKey === sourceKey;
        return {
          key: `${mappedTarget}-${sourceKey}`,
          name: item.name,
          isNone: Boolean(item.isNone),
          previewUrl: item.isNone ? undefined : item.url,
          isActive,
          onSelect: () => handleAddPatternMotif(item, selectedMotifTarget),
        };
      })
    : [];

  const selectedMotifTargetLabel = (() => {
    if (!selectedMotifTarget) return '';
    if (selectedMotifTarget === 'design1') return 'Design 1';
    if (selectedMotifTarget === 'design2') return 'Design 2';
    const material = materials.find(item => item.id === selectedMotifTarget);
    return material?.element || '';
  })();

  const toggleGradientValue = (
    value: string,
    onChange: (next: string) => void,
    enabled: boolean,
    preferredStop: 'start' | 'end' = 'start'
  ) => {
    if (enabled) {
      if (isGradientValue(value)) {
        return;
      }
      const base = value;
      const end = getContrastGradientColor(base);
      onChange(buildGradientValue(90, base, end, 50));
      return;
    }
    if (isGradientValue(value)) {
      const parsed = parseGradientValue(value);
      onChange(preferredStop === 'end' ? parsed.end : parsed.start);
    }
  };

  const handleAddPatternMotif = async (motif: DesignItem, targetId: string) => {
    try {
      const existingLayerIds = patternLayerIdsRef.current[targetId];
      if (existingLayerIds?.length) {
        existingLayerIds.forEach(layerId => layersManager.removeLayer(layerId));
        delete patternLayerIdsRef.current[targetId];
      }

      if (motif.isNone) {
        return;
      }

      const sourceKey = motif.url || motif.name;
      let image = motifImageCacheRef.current[sourceKey];
      if (!image) {
        image = await loadImageFromUrl(motif.url);
        motifImageCacheRef.current[sourceKey] = image;
      }
      const flags = analyzeImageForBlackWhite(image);
      setMotifColorFlags(prev => ({ ...prev, [sourceKey]: flags }));

      const isDesignTarget = targetId === 'design1' || targetId === 'design2';
      const baseDesignLayer = isDesignTarget
        ? layersManager.layers.find(layer => layer.data?.isMotif && !(layer.data as any)?.isPatternMotif)
        : null;
      const placements = isDesignTarget
        ? buildAutoMotifPlacements()
        : (() => {
            const fromMaterial = getPlacementFromBounds(materialUvBounds[targetId]);
            const placement = fromMaterial || getZonePlacement('front');
            if (!placement) return [];
            return [{ zone: 'front' as TShirtZone, materialId: targetId, uvCenter: placement.uvCenter, uvSize: placement.uvSize }];
          })();
      const fallbackPlacement = {
        zone: 'front' as TShirtZone,
        materialId: isDesignTarget ? undefined : targetId,
        uvCenter: (baseDesignLayer?.uvCenter || [0.5, 0.5]) as [number, number],
        uvSize: (baseDesignLayer?.uvSize || [1, 1]) as [number, number],
      };
      const effectivePlacements = placements.length > 0 ? placements : [fallbackPlacement];
      const needsInitialPlacement = placements.length === 0;
      const motifId = `pattern_${targetId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const selectedMaterialIds = isDesignTarget
        ? (effectivePlacements.map(p => p.materialId).filter(Boolean) as string[]).filter((id, idx, arr) => arr.indexOf(id) === idx)
        : [targetId];
      const fallbackSelectedIds = (baseDesignLayer?.data as any)?.selectedMaterialIds as string[] | undefined;
      const resolvedSelectedIds = selectedMaterialIds.length > 0 ? selectedMaterialIds : fallbackSelectedIds;

      const createdLayerIds = effectivePlacements.map((placement, index) => {
        const baseLayer: Omit<Layer, 'id' | 'order'> = {
          type: 'image',
          zone: placement.zone,
          locked: true,
          source: image,
          uvCenter: placement.uvCenter,
          uvSize: placement.uvSize,
          uvRotation: 0,
          transformMode: 'translate',
          data: {
            fileName: motif.name,
            originalSize: { width: image.width, height: image.height },
            needsInitialPlacement,
            isMotif: true,
            isPatternMotif: true,
            patternMaterialId: isDesignTarget ? undefined : targetId,
            patternDesignKey: isDesignTarget
              ? (targetId === 'design1' ? 'design2' : 'design1')
              : undefined,
            motifId,
            motifSourceKey: sourceKey,
            motifLabel: motif.name,
            selectedMaterialIds: resolvedSelectedIds,
            motifMaterialColors: isDesignTarget ? undefined : { [targetId]: getMaterialColorById(targetId) },
            motifPatternColors: { ...patternMotifColors },
          },
        };

        const layer = layersManager.addLayer(baseLayer);
        if (isDesignTarget && baseDesignLayer) {
          layersManager.updateLayer(layer.id, { order: baseDesignLayer.order + 0.001 + index * 0.0001 });
        } else {
          motifOrderRef.current -= 1;
          layersManager.updateLayer(layer.id, { order: motifOrderRef.current });
        }
        return layer.id;
      });

      patternLayerIdsRef.current[targetId] = createdLayerIds;
    } catch (error) {
      console.error('Motif add error:', error);
      alert('Motif add error.');
    }
  };


  const resetMotifVisibility = (motifId: string) => {
    if (materials.length === 0) return;
    const availableSelections = materials.reduce<Record<string, boolean>>((acc, material) => {
      const isOccupied = layersManager.layers.some(layer => {
        if (!layer.data?.isMotif) return false;
        if ((layer.data as any)?.isPatternMotif) return false;
        const layerMotifId = (layer.data as any)?.motifId || layer.id;
        if (layerMotifId === motifId) return false;
        const selectedIds = (layer.data as any)?.selectedMaterialIds as string[] | undefined;
        if (!selectedIds) return true;
        return selectedIds.includes(material.id);
      });
      acc[material.id] = !isOccupied;
      return acc;
    }, {});
    setMotifMaterialSelections(prev => ({ ...prev, [motifId]: availableSelections }));
    const targetLayer = getMotifLayer(motifId);
    if (!targetLayer) return;
    const selectedMaterialIds = Object.entries(availableSelections)
      .filter(([, value]) => value)
      .map(([id]) => id);
    const targetLayers = getBaseMotifLayersById(motifId);
    targetLayers.forEach(layer => {
      layersManager.updateLayer(layer.id, {
        data: { ...(layer.data as any), selectedMaterialIds },
      });
    });
    cacheDesignStateForMotif(motifId);
    requestPreviewRefresh();
  };

  const buildDesignPreview = async (design: DesignItem) => {
    if (design.isNone) return null;
    const renderer = previewRendererRef.current;
    const scene = previewSceneRef.current;
    if (!renderer || !scene) return null;

    const designTargets: { material: THREE.Material; uniforms: { designMap: { value: THREE.Texture } } }[] = [];
    scene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return;
      const material = obj.material;
      const materials = Array.isArray(material) ? material : [material];
      materials.forEach(item => {
        const userData = (item as THREE.Material).userData as any;
        if (userData?.designMixUniforms?.designMap) {
          designTargets.push({
            material: item as THREE.Material,
            uniforms: userData.designMixUniforms,
          });
        }
      });
    });
    if (designTargets.length === 0) return null;

    const materialBounds: Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]> = {};
    const recordBounds = (materialKey: string, bounds: { uMin: number; uMax: number; vMin: number; vMax: number }) => {
      if (!materialBounds[materialKey]) materialBounds[materialKey] = [];
      materialBounds[materialKey].push(bounds);
    };

    scene.traverse(obj => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (obj.name.endsWith('_overlay')) return;
      const geometry = obj.geometry as THREE.BufferGeometry;
      const uvAttr = geometry.getAttribute('uv') as THREE.BufferAttribute | null;
      if (!uvAttr) return;
      const index = geometry.getIndex();
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      const groups = geometry.groups && geometry.groups.length > 0
        ? geometry.groups
        : [{ start: 0, count: index ? index.count : uvAttr.count, materialIndex: 0 }];

      groups.forEach(group => {
        const material = materials[group.materialIndex] || materials[0];
        if (!material || !isPaintableMaterial(material)) return;
        let uMin = 1;
        let vMin = 1;
        let uMax = 0;
        let vMax = 0;
        const start = group.start;
        const end = group.start + group.count;
        if (index) {
          for (let i = start; i < end; i += 1) {
            const vertexIndex = index.getX(i);
            const u = uvAttr.getX(vertexIndex);
            const v = uvAttr.getY(vertexIndex);
            uMin = Math.min(uMin, u);
            vMin = Math.min(vMin, v);
            uMax = Math.max(uMax, u);
            vMax = Math.max(vMax, v);
          }
        } else {
          for (let i = start; i < end; i += 1) {
            const u = uvAttr.getX(i);
            const v = uvAttr.getY(i);
            uMin = Math.min(uMin, u);
            vMin = Math.min(vMin, v);
            uMax = Math.max(uMax, u);
            vMax = Math.max(vMax, v);
          }
        }
        const materialKey = getMaterialKey(material, obj.name || '', group.materialIndex);
        recordBounds(materialKey, { uMin, uMax, vMin, vMax });
      });
    });

    const materialIds = Object.keys(materialBounds);
    const sourceKey = design.url || design.name;
    let image = motifImageCacheRef.current[sourceKey];
    if (!image) {
      image = await loadImageFromUrl(design.url);
      motifImageCacheRef.current[sourceKey] = image;
    }

    const motifMaterialColors = materialIds.reduce<Record<string, string>>((acc, id) => {
      acc[id] = '#f97316';
      return acc;
    }, {});
    const selectedMaterialIds = materialIds;
    const motifDesignColors = { design1: '#ef7218', design2: '#ffffff' };

    const motifLayer: Layer = {
      id: `preview_${sourceKey}`,
      order: 0,
      type: 'image',
      zone: 'front',
      locked: true,
      source: image,
      uvCenter: [0.5, 0.5],
      uvSize: [1, 1],
      uvRotation: 0,
      transformMode: 'translate',
      data: {
        fileName: design.name,
        originalSize: { width: image.width, height: image.height },
        needsInitialPlacement: false,
        isMotif: true,
        isGlobalUv: true,
        motifSourceKey: sourceKey,
        motifLabel: design.name,
        selectedMaterialIds,
        motifMaterialColors,
        motifDesignColors,
      },
    };

    const textureSize = 512;
    const motifCanvas = document.createElement('canvas');
    motifCanvas.width = textureSize;
    motifCanvas.height = textureSize;
    const motifCtx = motifCanvas.getContext('2d');
    if (!motifCtx) return null;

    renderLayersToCanvas(
      motifCtx,
      [motifLayer],
      textureSize,
      textureSize
    );

    const previewTexture = new THREE.CanvasTexture(motifCanvas);
    previewTexture.flipY = false;
    previewTexture.colorSpace = THREE.SRGBColorSpace;
    previewTexture.needsUpdate = true;

    const originalDesignMaps = designTargets.map(target => target.uniforms.designMap.value);
    const prevTarget = renderer.getRenderTarget();
    const prevClear = renderer.getClearColor(new THREE.Color());
    const prevClearAlpha = renderer.getClearAlpha();
    const prevToneMapping = renderer.toneMapping;
    const prevToneMappingExposure = renderer.toneMappingExposure;
    const size = 192;
    let renderTarget: THREE.WebGLRenderTarget | null = null;
    let output: string | null = null;

    try {
      renderTarget = new THREE.WebGLRenderTarget(size, size);
      if ('colorSpace' in renderTarget.texture && 'outputColorSpace' in renderer) {
        renderTarget.texture.colorSpace = renderer.outputColorSpace;
      }
      renderer.setRenderTarget(renderTarget);
      renderer.setClearColor('#f0f0f0', 1);

      const bbox = new THREE.Box3();
      const tempBoxC = new THREE.Box3();
      scene.traverse(obj => {
        if (!(obj instanceof THREE.Mesh)) return;
        if (obj.name.endsWith('_overlay')) return;
        const geo = (obj as THREE.Mesh).geometry;
        if (!geo) return;
        if (!geo.boundingBox) geo.computeBoundingBox();
        if (!geo.boundingBox) return;
        tempBoxC.copy(geo.boundingBox).applyMatrix4(obj.matrixWorld);
        bbox.union(tempBoxC);
      });
      if (bbox.isEmpty()) bbox.setFromObject(scene);
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      const radius = bbox.getBoundingSphere(new THREE.Sphere()).radius || 1;
      const previewCamera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
      const fov = (previewCamera.fov * Math.PI) / 180;
      const distance = radius / Math.sin(fov / 2);
      const zoomDistance = distance * 0.8;
      const angle = (-20 * Math.PI) / 180;
      previewCamera.position.set(Math.sin(angle) * zoomDistance, distance * 0.05, Math.cos(angle) * zoomDistance);
      previewCamera.lookAt(0, 0, 0);
      previewCamera.near = Math.max(0.01, distance / 50);
      previewCamera.far = distance * 5;
      previewCamera.updateProjectionMatrix();

      // Warmup render: triggers onBeforeCompile for the preview renderer (first-time shader
      // compilation). This must happen BEFORE overriding designMap so that onBeforeCompile
      // does not reset our previewTexture back to the original canvas texture.
      renderer.render(scene, previewCamera);

      // onBeforeCompile replaces material.userData.designMixUniforms with a new object.
      // Refresh references so we write to the current shader's uniforms, not stale ones.
      designTargets.forEach(target => {
        const fresh = (target.material.userData as any).designMixUniforms;
        if (fresh) target.uniforms = fresh;
      });

      designTargets.forEach(target => {
        target.uniforms.designMap.value = previewTexture;
      });

      renderer.render(scene, previewCamera);

      const buffer = new Uint8Array(size * size * 4);
      renderer.readRenderTargetPixels(renderTarget, 0, 0, size, size, buffer);

      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const imageData = ctx.createImageData(size, size);
      const textureColorSpace = (renderTarget.texture as any).colorSpace;
      const shouldConvertToSrgb =
        textureColorSpace !== undefined && textureColorSpace !== THREE.SRGBColorSpace;
      const toSrgb = (value: number) => {
        const v = value / 255;
        const srgb = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
        return Math.min(255, Math.max(0, Math.round(srgb * 255)));
      };
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          const srcIndex = (x + (size - y - 1) * size) * 4;
          const dstIndex = (x + y * size) * 4;
          const r = buffer[srcIndex];
          const g = buffer[srcIndex + 1];
          const b = buffer[srcIndex + 2];
          imageData.data[dstIndex] = shouldConvertToSrgb ? toSrgb(r) : r;
          imageData.data[dstIndex + 1] = shouldConvertToSrgb ? toSrgb(g) : g;
          imageData.data[dstIndex + 2] = shouldConvertToSrgb ? toSrgb(b) : b;
          imageData.data[dstIndex + 3] = buffer[srcIndex + 3];
        }
      }
      ctx.putImageData(imageData, 0, 0);
      let nonBackground = 0;
      const bgR = 240;
      const bgG = 240;
      const bgB = 240;
      const threshold = 14;
      for (let i = 0; i < imageData.data.length; i += 4) {
        const a = imageData.data[i + 3];
        if (a < 20) continue;
        const dr = Math.abs(imageData.data[i] - bgR);
        const dg = Math.abs(imageData.data[i + 1] - bgG);
        const db = Math.abs(imageData.data[i + 2] - bgB);
        if (dr > threshold || dg > threshold || db > threshold) {
          nonBackground += 1;
        }
      }
      const totalPixels = size * size;
      if (nonBackground < totalPixels * 0.003) {
        output = null;
      } else {
      output = canvas.toDataURL('image/png');
      }
    } finally {
      designTargets.forEach((target, index) => {
        target.uniforms.designMap.value = originalDesignMaps[index];
        // needsUpdate=true restores GPU state for the main viewport. onBeforeCompile will
        // re-fire on the next main render and set designMap to textureRef.current, which
        // is the same canvas texture we are restoring here — no conflict.
        target.material.needsUpdate = true;
      });
      if (renderTarget) {
        renderTarget.dispose();
      }
      renderer.setRenderTarget(prevTarget);
      renderer.toneMapping = prevToneMapping;
      renderer.toneMappingExposure = prevToneMappingExposure;
      renderer.setClearColor(prevClear, prevClearAlpha);
    }

    return output;
  };


  useEffect(() => {
    if (appState.isModelLoading) return;
    if (pausePreviewGeneration) return;
    let cancelled = false;
    if (previewBusyRef.current) return;
    previewBusyRef.current = true;
    const delayMs = 350;

    const idle = (cb: () => void) => {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(cb, { timeout: 120 });
      } else {
        window.setTimeout(cb, 40);
      }
    };

    const buildAll = async () => {
      await new Promise<void>(resolve => window.setTimeout(resolve, delayMs));
      const ready = await waitForPreviewSceneReady(() => cancelled);
      if (!ready || cancelled) return;
      await new Promise<void>(resolve =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
      if (cancelled) return;

      for (const item of DESIGN_ITEMS) {
        if (item.isNone) continue;
        const key = item.url || item.name;
        if (cancelled) return;
        const storageKey = buildPreviewStorageKey(item.sourcePath || key);

        const hasStored = await hasStoredPreview('design', storageKey);
        if (cancelled) return;
        if (hasStored) {
          setDesignPreviewUrls(prev => ({ ...prev, [key]: buildPreviewUrl('design', storageKey) }));
          await new Promise<void>(resolve => idle(resolve));
          continue;
        }

        try {
          let preview: string | null = null;
          for (let attempt = 0; attempt < 3; attempt += 1) {
            previewRenderJobsRef.current += 1;
            try {
              preview = await buildDesignPreview(item);
            } finally {
              previewRenderJobsRef.current = Math.max(0, previewRenderJobsRef.current - 1);
            }
            if (preview) break;
            await new Promise<void>(resolve => window.setTimeout(resolve, 120));
          }
          if (preview) {
            setDesignPreviewUrls(prev => ({ ...prev, [key]: preview }));
            const saved = await saveStoredPreview('design', storageKey, preview);
            if (cancelled) return;
            if (saved) {
              setDesignPreviewUrls(prev => ({ ...prev, [key]: buildPreviewUrl('design', storageKey) }));
            }
          }
        } catch (error) {
          console.warn('Design preview failed:', error);
        }
        await new Promise<void>(resolve => idle(resolve));
      }
    };

    void buildAll().finally(() => {
      previewBusyRef.current = false;
      setModelPreviewRefreshKey(prev => prev + 1);
    });

    return () => {
      cancelled = true;
      previewBusyRef.current = false;
    };
  }, [activeModel?.url, appState.isModelLoading, pausePreviewGeneration]);

  const handleRemoveDesign = (motifId: string) => {
    const targetLayers = layersManager.layers.filter(
      layer => layer.data?.isMotif && ((layer.data as any)?.motifId || layer.id) === motifId
    );
    targetLayers.forEach(layer => layersManager.removeLayer(layer.id));
    delete motifLayerIdsRef.current[motifId];
    setMotifMaterialSelections(prev => {
      const next = { ...prev };
      delete next[motifId];
      return next;
    });
    if (activeMotifModal === motifId) {
      setActiveMotifModal(null);
    }
    if (
      activeColorPicker?.type === 'motif-group' &&
      activeColorPicker.motifId === motifId
    ) {
      setActiveColorPicker(null);
    }
    if (
      activeColorPicker?.type === 'motif-material' &&
      activeColorPicker.motifId === motifId
    ) {
      setActiveColorPicker(null);
    }
  };

  const renderMotifControls = (motifId: string) => {
    const motifSelections = motifMaterialSelections[motifId] || {};
    const hasGroups = Object.keys(groupedMaterials).length > 0;
    return (
      <div className="motif-panel">
        {hasGroups && (
          <>
            <div className="simple-list">
              {Object.entries(groupedMaterials).map(([groupName, groupMaterials]) => {
                const { color, isMixed } = getMotifGroupColorInfo(motifId, groupMaterials);
                const showElements = isMixed || Boolean(expandedMotifGroups[groupName]);
                const groupVisible = groupMaterials.some(material => motifSelections[material.id] !== false);
                return (
                  <div key={`${motifId}-${groupName}`} className={`simple-group ${showElements ? 'is-open' : ''}`}>
                    <div className={`simple-row ${!groupVisible ? 'is-hidden' : ''}`}>
                      <div className="simple-row-actions">
                        <button
                          className="simple-swatch-btn"
                          onClick={() => setActiveColorPicker({ type: 'motif-group', motifId, groupName })}
                          type="button"
                          aria-label={`Modifier la couleur du groupe ${groupName}`}
                        >
                          <span
                            className={`color-group-swatch ${isMixed ? 'color-preview-mixed' : ''} ${!isMixed && isLightSwatch(color) ? 'is-light' : ''}`}
                            style={isMixed ? undefined : { background: getSwatchBackground(color) }}
                          />
                        </button>
                        <button
                          className="motif-visibility-btn"
                          onClick={() => handleMotifGroupVisibilityToggle(motifId, groupMaterials, !groupVisible)}
                          type="button"
                          aria-pressed={groupVisible}
                          aria-label={groupVisible ? `Masquer ${groupName}` : `Afficher ${groupName}`}
                        >
                          <img
                            src={`/assets/icon/${groupVisible ? 'icons8-visible-50.png' : 'icons8-invisible-50.png'}`}
                            alt=""
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                      <button
                        className="simple-row-title simple-row-title-btn"
                        type="button"
                        onClick={() => setActiveColorPicker({ type: 'motif-group', motifId, groupName })}
                        aria-label={`Modifier la couleur du groupe ${groupName}`}
                      >
                        <span className="clamp-text">{groupName}</span>
                      </button>
                      <button
                        className="simple-row-divider simple-row-divider-btn"
                        aria-label={`Modifier la couleur du groupe ${groupName}`}
                        type="button"
                        onClick={() => setActiveColorPicker({ type: 'motif-group', motifId, groupName })}
                      />
                      {!isMixed && (
                        <button
                          className="simple-expand-btn simple-row-detail"
                          onClick={() => toggleMotifGroup(groupName)}
                          type="button"
                        >
                          {expandedMotifGroups[groupName] ? 'Masquer' : 'Détails'}
                        </button>
                      )}
                    </div>

                    <div className={`simple-sublist ${showElements ? 'is-open' : ''}`}>
                      {groupMaterials.map(material => {
                        const isVisible = motifSelections[material.id] !== false;
                        return (
                          <div
                            key={`${motifId}-${material.id}`}
                            className={`simple-row simple-subrow ${!isVisible ? 'is-hidden' : ''}`}
                          >
                            <div className="simple-row-actions">
                              <button
                                className={`color-element-swatch ${isLightSwatch(getMotifMaterialColor(motifId, material.id)) ? 'is-light' : ''}`}
                                onClick={() =>
                                  setActiveColorPicker({ type: 'motif-material', motifId, materialId: material.id })
                                }
                                type="button"
                                aria-label={`Changer la couleur de ${material.element}`}
                                style={{ background: getSwatchBackground(getMotifMaterialColor(motifId, material.id)) }}
                              />
                              <button
                                className="motif-visibility-btn"
                                onClick={() => handleMotifMaterialToggle(motifId, material.id, !isVisible)}
                                type="button"
                                aria-pressed={isVisible}
                                aria-label={isVisible ? `Masquer ${material.element}` : `Afficher ${material.element}`}
                              >
                                <img
                                  src={`/assets/icon/${isVisible ? 'icons8-visible-50.png' : 'icons8-invisible-50.png'}`}
                                  alt=""
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                              <button
                                className="simple-row-title simple-row-title-btn"
                                type="button"
                                onClick={() =>
                                  setActiveColorPicker({ type: 'motif-material', motifId, materialId: material.id })
                                }
                                aria-label={`Changer la couleur de ${material.element}`}
                              >
                                <span className="clamp-text">{material.element}</span>
                              </button>
                            <button
                              className="simple-row-divider simple-row-divider-btn"
                              aria-label={`Modifier la couleur du groupe ${groupName}`}
                              type="button"
                              onClick={() => setActiveColorPicker({ type: 'group', id: groupName })}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderColorTrigger = (
    value: string,
    onClick: () => void,
    isMixed?: boolean,
    labelText?: string
  ) => (
    <button
      className={`color-trigger material-color-trigger ${isMixed ? 'is-mixed' : ''}`}
      onClick={onClick}
      aria-label="Ouvrir le nuancier"
    >
      <span>{labelText || 'Couleur'}</span>
      <span
        className={`color-preview ${isMixed ? 'color-preview-mixed' : ''} ${!isMixed && isLightSwatch(value) ? 'is-light' : ''}`}
        style={isMixed ? undefined : { background: getSwatchBackground(value) }}
      />
    </button>
  );

  const applyGradientMode = (
    mode: 'solid' | 'gradient',
    pickerInfo: { value: string; onChange: (color: string) => void } | null,
    pickerTarget: MotifPickerTarget | null
  ) => {
    if (!pickerInfo) return;
    const gradientAllowed =
      pickerTarget?.type === 'material' ||
      pickerTarget?.type === 'motif-material' ||
      pickerTarget?.type === 'motif-design' ||
      pickerTarget?.type === 'pattern-motif';
    const isGradientTab = activeTab === 'gradient';
    if (mode === 'gradient' && !gradientAllowed && !isGradientTab) return;
    if (mode === 'gradient') {
      const base = isGradientValue(pickerInfo.value)
        ? parseGradientValue(pickerInfo.value).start
        : pickerInfo.value;
      setGradientColors({ start: base, end: base });
      setGradientAngle(90);
      setGradientStop('start');
      setGradientBalance(50);
      pickerInfo.onChange(buildGradientValue(90, base, base, 50));
      setColorPickerMode('gradient');
      return;
    }
    const base = isGradientValue(pickerInfo.value)
      ? parseGradientValue(pickerInfo.value).start
      : pickerInfo.value;
    pickerInfo.onChange(base);
    setColorPickerMode('solid');
  };

  const renderColorPickerBody = () => {
    if (!activePickerInfo) return null;
    const gradientAllowed =
      activeColorPicker?.type === 'material' ||
      activeColorPicker?.type === 'motif-material' ||
      activeColorPicker?.type === 'motif-design' ||
      activeColorPicker?.type === 'pattern-motif';
    const isGradientTab = activeTab === 'gradient';
    const currentColor =
      colorPickerMode === 'gradient'
        ? gradientColors[gradientStop]
        : activePickerInfo.value;

    const updateActiveColor = (next: string) => {
      if (colorPickerMode === 'gradient') {
        const nextColors = { ...gradientColors, [gradientStop]: next };
        setGradientColors(nextColors);
        scheduleGradientApply(
          buildGradientValue(gradientAngle, nextColors.start, nextColors.end, gradientBalance),
          activePickerInfo.onChange
        );
        return;
      }
      activePickerInfo.onChange(next);
    };

    const updateAngle = (angle: number) => {
      setGradientAngle(angle);
      scheduleGradientApply(
        buildGradientValue(angle, gradientColors.start, gradientColors.end, gradientBalance),
        activePickerInfo.onChange
      );
    };

    const updateBalance = (balance: number) => {
      setGradientBalance(balance);
      scheduleGradientApply(
        buildGradientValue(gradientAngle, gradientColors.start, gradientColors.end, balance),
        activePickerInfo.onChange
      );
    };

    const flushGradientApply = (onChange: (color: string) => void) => {
      if (gradientApplyTimerRef.current !== null) {
        window.clearTimeout(gradientApplyTimerRef.current);
        gradientApplyTimerRef.current = null;
      }
      const value =
        colorPickerMode === 'gradient'
          ? buildGradientValue(gradientAngle, gradientColors.start, gradientColors.end, gradientBalance)
          : activePickerInfo.value;
      onChange(value);
    };

    const scheduleGradientApply = (value: string, onChange: (color: string) => void) => {
      if (gradientApplyTimerRef.current !== null) {
        window.clearTimeout(gradientApplyTimerRef.current);
      }
      gradientApplyTimerRef.current = window.setTimeout(() => {
        gradientApplyTimerRef.current = null;
        onChange(value);
      }, 80);
    };

    const handleHexInput = (value: string) => {
      const normalized = value.startsWith('#') ? value : `#${value}`;
      if (/^#[0-9a-fA-F]{6}$/.test(normalized)) {
        updateActiveColor(normalized);
      }
    };

    const openEyeDropper = async () => {
      if (!('EyeDropper' in window)) return;
      try {
        const eyeDropper = new (window as any).EyeDropper();
        const result = await eyeDropper.open();
        if (result?.sRGBHex) {
          updateActiveColor(result.sRGBHex);
        }
      } catch (error) {
        console.warn('EyeDropper cancelled', error);
      }
    };
    const isEyeDropperSupported = 'EyeDropper' in window;

    const previewValue =
      colorPickerMode === 'gradient'
        ? (toCssGradient(buildGradientValue(gradientAngle, gradientColors.start, gradientColors.end, gradientBalance))
          || currentColor)
        : currentColor;

    const selectedColors = new Set<string>();
    materials.forEach(material => {
      selectedColors.add((getMaterialColor(material) || '').toLowerCase());
    });
    if (activeMotifId) {
      const designColors = getMotifDesignColors(activeMotifId);
      selectedColors.add((designColors.design1 || '').toLowerCase());
      selectedColors.add((designColors.design2 || '').toLowerCase());
    }
    selectedColors.add(patternMotifColors.motif1.toLowerCase());
    selectedColors.add(patternMotifColors.motif2.toLowerCase());

    const showGradientBalance = activeTab === 'gradient' && gradientMobileTab === 'balance';
    const showGradientSwatches = !(activeTab === 'gradient' && gradientMobileTab === 'balance');

    return (
      <>
        {activeTab === 'gradient' && (
          <div key={`gradient-${gradientMobileTab}`}>
            <div className="gradient-controls desktop-only">
              <div className="gradient-header-row">
                <div className="gradient-preview-card">
                  <div className="gradient-preview-box" style={{ background: previewValue }} />
                </div>
                <label className="toggle-row">
                  <span>Dégradé</span>
                  <input
                    type="checkbox"
                    checked={colorPickerMode === 'gradient'}
                    onChange={(e) => applyGradientMode(e.target.checked ? 'gradient' : 'solid', activePickerInfo, activeColorPicker)}
                  />
                </label>
              </div>
              {colorPickerMode === 'gradient' && (
                <>
                  <div className="gradient-slider-row">
                    <label className="gradient-slider">
                      <span>Translate</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={gradientBalance}
                        onChange={(e) => updateBalance(Number(e.target.value))}
                        onPointerUp={() => flushGradientApply(activePickerInfo.onChange)}
                        onTouchEnd={() => flushGradientApply(activePickerInfo.onChange)}
                      />
                      <span className="gradient-value">{(gradientBalance / 100).toFixed(2)}</span>
                    </label>
                    <label className="gradient-slider">
                      <span>Angle</span>
                      <input
                        type="range"
                        min={0}
                        max={360}
                        value={gradientAngle}
                        onChange={(e) => updateAngle(Number(e.target.value))}
                        onPointerUp={() => flushGradientApply(activePickerInfo.onChange)}
                        onTouchEnd={() => flushGradientApply(activePickerInfo.onChange)}
                      />
                      <span className="gradient-value">{`${gradientAngle}°`}</span>
                    </label>
                  </div>
                  <div className="gradient-tabs">
                    <button
                      className={`gradient-tab ${gradientStop === 'start' ? 'is-active' : ''}`}
                      type="button"
                      onClick={() => setGradientStop('start')}
                  >
                    Couleur principale
                  </button>
                  <button
                      className={`gradient-tab ${gradientStop === 'end' ? 'is-active' : ''}`}
                      type="button"
                      onClick={() => setGradientStop('end')}
                  >
                    Dégradé de couleurs
                  </button>
                  </div>
                </>
              )}
            </div>

            {showGradientBalance && (
              <div className="gradient-controls mobile-only">
                {colorPickerMode === 'gradient' && (
                  <div className="gradient-slider-row">
                    <label className="gradient-slider">
                      <span>Translate</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={gradientBalance}
                        onChange={(e) => updateBalance(Number(e.target.value))}
                        onPointerUp={() => flushGradientApply(activePickerInfo.onChange)}
                        onTouchEnd={() => flushGradientApply(activePickerInfo.onChange)}
                      />
                      <span className="gradient-value">{(gradientBalance / 100).toFixed(2)}</span>
                    </label>
                    <label className="gradient-slider">
                      <span>Angle</span>
                      <input
                        type="range"
                        min={0}
                        max={360}
                        value={gradientAngle}
                        onChange={(e) => updateAngle(Number(e.target.value))}
                        onPointerUp={() => flushGradientApply(activePickerInfo.onChange)}
                        onTouchEnd={() => flushGradientApply(activePickerInfo.onChange)}
                      />
                      <span className="gradient-value">{`${gradientAngle}°`}</span>
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {showGradientSwatches && (
          <div className="maring-left-right">
          <div className="two-col-container">
            <div className="column">
            {COLOR_SWATCH_GRID.left.map((row, idx) => (
              <div key={`left-${activePickerInfo?.key}-${idx}`} className="colorrow">
                {row.map((color) => (
                  (() => {
                    const isCurrent = currentColor.toLowerCase() === color.toLowerCase();
                    const isOtherSelected = !isCurrent && selectedColors.has(color.toLowerCase());
                    return (
                  <button
                    key={`${activePickerInfo?.key}-${color}`}
                    className={`colorPicker-swatch ${isCurrent ? 'currentColor' : ''} ${isOtherSelected ? 'otherSelected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => updateActiveColor(color)}
                    title={color.toUpperCase()}
                  >
                    <span className="color-ok" />
                  </button>
                    );
                  })()
                ))}
              </div>
            ))}
          </div>
          <div className="column">
            {COLOR_SWATCH_GRID.right.map((row, idx) => (
              <div key={`right-${activePickerInfo?.key}-${idx}`} className="colorrow">
                {row.map((color) => (
                  (() => {
                    const isCurrent = currentColor.toLowerCase() === color.toLowerCase();
                    const isOtherSelected = !isCurrent && selectedColors.has(color.toLowerCase());
                    return (
                  <button
                    key={`${activePickerInfo?.key}-${color}`}
                    className={`colorPicker-swatch ${isCurrent ? 'currentColor' : ''} ${isOtherSelected ? 'otherSelected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => updateActiveColor(color)}
                    title={color.toUpperCase()}
                  >
                    <span className="color-ok" />
                  </button>
                    );
                  })()
                ))}
              </div>
            ))}
            </div>
          </div>

        <div className="color-custom">
          <div className="color-custom-preview" style={{ background: previewValue }} />
          <div className="color-custom-controls">
            <label className="color-native-picker">
              <span>Nuancier</span>
              <input
                type="color"
                value={currentColor}
                onChange={(e) => updateActiveColor(e.target.value)}
              />
            </label>
            <label className="color-hex-field">
              <span>Hex</span>
              <input
                type="text"
                value={currentColor.toUpperCase()}
                onChange={(e) => handleHexInput(e.target.value)}
                maxLength={7}
                placeholder="#FFFFFF"
              />
            </label>
            <button
              className="outline-btn color-eye-btn"
              onClick={openEyeDropper}
              disabled={!isEyeDropperSupported}
              type="button"
            >
              Pipette
            </button>
          </div>
        </div>
        </div>
        )}
      </>
    );
  };

  const activePickerInfo = (() => {
    if (!activeColorPicker) return null;
    if (activeColorPicker.type === 'group') {
      const groupMaterials = groupedMaterials[activeColorPicker.id];
      if (!groupMaterials || groupMaterials.length === 0) return null;
      return {
        key: `group-${activeColorPicker.id}`,
        label: activeColorPicker.id,
        value: getMaterialColor(groupMaterials[0]),
        onChange: (color: string) => handleGroupColorChange(activeColorPicker.id, color),
      };
    }
    const material = materials.find(item => item.id === activeColorPicker.id);
    if (activeColorPicker.type === 'material') {
      if (!material) return null;
      return {
        key: `material-${material.id}`,
        label: `${material.element}`,
        value: getMaterialColor(material),
        onChange: (color: string) => handleMaterialColorChange(material.id, color),
      };
    }
    if (activeColorPicker.type === 'text') {
      const selected = layersManager.selectedLayer;
      if (!selected || selected.type !== 'text') return null;
      const data = selected.data as any;
      return {
        key: `text-${selected.id}`,
        label: 'Couleur du texte',
        value: data?.color || editTextForm.color || '#000000',
        onChange: (color: string) => handleEditTextFormChange('color', color),
      };
    }
    if (activeColorPicker.type === 'text-stroke') {
      const selected = layersManager.selectedLayer;
      if (!selected || selected.type !== 'text') return null;
      const data = selected.data as any;
      return {
        key: `text-stroke-${selected.id}`,
        label: 'Contour du texte',
        value: data?.strokeColor || editTextForm.strokeColor || '#000000',
        onChange: (color: string) => handleEditTextFormChange('strokeColor', color),
      };
    }
    if (activeColorPicker.type === 'motif-group') {
      const groupMaterials = groupedMaterials[activeColorPicker.groupName] || [];
      if (groupMaterials.length === 0) return null;
      const colors = Array.from(new Set(groupMaterials.map(mat => getMotifMaterialColor(activeColorPicker.motifId, mat.id))));
      return {
        key: `motif-group-${activeColorPicker.motifId}-${activeColorPicker.groupName}`,
        label: `${getMotifLabel(activeColorPicker.motifId)} / ${activeColorPicker.groupName}`,
        value: colors[0] || '#ffffff',
        onChange: (color: string) => handleMotifGroupColorChange(activeColorPicker.motifId, activeColorPicker.groupName, color),
      };
    }
    if (activeColorPicker.type === 'motif-design') {
      const colors = getMotifDesignColors(activeColorPicker.motifId);
      const label = activeColorPicker.designKey === 'design1' ? 'Design 1' : 'Design 2';
      return {
        key: `motif-design-${activeColorPicker.motifId}-${activeColorPicker.designKey}`,
        label,
        value: colors[activeColorPicker.designKey] || '#ffffff',
        onChange: (next: string) =>
          handleMotifDesignColorChange(activeColorPicker.motifId, activeColorPicker.designKey, next),
      };
    }
    if (activeColorPicker.type === 'pattern-motif') {
      const label = activeColorPicker.motifKey === 'motif1' ? 'Motif 1' : 'Motif 2';
      return {
        key: `pattern-motif-${activeColorPicker.motifKey}`,
        label,
        value: patternMotifColors[activeColorPicker.motifKey],
        onChange: (next: string) =>
          setPatternMotifColors(prev => ({ ...prev, [activeColorPicker.motifKey]: next })),
      };
    }
    if (activeColorPicker.type === 'motif-material') {
      const motifMaterial = materials.find(item => item.id === activeColorPicker.materialId);
      if (!motifMaterial) return null;
      return {
        key: `motif-material-${activeColorPicker.motifId}-${activeColorPicker.materialId}`,
        label: `${getMotifLabel(activeColorPicker.motifId)} / ${motifMaterial.element}`,
        value: getMotifMaterialColor(activeColorPicker.motifId, activeColorPicker.materialId),
        onChange: (color: string) => handleMotifMaterialColorChange(activeColorPicker.motifId, activeColorPicker.materialId, color),
      };
    }
    if (activeColorPicker.type === 'form-text-color') {
      return { key: 'form-text-color', label: 'Couleur texte', value: textForm.color,
        onChange: (c: string) => setTextForm(prev => ({ ...prev, color: c })) };
    }
    if (activeColorPicker.type === 'form-text-stroke') {
      return { key: 'form-text-stroke', label: 'Couleur contour', value: textForm.strokeColor,
        onChange: (c: string) => setTextForm(prev => ({ ...prev, strokeColor: c })) };
    }
    if (activeColorPicker.type === 'form-number-color') {
      return { key: 'form-number-color', label: 'Couleur numéro', value: numberForm.color,
        onChange: (c: string) => setNumberForm(prev => ({ ...prev, color: c })) };
    }
    if (activeColorPicker.type === 'form-number-stroke') {
      return { key: 'form-number-stroke', label: 'Couleur contour', value: numberForm.strokeColor,
        onChange: (c: string) => setNumberForm(prev => ({ ...prev, strokeColor: c })) };
    }
    if (activeColorPicker.type === 'form-name-color') {
      return { key: 'form-name-color', label: 'Couleur nom', value: nameForm.color,
        onChange: (c: string) => setNameForm(prev => ({ ...prev, color: c })) };
    }
    if (activeColorPicker.type === 'form-name-stroke') {
      return { key: 'form-name-stroke', label: 'Couleur contour', value: nameForm.strokeColor,
        onChange: (c: string) => setNameForm(prev => ({ ...prev, strokeColor: c })) };
    }
    if (activeColorPicker.type === 'form-edit-color') {
      return { key: 'form-edit-color', label: 'Couleur police', value: editTextForm.color,
        onChange: (c: string) => handleEditTextFormChange('color', c) };
    }
    if (activeColorPicker.type === 'form-edit-stroke') {
      return { key: 'form-edit-stroke', label: 'Couleur contour', value: editTextForm.strokeColor,
        onChange: (c: string) => handleEditTextFormChange('strokeColor', c) };
    }
    return null;
  })();

  useEffect(() => {
    if (!activePickerInfo || !activeColorPicker) return;
    const gradientAllowed =
      activeColorPicker.type === 'material' ||
      activeColorPicker.type === 'motif-material' ||
      activeColorPicker.type === 'motif-design' ||
      activeColorPicker.type === 'pattern-motif';
    const forceGradient = activeTab === 'gradient' && gradientAllowed;

    if (!gradientAllowed) {
      if (isGradientValue(activePickerInfo.value)) {
        const parsed = parseGradientValue(activePickerInfo.value);
        activePickerInfo.onChange(parsed.start);
        setGradientColors({ start: parsed.start, end: parsed.start });
        setGradientBalance(parsed.stop);
      } else {
        setGradientColors({ start: activePickerInfo.value, end: activePickerInfo.value });
        setGradientBalance(50);
      }
      setColorPickerMode('solid');
      setGradientAngle(90);
      setGradientStop('start');
      return;
    }

    if (forceGradient && !isGradientValue(activePickerInfo.value)) {
      const base = isGradientValue(activePickerInfo.value)
        ? parseGradientValue(activePickerInfo.value).start
        : activePickerInfo.value;
      setGradientColors({ start: base, end: base });
      setGradientAngle(90);
      setGradientBalance(50);
      setGradientStop('start');
      activePickerInfo.onChange(buildGradientValue(180, base, base, 50));
      setColorPickerMode('gradient');
      return;
    }

    if (isGradientValue(activePickerInfo.value)) {
      const parsed = parseGradientValue(activePickerInfo.value);
      setColorPickerMode('gradient');
      setGradientColors({ start: parsed.start, end: parsed.end });
      setGradientAngle(parsed.angle);
      setGradientBalance(parsed.stop);
      setGradientStop('start');
      return;
    }
    setColorPickerMode('solid');
    setGradientColors({ start: activePickerInfo.value, end: activePickerInfo.value });
    setGradientAngle(90);
    setGradientBalance(50);
    setGradientStop('start');
  }, [activePickerInfo?.key, activeColorPicker, activeTab]);

  const selectedLogoLayer =
    layersManager.selectedLayer &&
    layersManager.selectedLayer.type === 'image' &&
    !layersManager.selectedLayer.data?.isMotif &&
    !(layersManager.selectedLayer.data as any)?.isSystem
      ? layersManager.selectedLayer
      : null;

  const selectedFreeTextLayer =
    layersManager.selectedLayer &&
    layersManager.selectedLayer.type === 'text' &&
    (layersManager.selectedLayer.data as any)?.kind !== 'number' &&
    (layersManager.selectedLayer.data as any)?.kind !== 'name'
      ? layersManager.selectedLayer
      : null;

  const selectedLogoRotationDeg = selectedLogoLayer
    ? Math.round((normalizeRotation(selectedLogoLayer.uvRotation) * 180) / Math.PI)
    : 0;

  const isModalOpen = Boolean(activeMotifModal) || (activeColorPicker !== null && activeTab !== 'colors' && activeTab !== 'gradient');
  const isReadOnlySession = sessionAccess === 'viewer';

  return (
    <div className={`app ${isReadOnlySession ? 'is-readonly' : ''}`}>
      <div className="mobile-topbar">
        <div className="mobile-topbar-left">
          <img
            className="mobile-topbar-logo"
            src="/assets/IndyannaStudio_Logo.png"
            alt="Indyanna Studio"
          />
        </div>
        <button
          className="mobile-topbar-share"
          type="button"
          onClick={() => setShowModelSelector(true)}
        >
          <img src="/assets/icon/icons8-t-shirt-50.png" alt="" aria-hidden="true" />
          Changer de modèle
        </button>
      </div>

      {showIntro && (
        <div className="intro-overlay" aria-hidden="true">
          <img
            className="intro-logo"
            src="/assets/IndyannaStudio_Logo.png"
            alt=""
          />
          <div className="intro-shine" />
        </div>
      )}

      <button
        type="button"
        className="app-logo-btn"
        onClick={() => setShowWelcome(true)}
        aria-label="Ouvrir le panneau de bienvenue"
        title="Indyanna Studio — À propos"
      >
        <img
          className="app-logo"
          src="/assets/IndyannaStudio_Logo.png"
          alt="Indyanna Studio"
        />
      </button>
      {isReadOnlySession && <div className="readonly-badge">Mode lecture seule</div>}

      <div className="app-content">
        <div
          data-tutorial="canvas"
          className={`canvas-container ${
            SHOW_BEST_DESIGN_TAB && isBestPreviewRendering && activeTab === 'design' && designSubTab === 'best'
              ? 'is-preview-background'
              : ''
          }`}
        >
          <Canvas
            camera={{
              position: [0, 0, 1.1],
              fov: 50,
              near: 0.5,
              far: 20
            }}
            dpr={[1, 1]}
            onCreated={({ camera, gl, scene }) => {
              camera.near = 0.5;
              camera.far = 20;
              camera.lookAt(0, 0, 0);
              camera.updateProjectionMatrix();
              previewRendererRef.current = gl;
              previewSceneRef.current = scene;
              previewCameraRef.current = camera;
              gl.outputColorSpace = THREE.SRGBColorSpace;
              gl.toneMapping = THREE.ACESFilmicToneMapping;
              gl.toneMappingExposure = 0.8;
              gl.useLegacyLights = false;
              gl.physicallyCorrectLights = false;
            }}
            gl={{
              antialias: false,
              alpha: true,
              preserveDrawingBuffer: true,
              logarithmicDepthBuffer: true,
              powerPreference: 'high-performance'
            }}
          >
            <OrbitControls
              ref={orbitControlsRef}
              enablePan={false}
              enableZoom={true}
              enableRotate={true}
              mouseButtons={{
                LEFT: THREE.MOUSE.ROTATE,
                MIDDLE: THREE.MOUSE.DOLLY,
                RIGHT: THREE.MOUSE.ROTATE
              }}
              minPolarAngle={Math.PI / 2 - (30 * Math.PI) / 180}
              maxPolarAngle={Math.PI / 2 + (30 * Math.PI) / 180}
              minDistance={0.9}
              maxDistance={5}
              target={[0, 0, 0]}
            />

            <ambientLight intensity={0.2} />
            <directionalLight
              position={[4, 5, 3]}
              intensity={2}
              castShadow
              shadow-mapSize-width={2048}
              shadow-mapSize-height={2048}
            />
            <directionalLight
              position={[-4, 2, 3]}
              intensity={1}
            />
            <directionalLight
              position={[0, 3, -5]}
              intensity={0.5}
            />
            
            <Environment preset="studio" environmentIntensity={0.30} />

            <TShirt3D
              modelUrl={activeModel?.url || '/model/Tshirt%20Homme%20Manches%20Courtes.glb'}
              layers={layersManager.layers}
              selectedLayer={layersManager.selectedLayer}
              transformMode={appState.globalTransformMode}
              materialColors={materialColors}
              secondaryColor={secondaryColor}
              allowModelIntroAnimation={modelIntroReady}
              previewRefreshKey={modelPreviewRefreshKey}
              onLayerSelect={handleLayerSelect}
              onLayerTransform={(layerId, transform) => {
                if (isReadOnlySession) return;
                handleLayerTransform(layerId, transform);
              }}
              onModelLoad={(loaded) => setAppState(prev => ({ ...prev, isModelLoading: !loaded }))}
              onDragStateChange={setIsDragging}
              onMaterialsReady={handleMaterialsReady}
              onUvBoundsReady={(materialBounds, zoneBounds, materialTriangles) =>
                handleUvBoundsReady(materialBounds, zoneBounds, materialTriangles)
              }
              onLayerDuplicate={(layerId) => {
                if (isReadOnlySession) return;
                layersManager.duplicateLayer(layerId);
              }}
              onLayerDelete={(layerId) => {
                if (isReadOnlySession) return;
                layersManager.removeLayer(layerId);
              }}
            />
          </Canvas>

          <div className="product-switcher">
            <button
              className="outline-btn product-switcher-btn"
              type="button"
              onClick={() => setShowModelSelector(true)}
              aria-label="Changer de produit"
              title="Changer de produit"
            >
              <img src="/assets/icon/icons8-t-shirt-50.png" alt="" aria-hidden="true" />
            </button>
          </div>

          <input
            ref={importDesignInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                handleImportDesign(file);
                event.target.value = '';
              }
            }}
          />
          <input
            ref={importCreationInputRef}
            type="file"
            accept=".ist,application/json"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void handleImportCreation(file);
                event.target.value = '';
              }
            }}
          />

          {appState.isModelLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner">
                <img
                  className="loading-logo"
                  src="/assets/IndyannaStudio_Logo.png"
                  alt=""
                  aria-hidden="true"
                />
              </div>
            </div>
          )}

          {appState.modelError && (
            <div className="error-overlay">
              <div className="error-message">
                <h3>Load error</h3>
                <p>{appState.modelError}</p>
                <button onClick={() => window.location.reload()}>
                  Reload
                </button>
              </div>
            </div>
          )}
        </div>

        <aside
          className={`right-panel ${isModalOpen ? 'is-modal-open' : ''} ${isReadOnlySession ? 'is-readonly' : ''} ${activeTab === 'settings' ? 'is-settings-tab' : ''}`}
        >
          <div className="config-shell">
            <div className="config-main" data-tutorial="config-panel">
              <div className="config-header">
                <div className="config-header-text">
                  <div className="config-header-title">{TAB_META[activeTab].title}</div>
                  <div className="config-header-subtitle">{TAB_META[activeTab].subtitle}</div>
                </div>
              </div>

              {(activeTab === 'design' || activeTab === 'motif') && (
                <div className="config-subtabs">
                  <button
                    className={`config-subtab ${activeTab === 'design' && designSubTab === 'designs' ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => { setActiveTab('design'); setDesignSubTab('designs'); }}
                  >
                    Designs
                  </button>
                  <button
                    className={`config-subtab ${activeTab === 'motif' ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => setActiveTab('motif')}
                  >
                    Motifs
                  </button>
                  {SHOW_BEST_DESIGN_TAB && (
                    <button
                      className={`config-subtab ${activeTab === 'design' && designSubTab === 'best' ? 'is-active' : ''}`}
                      type="button"
                      onClick={() => { setActiveTab('design'); setDesignSubTab('best'); }}
                    >
                      Meilleurs designs
                    </button>
                  )}
                </div>
              )}

              {activeTab === 'logos' && (
                <div className="config-subtabs action-subtabs">
                  {selectedLogoLayer ? (
                    <>
                      <button
                        className="color-picker-back-btn"
                        aria-label="Retour"
                        type="button"
                        onClick={() => layersManager.selectLayer(null)}
                      >
                        <img src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                        Retour
                      </button>
                      <span className="action-subtab-status">Edition logo</span>
                    </>
                  ) : (
                    <Btn
                      size="sm"
                      icon="plus"
                      onClick={() => document.getElementById('logo-upload')?.click()}
                    >
                      Ajouter un logo
                    </Btn>
                  )}
                </div>
              )}

              {activeTab === 'text' && (
                <div className="config-subtabs action-subtabs">
                  {selectedFreeTextLayer ? (
                    <button
                      className="color-picker-back-btn"
                      aria-label="Retour"
                      type="button"
                      onClick={() => layersManager.selectLayer(null)}
                    >
                      <img src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                      Retour
                    </button>
                  ) : (
                    <Btn
                      size="sm"
                      icon="plus"
                      onClick={() => handleTextAdd({ ...textForm, kind: 'text' })}
                    >
                      Ajouter le texte
                    </Btn>
                  )}
                </div>
              )}

              {activeTab === 'motif' && motifPanelMode === 'grid' && (
                <div className="config-subtabs color-picker-header outside-panel">
                  <button
                    className="color-picker-back-btn"
                    aria-label="Retour"
                    type="button"
                    onClick={() => setMotifPanelMode('list')}
                  >
                    <img src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                    Retour
                  </button>
                  <div className="color-picker-title">{selectedMotifTargetLabel}</div>
                </div>
              )}

              {(activeTab === 'colors' || activeTab === 'gradient') && activeColorPicker && activePickerInfo && (
                <div
                  className={`config-subtabs color-picker-header outside-panel${
                    activeTab === 'gradient' ? ' gradient-header' : ''
                  }`}
                >
                  <button
                    className="color-picker-back-btn"
                    aria-label="Retour"
                    onClick={() => setActiveColorPicker(null)}
                    type="button"
                  >
                    <img src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                    Retour
                  </button>
                  {activeTab === 'gradient' && (
                    <label className="gradient-toggle-inline">
                      <span>Dégradé</span>
                      <input
                        type="checkbox"
                        checked={colorPickerMode === 'gradient'}
                        onChange={(e) =>
                          applyGradientMode(e.target.checked ? 'gradient' : 'solid', activePickerInfo, activeColorPicker)
                        }
                      />
                    </label>
                  )}
                  <div className="color-picker-title">{activePickerInfo.label}</div>
                </div>
              )}

              {activeTab === 'gradient' && activeColorPicker && activePickerInfo && (
                <div className="config-subtabs gradient-mobile-subtabs">
                  <button
                    className={`config-subtab ${gradientMobileTab === 'primary' ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => {
                      setGradientMobileTab('primary');
                      setGradientStop('start');
                    }}
                  >
                    Couleur principale
                  </button>
                  <button
                    className={`config-subtab ${gradientMobileTab === 'secondary' ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => {
                      setGradientMobileTab('secondary');
                      setGradientStop('end');
                    }}
                  >
                    Couleur dégradé
                  </button>
                  <button
                    className={`config-subtab ${gradientMobileTab === 'balance' ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => setGradientMobileTab('balance')}
                  >
                    Équilibre
                  </button>
                </div>
              )}

              {(activeTab === 'colors' || activeTab === 'gradient') && !activeColorPicker && (
                <div className="config-subtabs">
                  <button
                    className={`config-subtab ${activeTab === 'colors' ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => setActiveTab('colors')}
                  >
                    Couleur unie
                  </button>
                  <button
                    className={`config-subtab ${activeTab === 'gradient' ? 'is-active' : ''}`}
                    type="button"
                    onClick={() => setActiveTab('gradient')}
                  >
                    Dégradé
                  </button>
                </div>
              )}

              {(activeTab === 'colors' || activeTab === 'gradient') && !activeColorPicker && (
                <div className="config-subtabs color-copy-mobile-subtabs">
                  {isColorCopyMode ? (
                    <>
                      <button
                        className="secondary-btn color-copy-mobile-btn is-active"
                        type="button"
                        onClick={cancelColorCopyMode}
                      >
                        <img className="color-copy-btn-icon" src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                        <span>Quitter la copie</span>
                      </button>
                      {colorCopySource ? (
                        <span className="color-copy-mobile-status">Source: {colorCopySource.label}</span>
                      ) : (
                        <span className="color-copy-mobile-hint">Sélectionner l&apos;élément à copier</span>
                      )}
                    </>
                  ) : (
                    <button
                      className="secondary-btn color-copy-mobile-btn"
                      type="button"
                      onClick={startColorCopyMode}
                    >
                      <img className="color-copy-btn-icon" src="/assets/icon/icons8-copy-50.png" alt="" aria-hidden="true" />
                      <span>Copier couleur/dégradé</span>
                    </button>
                  )}
                </div>
              )}

              <div
                ref={panelScrollRef}
                className={`config-content ${activeTab === 'design' ? 'is-design-tab' : ''}`}
                key={activeTab}
              >
            {activeTab === 'design' && (
              <>
                {SHOW_BEST_DESIGN_TAB && designSubTab === 'best' && (
                  bestDesignCards.length > 0 ? (
                    <DesignGrid cards={bestDesignCards} />
                  ) : (
                    <div className="empty-tab">Aucun design client disponible.</div>
                  )
                )}
                {designSubTab === 'designs' && (
                  <DesignGrid cards={designCards} />
                )}
              </>
            )}

            {activeTab === 'motif' && (
              <div className="motif-panel-layout">
                {motifPanelMode === 'list' && (
                  <div className="color-list motif-targets">
                    {motifDesignRows.length > 0 && (
                      <>
                        <div className="color-list-section">Design</div>
                        <div className="color-list-divider" />
                      </>
                    )}
                    {motifDesignRows.map(entry => (
                      <button
                        key={entry.id}
                        className={`color-list-row motif-target-row ${entry.isDisabled ? 'is-disabled' : ''} ${selectedMotifTarget === entry.designKey ? 'is-active' : ''}`}
                        type="button"
                        onClick={() => {
                          if (entry.isDisabled || !activeMotifId) return;
                          setSelectedMotifTarget(entry.designKey);
                          setMotifPanelMode('grid');
                        }}
                      >
                        <span
                          className={`color-list-dot ${isLightSwatch(entry.color) ? 'is-light' : ''}`}
                          style={{ background: entry.color }}
                        />
                        <span className="color-list-label">{entry.label}</span>
                        <span className="color-list-chevron">›</span>
                      </button>
                    ))}
                    <div className="color-list-section">Éléments</div>
                    <div className="color-list-divider" />
                    {materials.map(material => (
                      <button
                        key={material.id}
                        className={`color-list-row motif-target-row ${selectedMotifTarget === material.id ? 'is-active' : ''}`}
                        type="button"
                        onClick={() => {
                          setSelectedMotifTarget(material.id);
                          setMotifPanelMode('grid');
                        }}
                      >
                        <span
                          className={`color-list-dot ${isLightSwatch(getMaterialColor(material)) ? 'is-light' : ''}`}
                          style={{ background: getSwatchBackground(getMaterialColor(material)) }}
                        />
                        <span className="color-list-label">{material.element}</span>
                        <span className="color-list-chevron">›</span>
                      </button>
                    ))}
                  </div>
                )}

                {motifPanelMode === 'grid' && (
                  <div className="motif-grid-panel">
                    <div className="color-picker-header in-panel">
                      <button
                        className="color-picker-back-btn"
                        aria-label="Retour"
                        type="button"
                        onClick={() => setMotifPanelMode('list')}
                      >
                        <img src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                        Retour
                      </button>
                      <div className="color-picker-title">{selectedMotifTargetLabel}</div>
                    </div>
                    {selectedMotifTarget ? (
                      <DesignGrid cards={motifCards} />
                    ) : (
                      <div className="empty-tab">Choisissez une zone pour voir les motifs.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {(activeTab === 'colors' || activeTab === 'gradient') && (
              <div className={`color-panel ${activeColorPicker ? 'is-picker-open' : ''}`}>
                {activeTab === 'gradient' && !activeColorPicker && (
                  <div className="gradient-banner">
                    Choisissez une zone pour appliquer un dégradé.
                  </div>
                )}
                {Object.keys(groupedMaterials).length === 0 && (
                  <div className="empty-tab">Aucun matériau détecté.</div>
                )}
                {activeColorPicker && activePickerInfo && (
                  <div className="color-picker-inline">
                    <div className="color-picker-header in-panel">
                      <button
                        className="color-picker-back-btn"
                        aria-label="Retour"
                        onClick={() => setActiveColorPicker(null)}
                        type="button"
                      >
                        <img src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                        Retour
                      </button>
                      <div className="color-picker-title">{activePickerInfo.label}</div>
                    </div>
                    <div className="color-picker-body">
                      {renderColorPickerBody()}
                    </div>
                  </div>
                )}

                {!activeColorPicker && (
                  <>
                    <div className="color-copy-toolbar">
                      {isColorCopyMode ? (
                        <>
                          <button
                            className="secondary-btn color-copy-btn is-active"
                            type="button"
                            onClick={cancelColorCopyMode}
                          >
                            <img className="color-copy-btn-icon" src="/assets/icon/icons8-return-50.png" alt="" aria-hidden="true" />
                            <span>Quitter la copie</span>
                          </button>
                          {colorCopySource && (
                            <span className="color-copy-source-tag">
                              Source: {colorCopySource.label}
                            </span>
                          )}
                        </>
                      ) : (
                        <button
                          className="secondary-btn color-copy-btn"
                          type="button"
                          onClick={startColorCopyMode}
                        >
                          <img className="color-copy-btn-icon" src="/assets/icon/icons8-copy-50.png" alt="" aria-hidden="true" />
                          <span>Copier couleur/dégradé</span>
                        </button>
                      )}
                    </div>
                    {isColorCopyMode && (
                      <div className="color-copy-hint">
                        {colorCopySource
                          ? 'Cliquez sur une zone cible pour coller la couleur ou le dégradé.'
                          : 'Cliquez d’abord sur la zone source à copier.'}
                      </div>
                    )}
                  <div className="color-list">
                    {designColorEntries.length > 0 && (
                      <>
                        <div className="color-list-section">Design</div>
                        <div className="color-list-divider" />
                      </>
                    )}
                    {designColorEntries.map(entry => (
                      (() => {
                        const rowKey = `motif-design-${entry.designKey}`;
                        const rowEntry: CopyableColorEntry = {
                          key: rowKey,
                          label: entry.label,
                          color: entry.color,
                          onPick: () =>
                            activeMotifId &&
                            setActiveColorPicker({
                              type: 'motif-design',
                              motifId: activeMotifId,
                              designKey: entry.designKey,
                            }),
                          onChange: (next: string) =>
                            activeMotifId && handleMotifDesignColorChange(activeMotifId, entry.designKey, next),
                        };
                        const isCopySource = isColorCopyMode && colorCopySource?.key === rowKey;
                        const isCopyTargetable =
                          isColorCopyMode && Boolean(colorCopySource) && colorCopySource.key !== rowKey;
                        return (
                      <button
                        key={entry.id}
                        className={`color-list-row ${isCopySource ? 'is-copy-source' : ''} ${isCopyTargetable ? 'is-copy-targetable' : ''}`}
                        type="button"
                        onClick={() => handleCopyableColorRowClick(rowEntry)}
                      >
                        <span
                          className={`color-list-dot ${isLightSwatch(entry.color) ? 'is-light' : ''}`}
                          style={{ background: entry.color }}
                        />
                        <span className="color-list-label">{entry.label}</span>
                        {activeTab === 'gradient' && !isColorCopyMode && (
                          <label className="color-list-toggle" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isGradientValue(entry.color)}
                              onChange={(e) => {
                                if (!activeMotifId) return;
                                const colors = getMotifDesignColors(activeMotifId);
                                const current = colors[entry.designKey] || '#ffffff';
                                const nextOnChange = (next: string) =>
                                  handleMotifDesignColorChange(activeMotifId, entry.designKey, next);
                                toggleGradientValue(current, nextOnChange, e.target.checked, gradientStop);
                              }}
                            />
                            <span />
                          </label>
                        )}
                        <span className="color-list-chevron">›</span>
                      </button>
                        );
                      })()
                    ))}
                    {motifPatternEntries.length > 0 && (
                      <>
                        <div className="color-list-section">Motif</div>
                        <div className="color-list-divider" />
                      </>
                    )}
                    {motifPatternEntries.map(entry => (
                      (() => {
                        const rowKey = `pattern-motif-${entry.motifKey}`;
                        const rowEntry: CopyableColorEntry = {
                          key: rowKey,
                          label: entry.label,
                          color: entry.color,
                          onPick: () =>
                            setActiveColorPicker({
                              type: 'pattern-motif',
                              motifKey: entry.motifKey,
                            }),
                          onChange: (next: string) =>
                            setPatternMotifColors(prev => ({ ...prev, [entry.motifKey]: next })),
                        };
                        const isCopySource = isColorCopyMode && colorCopySource?.key === rowKey;
                        const isCopyTargetable =
                          isColorCopyMode && Boolean(colorCopySource) && colorCopySource.key !== rowKey;
                        return (
                      <button
                        key={entry.id}
                        className={`color-list-row ${isCopySource ? 'is-copy-source' : ''} ${isCopyTargetable ? 'is-copy-targetable' : ''}`}
                        type="button"
                        onClick={() => handleCopyableColorRowClick(rowEntry)}
                      >
                        <span
                          className={`color-list-dot ${isLightSwatch(entry.color) ? 'is-light' : ''}`}
                          style={{ background: entry.color }}
                        />
                        <span className="color-list-label">{entry.label}</span>
                        {activeTab === 'gradient' && !isColorCopyMode && (
                          <label className="color-list-toggle" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isGradientValue(entry.color)}
                              onChange={(e) => {
                                const nextOnChange = (next: string) =>
                                  setPatternMotifColors(prev => ({ ...prev, [entry.motifKey]: next }));
                                toggleGradientValue(entry.color, nextOnChange, e.target.checked, gradientStop);
                              }}
                            />
                            <span />
                          </label>
                        )}
                        <span className="color-list-chevron">›</span>
                      </button>
                        );
                      })()
                    ))}
                    <div className="color-list-section">Éléments</div>
                    <div className="color-list-divider" />
                    {materials.map(material => (
                      (() => {
                        const rowKey = `material-${material.id}`;
                        const rowEntry: CopyableColorEntry = {
                          key: rowKey,
                          label: material.element,
                          color: getMaterialColor(material),
                          onPick: () => setActiveColorPicker({ type: 'material', id: material.id }),
                          onChange: (next: string) => handleMaterialColorChange(material.id, next),
                        };
                        const isCopySource = isColorCopyMode && colorCopySource?.key === rowKey;
                        const isCopyTargetable =
                          isColorCopyMode && Boolean(colorCopySource) && colorCopySource.key !== rowKey;
                        return (
                      <button
                        key={material.id}
                        className={`color-list-row ${isCopySource ? 'is-copy-source' : ''} ${isCopyTargetable ? 'is-copy-targetable' : ''}`}
                        type="button"
                        onClick={() => handleCopyableColorRowClick(rowEntry)}
                      >
                        <span
                          className={`color-list-dot ${isLightSwatch(getMaterialColor(material)) ? 'is-light' : ''}`}
                          style={{ background: getSwatchBackground(getMaterialColor(material)) }}
                        />
                        <span className="color-list-label">{material.element}</span>
                        {activeTab === 'gradient' && !isColorCopyMode && (
                          <label className="color-list-toggle" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isGradientValue(getMaterialColor(material))}
                              onChange={(e) => {
                                const current = getMaterialColor(material);
                                toggleGradientValue(current, (next) => handleMaterialColorChange(material.id, next), e.target.checked, gradientStop);
                              }}
                            />
                            <span />
                          </label>
                        )}
                        <span className="color-list-chevron">›</span>
                      </button>
                        );
                      })()
                    ))}
                  </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'logos' && (
              <>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      handleImageAdd(file);
                      event.target.value = '';
                    }
                  }}
                  style={{ display: 'none' }}
                />

                {selectedLogoLayer ? (
                  <div className="config-section layer-editor-panel logo-editor-panel">
                    <div className="layer-editor-row">
                      <span className="layer-editor-label">
                        Rotation: {selectedLogoRotationDeg} deg
                      </span>
                      <div className="layer-editor-actions">
                        <button
                          className="outline-btn layer-icon-btn"
                          type="button"
                          title="Rotation -5 deg"
                          onClick={() => handleImageLayerRotate(selectedLogoLayer.id, -5)}
                        >
                          <img src="/assets/icon/icons8-rotate-50.png" alt="Rotation -" />
                        </button>
                        <button
                          className="outline-btn layer-icon-btn"
                          type="button"
                          title="Rotation +5 deg"
                          onClick={() => handleImageLayerRotate(selectedLogoLayer.id, 5)}
                        >
                          <img
                            className="layer-icon-flip-vertical"
                            src="/assets/icon/icons8-rotate-50.png"
                            alt="Rotation +"
                          />
                        </button>
                      </div>
                    </div>
                    <div className="layer-editor-row">
                      <span className="layer-editor-label">Taille</span>
                      <div className="layer-editor-actions">
                        <button
                          className="outline-btn layer-icon-btn"
                          type="button"
                          title="Reduire"
                          onClick={() => handleImageLayerScale(selectedLogoLayer.id, 0.95)}
                        >
                          <img src="/assets/icon/icons8-zoom-out-50.png" alt="Taille -" />
                        </button>
                        <button
                          className="outline-btn layer-icon-btn"
                          type="button"
                          title="Agrandir"
                          onClick={() => handleImageLayerScale(selectedLogoLayer.id, 1.05)}
                        >
                          <img src="/assets/icon/icons8-zoom-in-50.png" alt="Taille +" />
                        </button>
                      </div>
                    </div>
                    <button
                      className="secondary-btn danger-btn layer-delete-icon-btn"
                      type="button"
                      title="Supprimer"
                      onClick={() => layersManager.removeLayer(selectedLogoLayer.id)}
                    >
                      <img src="/assets/icon/icons8-full-trash-50.png" alt="Supprimer" />
                    </button>
                  </div>
                ) : (
                  <LayerPanel
                    layers={layersManager.layers.filter(
                      layer => !layer.data?.isMotif && !(layer.data as any)?.isSystem && layer.type === 'image'
                    )}
                    emptyTitle="Calques"
                    emptyHint="Aucune image ajoutee"
                    selectedLayerId={null}
                    onLayerSelect={handleLayerSelect}
                    onLayerDelete={layersManager.removeLayer}
                    onLayerReorder={layersManager.reorderLayers}
                    onLayerUpdate={layersManager.updateLayer}
                  />
                )}
              </>
            )}

            {activeTab === 'settings' && (
              <SettingsMenu
                onImportDesign={() => importDesignInputRef.current?.click()}
                onResetModel={handleResetModel}
                onPersonnaliser={() => setActiveTab('design')}
                onAbout={() => setShowWelcome(true)}
                onTutorial={() => { resetTutorial(); setShowTutorial(true); }}
                onClose={() => setActiveTab('design')}
              />
            )}

            {activeTab === 'numbers' && (
              <>
                <div className="config-section">
                  <div className="config-title">Numéro</div>
                  <div className="config-form">
                    <label className="config-label">Valeur</label>
                    <input
                      className="config-input"
                      type="text"
                      maxLength={3}
                      value={numberForm.text}
                      onChange={(event) =>
                        setNumberForm(prev => ({ ...prev, text: event.target.value.replace(/[^0-9]/g, '').slice(0, 3) }))
                      }
                      placeholder="00"
                    />

                    <label className="config-label">Police</label>
                    <select
                      className="config-select"
                      value={numberForm.fontFamily}
                      onChange={(event) => setNumberForm(prev => ({ ...prev, fontFamily: event.target.value }))}
                      style={{ fontFamily: toCanvasFontFamily(numberForm.fontFamily) }}
                    >
                      {FONT_OPTIONS.map(font => (
                        <option key={font} value={font} style={{ fontFamily: toCanvasFontFamily(font) }}>
                          {font}
                        </option>
                      ))}
                    </select>

                    <div className="config-row">
                      <div className="config-field">
                        <label className="config-label">Couleur</label>
                        <button
                          className="config-color-swatch"
                          type="button"
                          style={{ backgroundColor: numberForm.color }}
                          onClick={() => setActiveColorPicker({ type: 'form-number-color' })}
                          title={numberForm.color}
                        />
                      </div>
                      <div className="config-field">
                        <label className="config-label">Contour</label>
                        <button
                          className="config-color-swatch"
                          type="button"
                          style={{ backgroundColor: numberForm.strokeColor }}
                          onClick={() => setActiveColorPicker({ type: 'form-number-stroke' })}
                          title={numberForm.strokeColor}
                        />
                      </div>
                      <div className="config-field">
                        <label className="config-label">Épaisseur</label>
                        <input
                          className="config-range"
                          type="range"
                          min={0}
                          max={30}
                          value={numberForm.strokeWidth}
                          onChange={(event) => setNumberForm(prev => ({ ...prev, strokeWidth: Number(event.target.value) }))}
                        />
                      </div>
                    </div>

                    <Btn
                      className="btn--full"
                      icon="number"
                      onClick={() => handleTextAdd({ ...numberForm, kind: 'number' })}
                    >
                      Ajouter le numéro
                    </Btn>
                  </div>
                </div>

                <LayerPanel
                  layers={layersManager.layers.filter(
                    layer => !layer.data?.isMotif && layer.type === 'text' && (layer.data as any)?.kind === 'number'
                  )}
                  selectedLayerId={
                    layersManager.selectedLayer && layersManager.selectedLayer.type === 'text' && (layersManager.selectedLayer.data as any)?.kind === 'number'
                      ? layersManager.selectedLayer.id
                      : null
                  }
                  onLayerSelect={handleLayerSelect}
                  onLayerDelete={layersManager.removeLayer}
                  onLayerReorder={layersManager.reorderLayers}
                  onLayerUpdate={layersManager.updateLayer}
                />
              </>
            )}

            {activeTab === 'name' && (
              <>
                <div className="config-section">
                  <div className="config-title">Nom</div>
                  <div className="config-form">
                    <label className="config-label">Nom</label>
                    <input
                      className="config-input"
                      type="text"
                      value={nameForm.text}
                      onChange={(event) => setNameForm(prev => ({ ...prev, text: event.target.value }))}
                      placeholder="Nom..."
                    />

                    <label className="config-label">Police</label>
                    <select
                      className="config-select"
                      value={nameForm.fontFamily}
                      onChange={(event) => setNameForm(prev => ({ ...prev, fontFamily: event.target.value }))}
                      style={{ fontFamily: toCanvasFontFamily(nameForm.fontFamily) }}
                    >
                      {FONT_OPTIONS.map(font => (
                        <option key={font} value={font} style={{ fontFamily: toCanvasFontFamily(font) }}>
                          {font}
                        </option>
                      ))}
                    </select>

                    <div className="config-row">
                      <div className="config-field">
                        <label className="config-label">Couleur</label>
                        <button
                          className="config-color-swatch"
                          type="button"
                          style={{ backgroundColor: nameForm.color }}
                          onClick={() => setActiveColorPicker({ type: 'form-name-color' })}
                          title={nameForm.color}
                        />
                      </div>
                      <div className="config-field">
                        <label className="config-label">Contour</label>
                        <button
                          className="config-color-swatch"
                          type="button"
                          style={{ backgroundColor: nameForm.strokeColor }}
                          onClick={() => setActiveColorPicker({ type: 'form-name-stroke' })}
                          title={nameForm.strokeColor}
                        />
                      </div>
                      <div className="config-field">
                        <label className="config-label">Épaisseur</label>
                        <input
                          className="config-range"
                          type="range"
                          min={0}
                          max={20}
                          value={nameForm.strokeWidth}
                          onChange={(event) => setNameForm(prev => ({ ...prev, strokeWidth: Number(event.target.value) }))}
                        />
                      </div>
                    </div>

                    <Btn
                      className="btn--full"
                      icon="name"
                      onClick={() => handleTextAdd({ ...nameForm, kind: 'name' })}
                    >
                      Ajouter le nom
                    </Btn>
                  </div>
                </div>

                <LayerPanel
                  layers={layersManager.layers.filter(
                    layer => !layer.data?.isMotif && layer.type === 'text' && (layer.data as any)?.kind === 'name'
                  )}
                  selectedLayerId={
                    layersManager.selectedLayer && layersManager.selectedLayer.type === 'text' && (layersManager.selectedLayer.data as any)?.kind === 'name'
                      ? layersManager.selectedLayer.id
                      : null
                  }
                  onLayerSelect={handleLayerSelect}
                  onLayerDelete={layersManager.removeLayer}
                  onLayerReorder={layersManager.reorderLayers}
                  onLayerUpdate={layersManager.updateLayer}
                />
              </>
            )}

            {activeTab === 'text' && (
              <>
                {selectedFreeTextLayer ? (
                  <TextEditor
                    form={editTextForm}
                    fontOptions={FONT_OPTIONS}
                    onChange={handleEditTextFormChange}
                    onColorPicker={(type) => setActiveColorPicker({ type } as any)}
                    onFontPicker={() => setShowFontPicker(p => !p)}
                    showFontPicker={showFontPicker}
                    onDelete={() => layersManager.removeLayer(selectedFreeTextLayer.id)}
                    isLightColor={isLightSwatch}
                  />
                ) : (
                  <LayerPanel
                    layers={layersManager.layers.filter(
                      layer => !layer.data?.isMotif && layer.type === 'text' && (layer.data as any)?.kind !== 'number' && (layer.data as any)?.kind !== 'name'
                    )}
                    emptyTitle="Calques"
                    emptyHint="Aucun texte ajoute"
                    selectedLayerId={null}
                    onLayerSelect={handleLayerSelect}
                    onLayerDelete={layersManager.removeLayer}
                    onLayerReorder={layersManager.reorderLayers}
                    onLayerUpdate={layersManager.updateLayer}
                  />
                )}
              </>
            )}

            {((activeTab === 'numbers' || activeTab === 'name' || activeTab === 'text-edit') && layersManager.selectedLayer?.type === 'text') && (
              <div className="text-edit-overlay" role="dialog" aria-label="Modification texte">
                <button className="color-picker-backdrop" aria-label="Fermer" onClick={closeTextEdit} />
                <div className="text-edit-panel">
                  <div className="color-picker-header">
                    <div className="color-picker-title">Modification texte</div>
                    <button className="color-picker-close" aria-label="Fermer" onClick={closeTextEdit}>×</button>
                  </div>
                  <div className="text-edit-body">
                    <TextEditor
                      form={editTextForm}
                      fontOptions={FONT_OPTIONS}
                      onChange={handleEditTextFormChange}
                      onColorPicker={(type) => setActiveColorPicker({ type } as any)}
                      onFontPicker={() => setShowFontPicker(p => !p)}
                      showFontPicker={showFontPicker}
                      onDelete={() => { if (layersManager.selectedLayer) { layersManager.removeLayer(layersManager.selectedLayer.id); closeTextEdit(); } }}
                      isLightColor={isLightSwatch}
                    />
                  </div>
                </div>
              </div>
            )}
              </div>
            </div>

            <ConfigNav
              items={NAV_ITEMS}
              activeKey={displayTab}
              onSelect={setActiveTab}
              onUndo={layersManager.undo}
              onRedo={layersManager.redo}
              canUndo={layersManager.canUndo}
              canRedo={layersManager.canRedo}
            />
          </div>

          {activeMotifModal && (
            <div className="motif-modal-overlay" role="dialog" aria-label="Motif">
              <button
                className="color-picker-backdrop"
                aria-label="Fermer"
                onClick={() => setActiveMotifModal(null)}
              />
              <div className="motif-modal-panel">
                <div className="color-picker-header">
                  <div className="color-picker-title">Paramètre du design</div>
                  <button
                    className="color-picker-close"
                    aria-label="Fermer"
                    onClick={() => setActiveMotifModal(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="motif-modal-body">
                  {renderMotifControls(activeMotifModal)}
                </div>
              </div>
            </div>
          )}

          {activePickerInfo && activeTab !== 'colors' && activeTab !== 'gradient' && (
            <div
              className="color-picker-overlay"
              role="dialog"
              aria-label="Choisir une couleur"
              style={
                colorPickerOffset
                  ? {
                      top: `${colorPickerOffset.top}px`,
                      height: `${colorPickerOffset.height}px`,
                      left: 0,
                      right: 0,
                    }
                  : { left: 0, right: 0 }
              }
            >
              <button
                className="color-picker-backdrop"
                aria-label="Fermer le nuancier"
                onClick={() => setActiveColorPicker(null)}
              />
              <div className="color-picker-panel">
                <div className="config-subtabs color-picker-header">
                  <div className="color-picker-title">{activePickerInfo.label}</div>
                  <button
                    className="color-picker-close"
                    aria-label="Fermer"
                    onClick={() => setActiveColorPicker(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="color-picker-body">
                  {renderColorPickerBody()}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>

      <BottomBar
        onExport={handleExport}
        onShareCreation={() => { void handleShareCreation(); }}
        onPriceSimulator={() => setShowPriceSimulator(true)}
        isReadOnly={isReadOnlySession}
        isExporting={appState.isExporting}
        activeModel={activeModel}
      />

      {exportModal.visible && exportModal.pdfUrl && (
        <ExportModal
          pdfUrl={exportModal.pdfUrl}
          captures={exportModal.captures}
          onClose={handleExportModalClose}
          onSubmitContact={(form) => handleContactSubmit(form)}
          initialMessage={exportModal.initialMessage}
        />
      )}

      {showWelcome && !appState.isModelLoading && (
        <WelcomeModal onClose={() => setShowWelcome(false)} />
      )}

      {showTutorial && !showWelcome && !appState.isModelLoading && (
        <TutorialOverlay
          onDone={() => setShowTutorial(false)}
          onActivateTab={(tabId) => setActiveTab(tabId as any)}
        />
      )}

      {showPriceSimulator && (
        <PriceSimulator
          gender={getModelGender(activeModel)}
          onClose={() => setShowPriceSimulator(false)}
        />
      )}

      {showModelSelector && (
        <div className="model-modal-overlay" role="dialog" aria-label="Changer de produit">
          <button
            className="color-picker-backdrop"
            aria-label="Fermer"
            onClick={() => setShowModelSelector(false)}
          />
          <div className="model-modal-panel">
            <div className="color-picker-header">
              <div className="color-picker-title">Changer de produit</div>
              <button
                className="color-picker-close"
                aria-label="Fermer"
                onClick={() => setShowModelSelector(false)}
              >
                ×
              </button>
            </div>
            <div className="model-modal-body">
              {MODEL_ITEMS.length === 0 ? (
                <div className="empty-tab">Aucun modèle trouvé.</div>
              ) : (
                <div className="model-list">
                  {MODEL_ITEMS.map(item => (
                    <button
                      key={item.fileName}
                      className={`model-item model-item-bar ${activeModel?.url === item.url ? 'active' : ''}`}
                      type="button"
                      onClick={() => {
                        setActiveModel(item);
                        setShowModelSelector(false);
                      }}
                    >
                      <span>{item.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showFontPicker && (
        <div className="font-picker-overlay" role="dialog" aria-label="Choisir une police">
          <button
            className="color-picker-backdrop"
            aria-label="Fermer"
            onClick={() => {
              setShowFontPicker(false);
              setFontSearch('');
            }}
          />
          <div className="font-picker-modal">
            <div className="font-picker-header">
              <div className="font-picker-title">Choisir une police</div>
              <button
                className="color-picker-close"
                aria-label="Fermer"
                onClick={() => {
                  setShowFontPicker(false);
                  setFontSearch('');
                }}
              >
                ×
              </button>
            </div>
            <div className="font-picker-body">
              <input
                type="text"
                className="font-picker-search"
                placeholder="Rechercher une police..."
                value={fontSearch}
                onChange={(e) => setFontSearch(e.target.value)}
              />
              <div className="font-picker-panel">
                {FONT_OPTIONS.filter(font =>
                  font.toLowerCase().includes(fontSearch.trim().toLowerCase())
                ).map(font => (
                  <button
                    key={font}
                    type="button"
                    className={`font-picker-option${editTextForm.fontFamily === font ? ' is-active' : ''}`}
                    onClick={() => {
                      handleEditTextFormChange('fontFamily', font);
                      setShowFontPicker(false);
                      setFontSearch('');
                    }}
                    style={{ fontFamily: font }}
                  >
                    {editTextForm.text?.trim() ? editTextForm.text : 'Texte'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {creationFeedback.visible && (
        <div
          className={`creation-feedback-overlay ${
            creationFeedback.mode === 'saved' ? 'is-saved has-links' : ''
          } ${creationFeedback.mode === 'saving' ? 'is-saving' : ''} ${
            creationFeedback.mode === 'loaded' ? 'is-loaded' : ''
          }`}
          aria-hidden="true"
        >
          <div className={`creation-feedback-card ${creationFeedback.mode === 'saved' ? 'is-result' : ''}`}>
            {creationFeedback.mode !== 'saved' && (
              <img className="creation-feedback-logo" src="/assets/IndyannaStudio_Logo.png" alt="" />
            )}
            <div className="creation-feedback-content">
              <p className="creation-feedback-text">{creationFeedback.message}</p>
              {creationFeedback.mode === 'saved' && (
                <div className="creation-feedback-links">
                  <div className="creation-feedback-link-row">
                    <button
                      type="button"
                      className="creation-feedback-close-btn"
                      onClick={closeCreationFeedback}
                    >
                      Fermer
                    </button>
                    <button
                      type="button"
                      className="creation-feedback-continue-btn"
                      onClick={() => window.open('https://www.indyannapub.com/', '_blank', 'noopener,noreferrer')}
                    >
                      Continuer sur Indyanna Pub →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {shareForm.visible && (
        <div className="creation-feedback-overlay has-links share-form-overlay" role="dialog" aria-label="Formulaire partage creation">
          <div className="creation-feedback-card is-result share-form-card">
            <div className="creation-feedback-content">
              <p className="creation-feedback-text">
                Renseignez votre adresse email pour recevoir vos liens de partage et d'edition.
              </p>
              <label className="share-form-label" htmlFor="share-email">Adresse email</label>
              <input
                id="share-email"
                className="share-form-input"
                type="email"
                value={shareForm.email}
                onChange={(event) => setShareForm(prev => ({ ...prev, email: event.target.value, error: null }))}
                placeholder="vous@exemple.com"
              />
              <label className="share-form-checkbox">
                <input
                  type="checkbox"
                  checked={shareForm.consentAssist}
                  onChange={(event) =>
                    setShareForm(prev => ({ ...prev, consentAssist: event.target.checked, error: null }))
                  }
                />
                <span>J'accepte que Indyanna Pub me contacte pour m'assister sur ma creation.</span>
              </label>
              {shareForm.error && <p className="share-form-error">{shareForm.error}</p>}
              <div className="share-form-actions">
                <button type="button" className="creation-feedback-copy" onClick={() => setShareForm(prev => ({ ...prev, visible: false }))}>
                  Annuler
                </button>
                <button type="button" className="creation-feedback-close-btn" onClick={() => void handleShareFormSubmit()}>
                  Envoyer les liens
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {creationImportIssues && (
        <div className="model-modal-overlay" role="dialog" aria-label="Rapport import creation">
          <button
            className="color-picker-backdrop"
            aria-label="Fermer"
            onClick={() => setCreationImportIssues(null)}
          />
          <div className="model-modal-panel">
            <div className="color-picker-header">
              <div className="color-picker-title">Import partiel de la creation</div>
              <button
                className="color-picker-close"
                aria-label="Fermer"
                onClick={() => setCreationImportIssues(null)}
              >
                ×
              </button>
            </div>
            <div className="model-modal-body">
              <p style={{ marginTop: 0 }}>Certains elements n'ont pas pu etre charges. Le reste a ete importe.</p>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', maxHeight: '45vh', overflow: 'auto' }}>
                {creationImportIssues.map((issue, index) => (
                  <li key={`import-issue-${index}`}>{issue}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
